import "./styles.css";
import { Archive, ArrowRight, Check, Eye, FileSpreadsheet, Pencil, Power, RotateCcw, Search, Send, Trash2 } from "lucide";
import type { IconNode } from "lucide";
import { api, downloadFile, getApiContext, getSheetCellDetail, getSheetCells, getSheetView, getSheetViews, login, logout, ROLES, runSheetCellAction, setApiContext, toQuery } from "./api";
import { hasActiveQueueJobs, mutationInvalidationTargets, uploadPreflightItems, uploadQueueResultText, uploadSubmissionContract, uploadSummaryFromEntries } from "./api-core";
import { REQUEST_TYPE_LABELS, ROLE_LABELS, STATUS_LABELS, TEMPLATE_LABELS, WORKPLACE_META } from "./labels";
import { canOpenWorkplaceFromNavigation, pathForWorkplace, routeFromPath, WORKPLACE_PATHS } from "./routes-core";
import { availableCostCentersFrom, costCenterOptionsHtml, costCenterScopeNote, referenceCostCentersFrom } from "./reference-core";
import { primaryActionsFromRoleWorkplace } from "./dashboard-core";
import { actionsForRole } from "./sheet-actions-core.mjs";
import { clampSheetSize, compactCellDisplay, frozenPaneClasses, moveSelection, rangeToGrid, sheetTrackTemplate } from "./sheet-grid-core.mjs";
import { BUDGET_TEMPLATE_PROFILES, adaptBudgetTemplateViewData, budgetTemplateExportQuery, budgetTemplateRequestPayload, coerceBudgetTemplateState, getBudgetTemplateProfile, getBudgetTemplateTab, rowsForBudgetTemplateTab } from "./budget-template-core.mjs";
import { fractionToPercentLabel, monthlyBillingDisplayValue, monthlyCostCenterTotal, percentToFraction, totalMonthlyBilling } from "./manual-request-core.mjs";
import type { Article, BudgetRequest, Campaign, CostCenter, Dictionaries, MeResponse, Role, SheetCell, SheetCellDetail, SheetTab, SheetView, Workplace } from "./types";

type AppState = {
  workplace: Workplace;
  deniedWorkplace?: Workplace | null;
  me?: MeResponse;
  dictionaries?: Dictionaries;
  theme: "light" | "dark";
  pendingRequestId?: string | null;
  pendingImportId?: string | null;
  pendingSheetViewId?: string | null;
  pendingSheetTab?: string | null;
  validationItems?: any[];
  selectedValidationIds?: string[];
  campaigns?: Campaign[];
  budgetCampaigns?: Campaign[];
  sheetViews?: SheetView[];
  activeSheetView?: SheetView | null;
  activeSheetTab?: string | null;
  sheetVersionId?: string | null;
  sheetRange?: string | null;
  selectedSheetCell?: string | null;
  loginRequired?: boolean;
  activeImportDetailId?: string | null;
  activeRequestDetailId?: string | null;
  budgetCampaignId?: string | null;
  budgetScopeMode?: "selection" | "effective";
  budgetIncludedRequestIds?: string[];
  budgetPreview?: any;
  budgetTemplateProfileId?: string;
  budgetTemplateTabId?: string;
  budgetTemplateViewData?: any;
  budgetTemplateCatalog?: any[];
  budgetSavedSelections?: any[];
  budgetSavedSelectionId?: string | null;
};

const state: AppState = {
  workplace: (localStorage.getItem("budgetWorkplace") as Workplace) || "dashboard",
  theme: (localStorage.getItem("budgetTheme") as "light" | "dark") || "light",
  budgetTemplateProfileId: coerceBudgetTemplateState(localStorage.getItem("budgetTemplateProfile"), localStorage.getItem("budgetTemplateTab")).profileId,
  budgetTemplateTabId: coerceBudgetTemplateState(localStorage.getItem("budgetTemplateProfile"), localStorage.getItem("budgetTemplateTab")).tabId
};

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("App root not found");
const root = app;
const MONTH_NAMES = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
let activeMonthPicker: HTMLElement | null = null;
let liveRefreshTimer: number | null = null;
let liveRefreshUntil = 0;
let liveRefreshInFlight = false;
let bootstrapGeneration = 0;
// A sheet projection is composed from three independently mutable controls:
// view, tab and range.  Network responses may arrive out of order while a
// user switches any of them, so only the newest projection is allowed to
// paint the grid and its metadata.
let sheetLoadGeneration = 0;
let allocationBasisEditorMembers: any[] = [];
const LIVE_REFRESH_INTERVAL_MS = 5000;
const LIVE_REFRESH_WINDOW_MS = 60000;
const SHEET_DEFAULT_COL_WIDTH = 96;
const SHEET_DEFAULT_ROW_HEIGHT = 28;
const SHEET_MIN_COL_WIDTH = 48;
const SHEET_MAX_COL_WIDTH = 360;
const SHEET_MIN_ROW_HEIGHT = 22;
const SHEET_MAX_ROW_HEIGHT = 160;

const REFERENCE_SELECT_CONTRACT_MARKERS = [
  '<select id="uploadCostCenter" name="cost_center_code"',
  '<select id="manualCostCenter" name="cost_center_code"',
  '<select id="revenueCostCenter" name="cost_center_code"',
  '<select id="explanationCostCenter" name="cost_center_code"',
  '<select id="reportCostCenter"',
  '<select id="campaignCostCenters" name="cost_center_codes" multiple',
  '<select id="userCostCenter" name="cost_center_code"',
  '<select id="mappingArticle" name="target_article_code"',
  '<select id="mappingCostCenter" name="target_cost_center_code"',
  '<select id="allocationArticle" name="target_article_code"',
  '<select id="allocationCostCenter" name="target_cost_center_code"',
  '<select id="revenueClient" name="client"',
  '<select name="client_type"',
  '<select name="client_category"',
  '<select id="revenueMedia" name="media"',
  '<select id="revenueContractType" name="contract_type"',
  '<select name="article_code"'
];

function html(strings: TemplateStringsArray, ...values: unknown[]) {
  return strings.reduce((acc, part, index) => acc + part + (values[index] ?? ""), "");
}

function escapeHtml(value: unknown) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function parseJsonObject(value: unknown): Record<string, any> {
  if (!value) return {};
  if (typeof value === "object") return value as Record<string, any>;
  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function money(value: unknown) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(amount);
}

function dateLabel(value: unknown) {
  if (!value) return "не указано";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function monthLabel(value: unknown) {
  const month = Number(value || 0);
  return month >= 1 && month <= 12 ? MONTH_NAMES[month - 1] : "Год";
}

function periodLabel(value: unknown) {
  return value === null || value === undefined || value === "" ? "Год" : monthLabel(value);
}

function lineTypeLabel(value: unknown) {
  return String(value || "plan").toLowerCase() === "fact" ? "Факт" : "План";
}

function coefficientLabel(value: unknown) {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : String(value);
}

function percentageLabel(value: unknown) {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  return Number.isFinite(number) ? `${(number * 100).toFixed(2).replace(/\.00$/, "")}%` : String(value);
}

function monthRangeLabel(months: number[]) {
  if (!months.length) return "Выбрать месяц";
  if (months.length === 1) return monthLabel(months[0]);
  const contiguous = months.every((month, index) => index === 0 || month === months[index - 1] + 1);
  return contiguous
    ? `${monthLabel(months[0])} - ${monthLabel(months[months.length - 1])}`
    : months.map(monthLabel).join(", ");
}

function normalizeMonthListFromField(field: HTMLElement | null) {
  if (!field) return [];
  const monthsValue = field.querySelector<HTMLInputElement>("[data-months-value]")?.value || "";
  const parsed = monthsValue.split(",").map((part) => Number(part.trim())).filter((month) => month >= 1 && month <= 12);
  const single = Number(field.querySelector<HTMLInputElement>("[data-month-value]")?.value || 0);
  return parsed.length ? parsed : single >= 1 && single <= 12 ? [single] : [];
}

function parseMonthCostCenters(field: HTMLElement | null): Record<string, string[]> {
  if (!field) return {};
  const raw = field.querySelector<HTMLInputElement>("[data-month-cost-centers]")?.value || "{}";
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed).map(([month, codes]) => [month, Array.isArray(codes) ? codes.map(String).filter(Boolean) : []]));
  } catch {
    return {};
  }
}

function parseMonthCostCenterAmounts(field: HTMLElement | null): Record<string, Record<string, number>> {
  if (!field) return {};
  const raw = field.querySelector<HTMLInputElement>("[data-month-cost-center-amounts]")?.value || "{}";
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed).map(([month, byCenter]) => [
      month,
      byCenter && typeof byCenter === "object" && !Array.isArray(byCenter)
        ? Object.fromEntries(Object.entries(byCenter).filter(([, amount]) => amount !== "").map(([code, amount]) => [code, Number(amount || 0)]))
        : {}
    ]));
  } catch {
    return {};
  }
}

function defaultCostCenterForField(field: HTMLElement | null) {
  const form = field?.closest("form");
  return form?.querySelector<HTMLSelectElement>('select[name="cost_center_code"]')?.value || "";
}

function setMonthCostCenters(field: HTMLElement, values: Record<string, string[]>) {
  const input = field.querySelector<HTMLInputElement>("[data-month-cost-centers]");
  if (input) input.value = JSON.stringify(values);
}

function setMonthCostCenterAmounts(field: HTMLElement, values: Record<string, Record<string, number>>) {
  const input = field.querySelector<HTMLInputElement>("[data-month-cost-center-amounts]");
  if (input) input.value = JSON.stringify(values);
}

function monthCostCentersEnabled(field: HTMLElement | null) {
  return field?.dataset.monthCostCentersEnabled === "1";
}

function statusLabel(status?: string | null) {
  return STATUS_LABELS[String(status || "")] || String(status || "не назначено");
}

function requestTypeLabel(type?: string | null) {
  return REQUEST_TYPE_LABELS[String(type || "")] || TEMPLATE_LABELS[String(type || "")] || String(type || "тип не указан");
}

function roleLabel(role?: string | null) {
  return ROLE_LABELS[role as Role] || role || "не назначено";
}

function statusChip(status?: string | null) {
  const value = String(status || "");
  return `<span class="status-chip status-${escapeHtml(value)}" aria-label="Статус: ${escapeHtml(statusLabel(value))}" data-status-text="${escapeHtml(statusLabel(value))}">${escapeHtml(statusLabel(value))}</span>`;
}

function severityBadge(severity?: string | null) {
  const value = String(severity || "info");
  return `<span class="severity severity-${escapeHtml(value)}" aria-label="Важность: ${escapeHtml(statusLabel(value))}" data-severity-text="${escapeHtml(statusLabel(value))}">${escapeHtml(statusLabel(value))}</span>`;
}

function hasCapability(capability: string) {
  return Boolean(state.me?.capabilities?.includes(capability as never));
}

function canOpenWorkplace(workplace: Workplace) {
  return canOpenWorkplaceFromNavigation(state.me?.navigation, workplace, state.me?.demo_auth_enabled === true);
}

function canCollaborateOnRequests() {
  return state.me?.actor?.role !== "observer";
}

function applyTheme() {
  document.documentElement.dataset.theme = state.theme;
  localStorage.setItem("budgetTheme", state.theme);
}

function toggleTheme() {
  state.theme = state.theme === "dark" ? "light" : "dark";
  applyTheme();
}

function showToast(message: string, kind: "success" | "error" | "info" = "info") {
  const host = document.querySelector("#toastHost");
  if (!host) return;
  const item = document.createElement("div");
  item.className = `toast ${kind}`;
  item.textContent = message;
  host.append(item);
  window.setTimeout(() => item.remove(), 4200);
}

function renderShell() {
  root.innerHTML = html`
    <div class="app-shell">
      <aside class="sidebar">
        <div class="brand">
          <h1>Система бюджетирования</h1>
          <small>Ролевой финансовый контур</small>
        </div>
        <div class="actor-card">
          <strong id="sidebarActor">Загрузка профиля</strong>
          <small id="actorState">Рабочий контур загружается</small>
        </div>
        <nav id="workplaceNav" class="workplace-nav" aria-label="Разделы бюджета"></nav>
      </aside>
      <main class="main">
        <header class="topbar">
          <div>
            <strong id="activeCampaign" role="status" aria-live="polite">Кампания загружается</strong>
            <div class="muted">Рабочий контур · контейнерная сборка</div>
          </div>
          <div class="context-controls">
            <form id="loginForm" class="login-form is-hidden" autocomplete="on">
              <label>Логин<input id="loginCode" name="code" autocomplete="username" required /></label>
              <label>Пароль<input id="loginPassword" name="password" type="password" autocomplete="current-password" required /></label>
              <button type="submit" class="primary">Войти</button>
            </form>
            <button id="logoutButton" type="button" class="is-hidden">Выйти</button>
            <label class="role-picker">
              Пользователь
              <select id="user"><option value="">Демо-роль</option></select>
            </label>
            <label class="role-picker">
              Роль
              <select id="role">
                ${ROLES.map((role) => `<option value="${role}">${ROLE_LABELS[role]}</option>`).join("")}
              </select>
            </label>
            <button id="themeToggle" type="button" title="Переключить тему">День/ночь</button>
          </div>
        </header>
        <header class="page-header">
          <div>
            <span id="pageEyebrow" class="eyebrow"></span>
            <h2 id="pageTitle"></h2>
            <p id="pageSubtitle" class="muted"></p>
          </div>
          <button id="pagePrimaryAction" class="primary" type="button" aria-describedby="pageSubtitle"></button>
        </header>
        <div class="workspace">
          ${renderAccessDeniedSection()}
          ${renderDashboardSection()}
          ${renderCampaignsSection()}
          ${renderUploadSection()}
          ${renderAdminSection()}
          ${renderAllocationBasisSection()}
          ${renderManualRequestSection()}
          ${renderWorkflowSection()}
          ${renderBudgetSection()}
          ${renderReportsSection()}
          ${renderSheetsSection()}
          ${renderExportsSection()}
          ${renderUiKitSection()}
        </div>
      </main>
      <dialog id="drillDialog" aria-label="Детализация бюджета">
        <form method="dialog"><button>Закрыть</button></form>
        <div id="drilldown"></div>
      </dialog>
      <dialog id="campaignDialog" class="campaign-dialog" aria-label="Новая бюджетная кампания" data-campaign-dialog>
        <form method="dialog" class="dialog-close"><button type="submit" aria-label="Закрыть создание кампании">Закрыть</button></form>
        <div class="dialog-body">
          <div class="section-head">
            <div>
              <span class="eyebrow">Создание</span>
              <h2>Новая бюджетная кампания</h2>
              <p class="muted">Заполните период, сценарий, доступные кост-центры и формы подачи.</p>
            </div>
          </div>
          ${campaignFormHtml()}
          <div id="campaignResult" class="preview-box"></div>
        </div>
      </dialog>
      <dialog id="monthDialog" class="month-dialog" aria-label="Выбор месяца" data-month-dialog>
        <form method="dialog" class="dialog-close"><button type="submit" aria-label="Закрыть выбор месяца">Закрыть</button></form>
        <div class="dialog-body">
          <div class="section-head">
            <div>
              <span class="eyebrow">Период</span>
              <h2>Выбор месяца</h2>
              <p id="monthDialogHint" class="muted">Выберите месяц размещения.</p>
            </div>
          </div>
          <div id="monthDialogGrid" class="month-grid" role="listbox" aria-label="Месяцы года"></div>
          <div id="monthCostCenterPanel" class="month-cost-center-panel" aria-label="Кост-центры по месяцам"></div>
          <div class="actions">
            <button type="button" data-month-clear>Сбросить</button>
            <button type="button" class="primary" data-month-apply>Применить</button>
          </div>
        </div>
      </dialog>
      <dialog id="revenueClientDialog" class="campaign-dialog revenue-client-dialog" aria-label="Создать нового клиента" data-revenue-client-dialog>
        <form method="dialog" class="dialog-close"><button type="submit" aria-label="Закрыть создание клиента">Закрыть</button></form>
        <div class="dialog-body">
          <div class="section-head">
            <div>
              <span class="eyebrow">Доходы блока A</span>
              <h2>Создать нового клиента</h2>
              <p class="muted">Клиент будет сохранён в справочнике и сразу выбран в текущей заявке.</p>
            </div>
          </div>
          <form id="revenueClientForm" class="manual-form">
            <div class="manual-request-field-grid">
              ${formField("Название", `<input name="name" required autocomplete="organization" />`)}
              ${formField("Категория", `<select id="newRevenueClientCategory" name="client_category" required></select>`)}
              ${formField("Ответственный менеджер", `<select id="newRevenueClientResponsible" name="responsible" required></select>`)}
              ${formField("Агентство", `<select id="newRevenueClientAgency" name="agency" required></select>`)}
            </div>
            ${toolbar(actionButton("Создать и выбрать", "primary", { type: "submit" }), "wide")}
          </form>
          <div id="revenueClientResult" class="preview-box"></div>
        </div>
      </dialog>
      <aside id="sidePanel" class="side-panel" role="dialog" aria-modal="false" aria-live="polite" aria-label="Панель деталей" tabindex="-1"></aside>
      <div id="toastHost" class="toast-host" role="status" aria-live="polite" aria-label="Уведомления интерфейса"></div>
    </div>
  `;
  root.dataset.referenceSelectContract = REFERENCE_SELECT_CONTRACT_MARKERS.join("|");
}

function renderAccessDeniedSection() {
  return html`
    <section class="workplace-section access-denied" data-workplace-denied>
      ${panel(
        "Раздел недоступен",
        `<div class="denied-state">
          <strong id="deniedRouteTitle">Нет доступа к разделу</strong>
          <small id="deniedRouteReason">Навигация и права берутся из профиля роли.</small>
          <button type="button" data-open-workplace="dashboard">На рабочий стол</button>
        </div>`
      )}
    </section>
  `;
}

function renderDashboardSection() {
  return html`
    <section class="workplace-section dashboard-workspace" data-workplace="dashboard" data-desktop-redesign="dashboard">
      <article class="panel dashboard-hero">
        <div>
          <span class="eyebrow">Контекст</span>
          <h2 id="dashboardHeroTitle">Кампания загружается</h2>
          <p id="dashboardHeroSubtitle" class="muted">Роль и следующий шаг появятся после загрузки профиля.</p>
        </div>
        <div id="dashboardHeroAction" class="dashboard-hero-action"></div>
      </article>
      <div class="dashboard-summary">
        ${panel("Требует внимания", `<div id="attention" class="list compact list-scroll"></div>`, "panel-priority")}
        ${panel("Следующее действие", `<div id="quickActions" class="list compact list-scroll"></div>`, "panel-action")}
        ${panel("Статус роли", `<div id="roleWorkplace" class="list compact"></div><div id="roleWidgets" class="metric-strip"></div>`, "panel-quiet")}
      </div>
      <div class="dashboard-main">
        ${panel("Мои задачи", `<div id="tasks" class="list compact list-scroll"></div>`, "panel-fill")}
        ${panel("Последние события", `<div id="notifications" class="list compact list-scroll"></div>`, "panel-fill")}
      </div>
      <details class="panel system-details">
        <summary><span>Состояние системы</span><span id="systemState" class="state">Загрузка...</span></summary>
        <div id="activeJob" class="state muted">Нет активной задачи</div>
        <div class="metric-strip" id="metrics"></div>
        <h3>Статусы подачи</h3>
        <div id="submissionStatus" class="list compact"></div>
      </details>
    </section>
  `;
}

function renderCampaignsSection() {
  return html`
    <section class="workplace-section workspace-console campaigns-workspace" data-capability="write_admin" data-workplace="campaigns" data-desktop-redesign-route="campaigns">
      <article class="panel route-panel panel-fill campaign-list-panel">
        <div class="section-head campaign-list-head">
          <div>
            <h2>Бюджетные кампании</h2>
            <p class="muted">Сначала доступные кампании. Создание вынесено в компактное действие, чтобы список оставался главным экраном раздела.</p>
          </div>
          ${iconButton("Создать кампанию", "+", "primary", "openCampaignDialog")}
        </div>
        <div id="campaigns" class="list compact list-scroll campaign-list"></div>
      </article>
    </section>
  `;
}

function campaignFormHtml() {
  return html`
    <form id="campaignForm" class="manual-form campaign-form">
      <label>Год<input name="year" type="number" value="2026" required /></label>
      <label>Название бюджетной кампании<input name="scenario" value="base" required /></label>
      <label>Старт подачи<input name="submission_start" type="date" /></label>
      <label>Завершение подачи<input name="submission_end" type="date" /></label>
      <label>Статус<select name="status"><option value="open">Открыта</option><option value="closed">Закрыта</option></select></label>
      <label class="wide">Кост-центры<select id="campaignCostCenters" name="cost_center_codes" multiple></select></label>
      <label class="wide">Формы<select id="campaignTemplateTypes" name="template_types" multiple>${Object.entries(TEMPLATE_LABELS).filter(([value]) => value !== "auto").map(([value, label]) => `<option value="${value}" selected>${label}</option>`).join("")}<option value="manual_request" selected>Ручная заявка</option><option value="revenue_request" selected>Доходная заявка</option></select></label>
      <button type="submit" class="primary">Сохранить кампанию</button>
    </form>
  `;
}

function renderUploadSection() {
  const templateButtons = [
    ["downloadTemplateCompiled", "Шаблон бюджета"],
    ["downloadTemplateBusinessV1", "Шаблон бизнес-варианта 1"],
    ["downloadTemplateBusinessV2", "Шаблон бизнес-варианта 2"],
    ["downloadTemplateExplanation", "Шаблон пояснительной"],
    ["downloadTemplateSummaryC", "Шаблон свода C"],
  ].map(([id, label]) => actionButton(label, "", { id })).join("");
  return html`
    <section class="workplace-section workspace-console upload-workspace" data-capability="import" data-workplace="upload" data-desktop-redesign-route="upload">
      <div class="workspace-split upload-grid">
        ${panel(
        "Загрузка данных",
        `<span id="uploadWizardState" class="state muted" role="status" aria-live="polite">Выберите файл и шаблон</span>
        <div class="wizard-steps" aria-label="Шаги загрузки">
          <div class="wizard-step is-active" data-upload-step="1"><strong>1</strong><span>Файл</span></div>
          <div class="wizard-step" data-upload-step="2"><strong>2</strong><span>Проверка</span></div>
          <div class="wizard-step" data-upload-step="3"><strong>3</strong><span>Очередь</span></div>
        </div>
        <form id="uploadForm" class="upload">
          ${formField("Тип файла", `<select name="mode"><option value="compiled_budget">Готовый бюджет кост-центра</option><option value="source_form">Исходная форма подразделения</option><option value="explanation_note">Пояснительная записка</option><option value="summary">Сводная форма</option></select>`, "Для пояснительной лист ИНФО задаёт общую базу рабочих мест и базы КЦ; строки ОФИС распределяются системой автоматически.")}
          ${formField("Шаблон", `<select name="template_type">${Object.entries(TEMPLATE_LABELS).map(([value, label]) => `<option value="${value}" data-template-type="${value}">${label}</option>`).join("")}</select>`, "Шаблон определяет обязательные листы и правила проверки.")}
          ${formField("Кост-центр", `<select id="uploadCostCenter" name="cost_center_code"></select>`, "Список ограничивается доступом текущей роли.")}
          <div id="uploadCostCenterScope" class="scope-note wide"></div>
          ${formField("Название загрузки", `<input name="upload_title" placeholder="Например: План MICE июль, версия от бизнеса" />`, "Свободное название поможет отличить загрузки в очереди и истории.", "", "wide")}
          ${formField("Комментарий", `<textarea name="upload_comment" rows="3" placeholder="Что изменилось, откуда файл, что проверить"></textarea>`, "Комментарий сохранится в карточке импорта.", "", "wide")}
          ${formField("Excel-файл", `<input name="file" type="file" accept=".xlsx" multiple required />`, "Можно приложить несколько файлов одного типа.", "", "wide")}
          ${toolbar(`${actionButton("Проверить перед загрузкой", "", { id: "previewUpload" })}${actionButton("Поставить в очередь", "primary", { type: "submit" })}`, "wide")}
        </form>
        <div id="uploadPreflight" class="preview-box"></div>
        ${toolbar(templateButtons, "", 'data-capability="export"')}
        <div id="uploadResult" class="preview-box"></div>`
        , "route-panel route-primary scroll-panel upload-primary-panel")}
        <div class="workspace-stack">
          ${panel("История импортов", scrollPanel("imports"), "route-panel panel-fill")}
          ${panel("Очередь", scrollPanel("queue"), "route-panel panel-fill")}
        </div>
      </div>
    </section>
  `;
}

function renderAdminSection() {
  return html`
    <section class="workplace-section workspace-console admin-workspace" data-capability="write_admin" data-workplace="admin" data-desktop-redesign-route="admin">
      <div class="workspace-split admin-console-grid">
        ${panel(
        "Администрирование справочников и сопоставления",
        `<div class="admin-tool-tabs" role="tablist" aria-label="Инструменты администрирования">
          ${actionButton("Статьи", "is-active", { attrs: 'data-admin-tool="articles" role="tab" aria-selected="true"' })}
          ${actionButton("Кост-центры", "", { attrs: 'data-admin-tool="cost_centers" role="tab" aria-selected="false"' })}
          ${actionButton("Пользователи", "", { attrs: 'data-admin-tool="users" role="tab" aria-selected="false"' })}
          ${actionButton("Сопоставления", "", { attrs: 'data-admin-tool="mapping" role="tab" aria-selected="false"' })}
          ${actionButton("Аллокации", "", { attrs: 'data-admin-tool="allocations" role="tab" aria-selected="false"' })}
          ${actionButton("Клиенты", "", { attrs: 'data-admin-tool="clients" role="tab" aria-selected="false"' })}
          ${actionButton("Измерения", "", { attrs: 'data-admin-tool="business_dimensions" role="tab" aria-selected="false"' })}
        </div>
        <div class="admin-grid admin-tool-forms">
          ${miniForm("articleForm", "Статья", ["code:Код, например C.2.3", "title:Название", "block:Блок", "parent_code:Родитель"], formField("Знак", `<select name="sign"><option value="1">Доход / плюс</option><option value="-1">Расход / минус</option></select>`), "articles")}
          ${miniForm("costCenterForm", "Кост-центр", ["code:Код, например MICE", "name:Название", "aliases:Алиасы через запятую"], "", "cost_centers")}
          ${miniForm("userForm", "Пользователь", ["code:Логин", "display_name:Имя"], `${formField("Роль", `<select name="role">${ROLES.map((role) => `<option value="${role}">${ROLE_LABELS[role]}</option>`).join("")}</select>`)}${formField("Кост-центр", `<select id="userCostCenter" name="cost_center_code"></select>`)}`, "users")}
          ${miniForm("mappingForm", "Правило сопоставления", ["source_type:Тип источника", "match_key:Поле сопоставления", "match_value:Значение источника", "description:Описание"], `${formField("Целевая статья", `<select id="mappingArticle" name="target_article_code"></select>`)}${formField("Целевой кост-центр", `<select id="mappingCostCenter" name="target_cost_center_code"></select>`)}${formField("Знак суммы", `<select name="amount_sign"><option value="1">Плюс</option><option value="-1">Минус</option></select>`)}`, "mapping")}
          ${miniForm("allocationForm", "Правило аллокации", ["name:Название", "source_type:Тип источника", "source_sheet:Лист", "source_row:Строка источника", "base_total:База всего", "cost_center_base:База КЦ", "manual_share:Ручная доля"], `${formField("Целевая статья", `<select id="allocationArticle" name="target_article_code"></select>`)}${formField("Целевой кост-центр", `<select id="allocationCostCenter" name="target_cost_center_code"></select>`)}${formField("База распределения", `<select name="base_type"><option value="workplaces">Рабочие места</option><option value="area">Площадь</option><option value="payroll">ФОТ</option><option value="fixed_share">Фиксированная доля</option><option value="manual_share">Ручная доля</option><option value="direct">Прямое отнесение</option></select>`)}`, "allocations")}
          <form id="allocationReconciliationForm" class="mini-form" data-admin-tool-panel="allocations">
            <strong>Сверка распределения</strong>
            ${formField("Сумма источника", `<input name="source_amount" type="number" step="0.000001" value="295000" required />`, "Общая сумма до распределения.")}
            ${formField("Тип источника", `<input name="source_type" value="explanation_note" required />`)}
            ${formField("Лист", `<input name="source_sheet" value="ОФИС" required />`)}
            ${formField("Строка источника", `<select name="source_row"><option value="129">129 · Офисная аренда</option><option value="149">149 · Коммунальные услуги</option></select>`)}
            ${formField("Месяц базы", `<select name="month">${MONTH_NAMES.map((month, index) => `<option value="${index + 1}">${index + 1} · ${month}</option>`).join("")}</select>`)}
            ${toolbar(actionButton("Проверить распределение", "primary", { type: "submit" }))}
            <div id="allocationReconciliationResult" class="preview-box" aria-live="polite">Введите сумму источника для read-only сверки.</div>
          </form>
          ${miniForm("clientForm", "Клиент", ["code:Код", "name:Краткое название", "legal_name:Полное юр. название", "inn:ИНН", "kpp:КПП", "ogrn:ОГРН", "legal_address:Юридический адрес", "postal_address:Почтовый адрес", "contact_person:Контакт", "contact_email:Email", "phone:Телефон", "industry:Отрасль", "aliases:Алиасы через запятую"], `${formField("Тип клиента", `<select name="client_type"><option value="direct">Прямой клиент</option><option value="agency">Агентство</option><option value="group">Группа компаний</option><option value="supplier_client">Клиент-поставщик</option></select>`)}${formField("Категория", `<select name="client_category"><option value="strategic">Стратегический</option><option value="key_account">Ключевой</option><option value="regular">Регулярный</option><option value="new">Новый</option><option value="risk">Риск</option></select>`)}${formField("НДС", `<select name="vat_status"><option value="vat_payer">Плательщик НДС</option><option value="no_vat">Без НДС</option><option value="unknown">Неизвестно</option></select>`)}${formField("Риск", `<select name="risk_level"><option value="low">Низкий</option><option value="medium">Средний</option><option value="high">Высокий</option></select>`)}${formField("Вес ранжирования", `<input name="ranking_weight" type="number" step="0.01" value="1" />`)}`, "clients")}
          ${miniForm("businessDimensionForm", "Бизнес-измерение", ["code:Код", "name:Название", "aliases:Алиасы через запятую"], formField("Тип измерения", `<select name="dimension_type"><option value="client">Клиент</option><option value="client_category">Категория клиента</option><option value="responsible">Ответственный менеджер</option><option value="agency">Агентство</option><option value="media">Медиа</option><option value="contract_type">Тип договора</option><option value="legal_entity">Юрлицо</option></select>`), "business_dimensions")}
        </div>
        <div class="danger-zone">
          <strong>Очистка пользовательских данных</strong>
          <small>Удаляются заявки, бюджеты, импорты, очереди, проверки, уведомления и сопоставления. Статьи, кост-центры, пользователи, кампании и правила распределения остаются.</small>
          <label class="form-field"><span>Подтверждение</span><input id="adminResetConfirmation" autocomplete="off" placeholder="ОЧИСТИТЬ ДАННЫЕ" /></label>
          <button id="adminResetUserData" type="button" class="danger" data-admin-reset-user-data>Очистить данные</button>
        </div>
        ${adminHelpManualHtml()}
        <div id="adminResult" class="preview-box"></div>`
        , "route-panel route-primary scroll-panel")}
        <div class="workspace-stack admin-list-stack">
          ${adminRightPanel("articles", "Текущие статьи", "articleList")}
          ${adminRightPanel("cost_centers", "Текущие кост-центры", "costCenterList")}
          ${adminRightPanel("mapping", "Текущие сопоставления", "mappingList")}
          ${adminRightPanel("users", "Текущие пользователи", "userList")}
          ${adminRightPanel("allocations", "Текущие аллокации", "allocationList")}
          ${adminRightPanel("clients", "Текущие клиенты", "clientList")}
          ${adminRightPanel("business_dimensions", "Текущие измерения", "businessDimensionList")}
          ${panel("Аудит формул", scrollPanel("formulaAudit"), "route-panel panel-fill", 'data-admin-list-panel="formula_audit"')}
          ${panel("Журнал аудита", scrollPanel("audit"), "route-panel panel-fill admin-audit-panel", 'data-admin-list-panel="audit"')}
        </div>
      </div>
    </section>
  `;
}

function renderAllocationBasisSection() {
  return html`
    <section class="workplace-section workspace-console allocation-basis-workspace" data-capability="allocation_basis" data-workplace="allocation_basis" data-desktop-redesign-route="allocation_basis">
      ${panel("Помесячная база распределения", `<p class="muted">HR вводит штат вручную или загружает согласованный файл офисных расходов. Изменения становятся действующими только после подтверждения финансистом.</p><div id="allocationBasisTemplateAdmin"></div><div class="allocation-input-actions"><button type="button" id="downloadAllocationBasisTemplate">Скачать актуальный шаблон HR</button><form id="allocationBasisImportForm" class="inline-upload"><input name="file" type="file" accept=".xlsx" required /><input name="reason" placeholder="Причина изменения" required /><button type="submit">Загрузить заполненный Excel</button></form></div><div id="allocationBasisImportResult" class="preview-box"></div><div id="allocationBasisList" class="table-wrap"><span class="muted">Загрузка базы…</span></div><div id="allocationBasisEditor" class="preview-box"></div>`, "route-panel panel-fill")}
    </section>
  `;
}

function renderManualRequestSection() {
  const manualModeTabs = toolbar(
    `${actionButton("Строки бюджета", "is-active", { attrs: 'data-manual-mode="manual" role="tab" aria-selected="true"' })}${actionButton("Доходы блока A", "", { attrs: 'data-manual-mode="revenue" role="tab" aria-selected="false"' })}${actionButton("Пояснительная", "", { attrs: 'data-manual-mode="explanation" role="tab" aria-selected="false"' })}`,
    "manual-mode-tabs",
    'role="tablist" aria-label="Тип ручной подачи"'
  );
  return html`
    <section class="workplace-section workspace-console manual-workspace" data-capability="create_manual_request" data-workplace="manual_request" data-desktop-redesign-route="manual_request">
      <div class="workspace-split manual-grid">
        ${panel(
        "Подача заявки",
        `${manualModeTabs}
        <div class="manual-mode-panels">
          <div class="manual-mode-panel is-active" data-manual-mode-panel="manual">
            <form id="manualRequestForm" class="manual-form" data-submission-type="manual_request">
              <div class="manual-request-field-grid" data-manual-header-grid>
                ${formField("Название", `<input name="title" value="Новая заявка подразделения" required />`, "Коротко назовите заявку для очереди согласования.", "", "manual-header-field", 'data-manual-header-field="title"')}
                ${formField("Тип", `<select name="request_type"><option value="revenue">Блок А / доходы</option><option value="opex">Блок C / расходы</option><option value="capex">Блок B / CAPEX</option><option value="tax_debt_depreciation">Блок D</option><option value="manual_budget">Универсальная</option></select>`, "Тип определяет блок бюджета и доступные статьи.", "", "manual-header-field", 'data-manual-header-field="request-type"')}
                ${formField("Тип данных", `<select name="line_type"><option value="plan">План</option><option value="fact">Факт</option></select>`, "Факт попадет в отчеты только в режиме факт или план-факт.", "", "manual-header-field", 'data-manual-header-field="line-type"')}
                ${formField("Кост-центр", `<select id="manualCostCenter" name="cost_center_code" required></select>`, "Список учитывает права текущей роли.", "", "manual-header-field", 'data-manual-header-field="cost-center"')}
              </div>
              <div id="manualCostCenterScope" class="scope-note wide"></div>
              <div class="wide line-editor"><div class="section-head"><strong>Строки заявки</strong>${actionButton("Добавить строку", "", { id: "addManualLine" })}</div><div id="manualLineEditor" class="line-grid"></div></div>
              ${toolbar(actionButton("Создать заявку", "primary", { type: "submit" }), "wide")}
            </form>
          </div>
          <div class="manual-mode-panel" data-manual-mode-panel="revenue" hidden>
            <form id="revenueRequestForm" class="manual-form" data-submission-type="revenue_request">
              <div class="manual-request-field-grid" data-revenue-row="identity">
                ${formField("Клиент", `<div class="select-with-action"><select id="revenueClient" name="client" required></select>${actionButton("Создать нового клиента", "", { attrs: 'data-open-revenue-client-dialog' })}</div>`, "Выберите клиента из справочника или создайте нового.")}
                ${formField("Тип данных", `<select name="line_type"><option value="plan">План</option><option value="fact">Факт</option></select>`, "Факт попадет в отчеты только в режиме факт или план-факт.")}
                ${formField("Форма договора", `<select id="revenueContractType" name="contract_type"><option value="агентская">агентская</option><option value="услужная">услужная</option></select>`, "Влияет на расчет доходных строк.")}
                ${formField("Медиа", `<select id="revenueMedia" name="media"></select>`, "Медиа-направление для блока A.")}
              </div>
              ${formField("Кост-центр", `<select id="revenueCostCenter" name="cost_center_code" required></select>`, "Кост-центр попадет в рассчитанные строки.")}
              <div id="revenueCostCenterScope" class="scope-note wide"></div>
              <div class="manual-request-field-grid" data-revenue-row="calculation">
                ${formField("Месяц", monthPickerControl("month", 6, true, "", false, true), "Выберите один или несколько месяцев; для каждого месяца можно указать КЦ.")}
                ${formField("Бюджет", `<input name="billing" type="number" step="0.01" value="1000000" required />`)}
                ${formField("АК", `<div class="revenue-driver-pair"><label><span>%</span><input name="ak_percent" data-revenue-driver="ak" data-driver-unit="percent" type="number" min="0" max="100" step="0.01" value="2.4" inputmode="decimal" /></label><label><span>руб.</span><input name="ak_amount" data-revenue-driver="ak" data-driver-unit="amount" type="number" step="0.01" inputmode="decimal" /></label></div>`)}
                ${formField("Разница цены (РЦ)", `<div class="revenue-driver-pair"><label><span>%</span><input name="rc_percent" data-revenue-driver="rc" data-driver-unit="percent" type="number" min="0" max="100" step="0.01" value="30" inputmode="decimal" required /></label><label><span>руб.</span><input name="rc_amount" data-revenue-driver="rc" data-driver-unit="amount" type="number" step="0.01" inputmode="decimal" required /></label></div>`)}
                ${formField("Суперкомиссия от поставщика (СК)", `<div class="revenue-driver-pair"><label><span>%</span><input name="sk_percent" data-revenue-driver="sk" data-driver-unit="percent" type="number" min="0" max="100" step="0.01" value="16.5" inputmode="decimal" required /></label><label><span>руб.</span><input name="sk_amount" data-revenue-driver="sk" data-driver-unit="amount" type="number" step="0.01" inputmode="decimal" required /></label></div>`)}
              </div>
              <div id="revenueMonthlyBilling" class="monthly-budget-grid wide" data-monthly-billing-host></div>
              <div class="revenue-derived-totals wide" aria-label="Итоговая рентабельность">
                ${formField("Рентабельность, руб.", `<input name="profitability_amount" type="number" step="0.01" readonly />`)}
                ${formField("Рентабельность, %", `<input name="profitability_percent" type="number" step="0.01" readonly />`)}
              </div>
              ${formField("Комментарий", `<input name="comment" />`, "", "", "wide")}
              ${toolbar(`${actionButton("Предпросмотр", "", { attrs: 'data-request-preview="revenue"' })}${actionButton("Рассчитать и создать", "primary", { type: "submit" })}`, "wide")}
            </form>
          </div>
          <div class="manual-mode-panel" data-manual-mode-panel="explanation" hidden>
            <form id="explanationNoteForm" class="manual-form" data-submission-type="explanation_note">
              <div class="manual-request-field-grid" data-explanation-row="identity">
                ${formField("Название", `<input name="title" value="Пояснительная записка подразделения" required />`, "Название видно в списке заявок.")}
                ${formField("Тип данных", `<select name="line_type"><option value="plan">План</option><option value="fact">Факт</option></select>`, "Факт попадет в отчеты только в режиме факт или план-факт.")}
                ${formField("Кост-центр для заявки", `<select id="explanationCostCenter" name="cost_center_code" required></select>`, "Для офисной аренды и коммунальных услуг система рассчитает долю этого КЦ от общей базы; остальные статьи сохраняются напрямую в выбранный КЦ.")}
                ${formField("Охват распределения", `<span class="checkbox-line"><input id="explanationAllCostCenters" name="allocation_scope" type="checkbox" value="all" /><span>Выбрать все КЦ</span></span>`, "Для аренды и коммунальных платежей распределит сумму по всем КЦ действующей базы.", "", "explanation-office-policy")}
              </div>
              <div id="explanationCostCenterScope" class="scope-note wide"></div>
              <div class="manual-request-field-grid" data-explanation-row="details">
                ${formField("Статья", `<select id="explanationArticle" name="article_code"></select>`)}
                ${formField("Период", monthPickerControl("month", 1, true), "Выберите один или несколько месяцев.")}
                ${formField("Способ ввода суммы", `<select id="explanationAmountMode" name="amount_mode"><option value="annual">Внести сумму за весь период</option><option value="monthly">Внести сумму за каждый месяц</option></select>`)}
                ${formField("Общая сумма", `<input name="amount" data-explanation-total type="number" step="0.01" value="-300000" required />`, "В режиме помесячного ввода рассчитывается автоматически. Для офисных расходов применяется база рабочих мест; остальные статьи относятся к выбранному КЦ.")}
                ${formField("Тип офисного расхода", `<select name="source_row"><option value="129">Офисная аренда</option><option value="149">Коммунальные услуги</option></select>`, "Строки ОФИС!129 и ОФИС!149 имеют утверждённую политику распределения.", "", "explanation-office-policy")}
              </div>
              <div id="explanationMonthlyAmounts" class="monthly-budget-grid wide" data-explanation-monthly-host hidden></div>
              <input type="hidden" name="allocation_mode" value="effective_workplaces_v1" />
              <p id="explanationAllocationScope" class="scope-note wide">Общая база, база КЦ и доля — вычисляемые значения действующей базы рабочих мест. В предпросмотре и Excel будет только сумма выбранного КЦ; полный офисный источник для всех КЦ загружается отдельным Excel-файлом.</p>
              ${formField("Комментарий", `<input name="comment" />`, "", "", "wide")}
              ${toolbar(`${actionButton("Предпросмотр", "", { attrs: 'data-request-preview="explanation"' })}${actionButton("Создать пояснительную", "primary", { type: "submit" })}`, "wide")}
            </form>
          </div>
        </div>`
        , "route-panel route-primary scroll-panel manual-mode-workspace")}
        <div class="workspace-stack manual-support-stack">
          ${panel("Результат подачи", `<div id="manualRequestResult" class="preview-box"></div><div id="revenueRequestResult" class="preview-box"></div><div id="explanationNoteResult" class="preview-box"></div>`, "route-panel panel-fill")}
          ${panel("Подсказки", `<div class="list compact manual-mode-help">
            ${item("Строки бюджета", "Для обычных расходов, CAPEX и универсальных строк с несколькими статьями.")}
            ${item("Доходы блока A", "Для расчета клиентской выручки, АК и рентабельности по выбранному кост-центру.")}
            ${item("Пояснительная", "Для обоснования суммы, источника и базы распределения.")}
          </div>`, "route-panel panel-fill")}
        </div>
      </div>
    </section>
  `;
}

function renderWorkflowSection() {
  return html`
    <section class="workplace-section workspace-console workflow-workspace" data-workplace="workflow" data-desktop-redesign-route="workflow">
      <div class="workflow-grid">
        ${panel("Очередь согласования", `${toolbar(actionButton("Обновить", "", { id: "refreshApprovalQueue" }), "section-head")}<div id="approvalMetrics" class="metrics compact-metrics"></div>${scrollPanel("approvalQueue")}`, "route-panel panel-fill")}
        ${panel("Заявки и согласования", `<form class="filters route-filterbar">
          ${formField("Статус", `<select id="requestStatus"><option value="">Все</option><option value="draft">Черновик</option><option value="submitted">Подана</option><option value="in_review">На проверке</option><option value="pending_approval">На согласовании</option><option value="returned">Возвращена</option><option value="approved">Утверждена</option><option value="archived">Архив</option></select>`)}
          ${formField("Тип", `<select id="requestType"><option value="">Все</option><option value="revenue">Доходы</option><option value="opex">Расходы</option><option value="capex">CAPEX</option><option value="manual_budget">Универсальная</option><option value="explanation_note">Пояснительная</option></select>`)}
          ${formField("Назначено", `<select id="requestAssignedRole"><option value="">Все</option><option value="finance_controller">Финансовый контролер</option><option value="finance_admin">Финансовый администратор</option><option value="approver">Согласующий</option></select>`)}
          ${formField("Поиск", `<input id="requestSearch" />`)}
          ${formField("На странице", `<select id="requestLimit"><option>10</option><option selected>25</option><option>50</option></select>`)}
          ${toolbar(actionButton("Показать", "", { id: "applyRequestFilters" }))}
        </form><div id="requestListMeta" class="preview-box"></div>${scrollPanel("requests")}`, "route-panel route-primary")}
        ${panel("Центр проверок", `<div class="validation-bulk">
          ${formField("Решение для выбранных", `<textarea id="bulkValidationResolution" rows="2">Массово проверено финансовым контролером</textarea>`, "", "", "wide")}
          ${toolbar(`${actionButton("Закрыть выбранные", "", { id: "bulkResolveValidation" })}${actionButton("Обновить", "", { id: "refreshValidationCenter" })}`)}
        </div><form class="filters route-filterbar">
          ${formField("Статус", `<select id="validationStatus"><option value="open">Открытые</option><option value="">Все</option><option value="resolved">Закрытые</option></select>`)}
          ${formField("Важность", `<select id="validationSeverity"><option value="">Все</option><option value="error">Ошибки</option><option value="warning">Предупреждения</option></select>`)}
          ${formField("Шаблон", `<select id="validationTemplate"><option value="">Все</option><option value="compiled_budget">Готовый бюджет</option><option value="business_variant_1">Бизнес-вариант 1</option><option value="business_variant_2">Бизнес-вариант 2</option><option value="explanation_note">Пояснительная</option><option value="summary_c">Свод C</option></select>`)}
          ${formField("Импорт", `<input id="validationImportId" type="number" min="1" />`)}
          ${formField("Поиск", `<input id="validationSearch" />`)}
        </form>${scrollPanel("validation")}`, "route-panel panel-fill")}
        ${panel("Сравнение версий", `${toolbar(actionButton("Сравнить", "", { id: "compareVersions" }))}<form class="version-compare">${formField("База", `<select id="baseVersion"></select>`)}${formField("Сравнить с", `<select id="compareVersion"></select>`)}</form>${scrollPanel("versionDiff")}`, "route-panel panel-fill scroll-panel")}
        ${panel("Контрольные сверки", `<div data-control-run-panel>${toolbar(actionButton("Запустить", "", { id: "runControls", attrs: 'data-action="controls-run"' }))}<form class="version-compare">${formField("Версия", `<select id="controlVersion"></select>`)}</form>${scrollPanel("controlChecks")}</div>`, "route-panel panel-fill scroll-panel")}
      </div>
    </section>
  `;
}

function renderReportsSection() {
  return html`
    <section class="workplace-section workspace-console reports-workspace" data-workplace="reports" data-desktop-redesign-route="reports">
      ${panel("Фильтры отчетов", renderReportFilters("report", "applyReportFilters", "Применить"), "route-panel report-filters")}
      <div class="report-console-grid">
        ${panel("Строки бюджета и детализация", `${toolbar(`${actionButton("Обновить", "", { id: "refreshLines" })}${openWorkplaceButton("Excel-вид", "sheets")}<span id="reportLinesMeta" class="state muted">Строки загружаются</span>`, "report-table-actions")}<div class="table-wrap table-scroll report-table-scroll"><table class="data-table report-data-table"><thead><tr><th>Строка</th><th>Статья</th><th>Блок</th><th>Тип</th><th>Месяц</th><th class="num">Сумма</th><th>Источник</th><th></th></tr></thead><tbody id="lines"></tbody></table></div>`, "route-panel route-primary report-table-panel")}
        ${panel("Бюджет кост-центра", `${toolbar(`${actionButton("Обновить", "", { id: "refreshCostCenterBudget" })}<span id="costCenterBudgetMeta" class="state muted">Ожидает фильтры</span>`)}${scrollPanel("costCenterBudget")}`, "route-panel panel-fill")}
        ${panel("Свод A", `${toolbar(`${actionButton("Скачать свод A", "", { id: "exportSummaryA", attrs: 'data-capability="export"' })}<span id="summaryAMeta" class="state muted">Ожидает фильтры</span>`)}${scrollPanel("summaryA")}`, "route-panel panel-fill")}
        ${panel("Свод C", `${toolbar(`${actionButton("Скачать свод C", "", { id: "exportSummaryC", attrs: 'data-capability="export"' })}<span id="summaryCMeta" class="state muted">Ожидает фильтры</span>`)}${scrollPanel("summaryC")}`, "route-panel panel-fill")}
      </div>
    </section>
  `;
}

function renderBudgetSection() {
  return html`
    <section class="workplace-section workspace-console budget-workspace" data-workplace="budget" data-desktop-redesign-route="budget">
      <div class="budget-grid">
        ${panel(
          "Сборка бюджета",
          `<div class="budget-control-body">
            <form class="filters route-filterbar budget-filterbar">
              ${formField("Бюджетная кампания", `<select id="budgetCampaign"></select>`)}
              ${formField("Источник итогов", `<select id="budgetScopeMode"><option value="selection">Рабочая сборка (выбранные заявки)</option><option value="effective">Активный утверждённый бюджет</option></select>`)}
              ${formField("Шаблон итогового бюджета", `<select id="budgetTemplateProfile">${BUDGET_TEMPLATE_PROFILES.map((profile) => `<option value="${escapeHtml(profile.id)}">${escapeHtml(profile.label)}</option>`).join("")}</select>`)}
              ${formField("Название сборки", `<input id="budgetSelectionTitle" value="Рабочая сборка бюджета" />`)}
              ${formField("Сохраненная сборка", `<select id="budgetSavedSelection"><option value="">Новая сборка</option></select>`)}
              ${toolbar(`${actionButton("Обновить", "", { id: "refreshBudgetPreview" })}${actionButton("Вид шаблона", "", { id: "openBudgetTemplateView" })}${actionButton("Сохранить сборку", "", { id: "saveBudgetSelection", attrs: 'data-capability="export"' })}${actionButton("Скачать Excel", "", { id: "exportBudgetPreview", attrs: 'data-capability="export"' })}`)}
            </form>
            <div id="budgetScopeModeNote" class="scope-note budget-template-note"></div>
            <div id="budgetSavedSelectionNote" class="scope-note budget-template-note"></div>
            <div id="budgetTemplateProfileNote" class="scope-note budget-template-note"></div>
            <div id="budgetKpis" class="metrics compact-metrics"></div>
            <div id="budgetWarnings" class="preview-box"></div>
          </div>`,
          "route-panel budget-control-panel"
        )}
        ${panel("Заявки кампании", `<div id="budgetRequestsMeta" class="state muted">Заявки загружаются</div>${scrollPanel("budgetRequests")}`, "route-panel route-primary budget-requests-panel")}
        ${panel("Итог по шаблону", `<div id="budgetTemplateMeta" class="state muted">Матрица ожидает кампанию</div>${scrollPanel("budgetTemplate")}`, "route-panel panel-fill budget-template-panel")}
        ${panel("Свод по граням", `<div id="budgetFacetTotals" class="budget-facet-grid"></div>`, "route-panel panel-fill")}
      </div>
    </section>
  `;
}

function renderSheetsSection() {
  return html`
    <section class="workplace-section sheets-workspace" data-workplace="sheets" data-desktop-redesign-route="sheets" data-sheet-workspace="excel-like-filled">
      <div class="sheet-toolbar" data-sheet-toolbar>
        <label>Вид
          <select id="sheetViewSelect"></select>
        </label>
        <label>Лист
          <select id="sheetTabSelect"></select>
        </label>
        <label>Версия
          <select id="sheetVersionSelect"><option value="">Все версии</option></select>
        </label>
        <label>Диапазон
          <input id="sheetRangeInput" value="A1:W90" />
        </label>
        <button type="button" id="refreshSheetView">Обновить</button>
        <button type="button" data-open-workplace="reports">Отчеты</button>
        <span id="sheetMeta" class="state muted">Таблица загружается</span>
      </div>
      <div class="sheet-shell">
        <div class="sheet-grid-host" id="sheetGridHost" role="grid" aria-label="Excel-like заполненная таблица" tabindex="0"></div>
        <aside class="sheet-detail" id="sheetDetail" aria-label="Карточка ячейки">
          <div class="empty-state compact-empty">
            <strong>Выберите ячейку</strong>
            <small>Системные суммы, проверки и заявки откроются здесь без ухода из таблицы.</small>
          </div>
        </aside>
      </div>
      <div class="sheet-tabs" id="sheetTabs" role="tablist" aria-label="Листы workbook"></div>
    </section>
  `;
}

function renderExportsSection() {
  return html`
    <section class="workplace-section workspace-console exports-workspace" data-workplace="exports" data-desktop-redesign-route="exports">
      <div class="workspace-split export-grid">
        ${panel("Фильтры экспорта", renderReportFilters("export", "", ""), "route-panel route-primary")}
        ${panel("Консолидированный бюджет", `${toolbar(`${actionButton("Скачать Excel", "", { id: "exportXlsx", attrs: 'data-capability="export"' })}${actionButton("Скачать CSV", "", { id: "exportCsv", attrs: 'data-capability="export"' })}`)}<div id="consolidatedKpis" class="metrics compact-metrics"></div>${scrollPanel("consolidatedRows")}`, "route-panel panel-fill")}
      </div>
    </section>
  `;
}

function renderReportFilters(prefix: "report" | "export", buttonId: string, buttonLabel: string) {
  const id = (name: string) => `${prefix}${name}`;
  const button = buttonId ? toolbar(actionButton(buttonLabel, "", { id: buttonId })) : "";
  return `<form class="filters route-filterbar" data-report-filter-scope="${prefix}">
    ${formField("Год", `<input id="${id("Year")}" type="number" min="2000" placeholder="2026" />`)}
    ${formField("Бюджетная кампания", `<select id="${id("Campaign")}"></select>`)}
    ${formField("Кост-центр", `<select id="${id("CostCenter")}"></select>`)}
    ${formField("Версия", `<select id="${id("Version")}"></select>`)}
    ${formField("Статус", `<select id="${id("Status")}"><option value="">Все</option><option value="draft">Черновик</option><option value="submitted">Подана</option><option value="in_review">На проверке</option><option value="pending_approval">На согласовании</option><option value="approved">Утверждена</option><option value="correction">Корректировка</option></select>`)}
    ${formField("Тип данных", `<select id="${id("LineType")}"><option value="all">Все</option><option value="plan">План</option><option value="fact">Факт</option></select>`)}
    ${formField("План/факт", `<label class="checkbox-line"><input id="${id("PlanFact")}" type="checkbox" /> <span>Показать разницу</span></label>`)}
    ${formField("Блок", `<select id="${id("Block")}"><option value="">Все</option><option>A</option><option>B</option><option>C</option><option>D</option><option>E</option></select>`)}
    ${formField("Статья", `<select id="${id("Article")}"></select>`)}
    ${formField("Месяц", monthPickerControl("month", "", false, id("Month")))}
    ${button}
  </form>`;
}

function renderUiKitSection() {
  return html`
    <section class="workplace-section ui-kit" data-workplace="ui_kit" data-demo-only="true" data-ui-kit-contract="component-primitives">
      ${panel(
        "UI kit и компонентный контракт",
        `<div class="ui-kit-grid" data-ui-kit-showcase>
          <div class="ui-kit-sample" data-ui-component="Button" data-ui-component-alias="ActionButton IconButton">
            <strong>Кнопки действий</strong>
            <div class="actions">
              ${actionButton("Основное действие", "primary")}
              ${actionButton("Вторичное")}
              ${actionButton("Опасное", "danger")}
              ${actionButton("Недоступно", "", { disabled: true })}
              ${actionButton("Сохранение", "button-loading", { loading: true })}
              ${iconButton("Обновить", "↻")}
            </div>
          </div>
          <div class="ui-kit-sample" data-ui-component="FormField" data-ui-component-alias="CompactSelect">
            <strong>Поля формы</strong>
            <div class="manual-form">
              ${formField("План", '<input value="План бюджета" />', "Короткое имя сценария или версии.")}
              ${formField("Кост-центр", '<select class="compact-select"><option>MICE · Х / MICE</option><option>BK · БК</option></select>', "Плотный выбор для рабочих форм.")}
              ${formField("Комментарий", '<textarea rows="2">Комментарий к заявке</textarea>', "Дополнительный контекст для проверяющего.", "", "wide")}
              ${formField("Ошибка", '<input value="A.10" aria-invalid="true" />', "", "Код уже используется")}
              ${formField("Недоступное поле", '<input value="Закрыто" disabled />', "Поле недоступно в этом статусе.")}
              <label><input type="checkbox" checked /> Включено</label>
              ${formField("Файл", '<input type="file" />')}
            </div>
          </div>
          <div class="ui-kit-sample" data-ui-component="StatusChip" data-ui-component-alias="Badge SeverityBadge">
            <strong>Статусы и важность</strong>
            <div class="progress-track">
              ${statusChip("draft")}
              ${statusChip("pending_approval")}
              ${severityBadge("error")}
              ${severityBadge("warning")}
              ${severityBadge("resolved")}
              <span class="chip">Справочник</span>
            </div>
          </div>
          <div class="ui-kit-sample" data-ui-component="DataTable" data-ui-component-alias="FilterBar">
            <strong>Фильтры и таблица</strong>
            <div class="filters ui-kit-focus-ring"><label>Статус<select><option>Все</option></select></label><button type="button">Применить</button></div>
            ${dataTableSample()}
          </div>
          <div class="ui-kit-sample" data-ui-component="EmptyState" data-ui-component-alias="LoadingState ErrorState">
            <strong>Состояния списка</strong>
            ${loadingState("Загрузка данных")}
            ${emptyState("Пока нет строк для выбранного фильтра.", "Смените фильтр или создайте запись.")}
            ${errorState("Ошибка загрузки справочника")}
          </div>
          <div class="ui-kit-sample" data-ui-component="Drawer" data-ui-component-alias="Dialog Toast">
            <strong>Уведомление / панель деталей / окно</strong>
            <div class="toast static-toast">Данные сохранены</div>
            <div class="preview-box drawer-preview">Панель деталей: заявка, комментарии, вложения</div>
            <button type="button" data-ui-dialog-sample>Открыть окно</button>
          </div>
          <div class="ui-kit-sample" data-ui-component="WizardSteps" data-ui-component-alias="Stepper">
            <strong>Шаги мастера</strong>
            <div class="wizard-steps"><div class="wizard-step is-active"><span>1</span>Файл</div><div class="wizard-step"><span>2</span>Проверка</div><div class="wizard-step"><span>3</span>Очередь</div></div>
          </div>
          <div class="ui-kit-sample" data-ui-component="MetricTile" data-ui-component-alias="MetricStrip Timeline AttachmentList">
            <strong>Метрики, история и вложения</strong>
            <div class="metrics"><div class="metric"><strong>8</strong><span>кост-центров</span></div><div class="metric"><strong>12</strong><span>проверок интерфейса</span></div></div>
            <div class="timeline"><div>Создано</div><div>Проверено</div><div>Утверждено</div></div>
            <div class="list compact"><div class="item"><strong>budget.xlsx</strong><small>Вложение · 24 КБ</small></div></div>
          </div>
          <div class="ui-kit-sample" data-ui-component="Density">
            <strong>Плотность: обычная / компактная</strong>
            <div class="density-stack">
              <div class="density-regular">${item("Обычная строка", "Больше воздуха для справочного режима")}</div>
              <div class="density-compact list compact">${item("Компактная строка", "Плотный рабочий режим")}</div>
            </div>
          </div>
          <div class="ui-kit-sample" data-ui-component="WorkspaceHeader" data-ui-component-alias="Toolbar ScrollPanel">
            <strong>Шапка рабочей области / панель действий / панель с внутренней прокруткой</strong>
            <div class="section-head ui-kit-workspace-header"><div><span class="eyebrow">Раздел</span><h3>Заявки</h3></div><div class="toolbar">${actionButton("Обновить")}${iconButton("Добавить", "+")}</div></div>
            <div class="scroll-panel ui-kit-scroll-panel">${item("Внутренний скролл", "Длинные списки не растягивают экран")}${item("Фокус", "Интерактивные элементы показывают видимую рамку")}</div>
          </div>
        </div>`
      )}
    </section>
  `;
}

function actionButton(label: string, className = "", options: { attrs?: string; disabled?: boolean; id?: string; loading?: boolean; type?: "button" | "submit" } = {}) {
  const id = options.id ? ` id="${escapeHtml(options.id)}"` : "";
  const type = options.type || "button";
  const attrs = `${options.disabled ? " disabled" : ""}${options.loading ? ' aria-busy="true" data-loading-state="true"' : ""}`;
  const classes = className ? ` class="${className}"` : "";
  return `<button${id} type="${type}"${classes}${attrs}${options.attrs ? ` ${options.attrs}` : ""}>${escapeHtml(label)}</button>`;
}

function openWorkplaceButton(label: string, workplace: string, className = "", disabled = false) {
  const classes = className ? ` class="${className}"` : "";
  return `<button type="button"${classes} data-open-workplace="${escapeHtml(workplace)}"${disabled ? " disabled" : ""}>${escapeHtml(label)}</button>`;
}

function iconButton(label: string, symbol: string, className = "", id = "") {
  const idAttr = id ? ` id="${escapeHtml(id)}"` : "";
  const classes = ["icon-button", className].filter(Boolean).join(" ");
  return `<button${idAttr} type="button" class="${classes}" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}">${escapeHtml(symbol)}</button>`;
}

const TOOL_ICONS: Record<string, IconNode> = {
  card: Eye,
  sheet: FileSpreadsheet,
  submit: Send,
  review: Search,
  approve: Check,
  return: RotateCcw,
  correction: Pencil,
  archive: Archive,
  edit: Pencil,
  deactivate: Power,
  delete: Trash2,
  generic: ArrowRight
};

function lucideSvg(icon: IconNode) {
  const children = icon
    .map(([tag, attrs]) => {
      const attrText = Object.entries(attrs)
        .map(([key, value]) => `${key}="${escapeHtml(value)}"`)
        .join(" ");
      return `<${tag} ${attrText}></${tag}>`;
    })
    .join("");
  return `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${children}</svg>`;
}

function workflowToolButton(label: string, icon: string, attrs: string, disabled = false, className = "") {
  const classes = ["workflow-tool-button", className].filter(Boolean).join(" ");
  return `<button type="button" class="${classes}" ${attrs}${disabled ? " disabled" : ""} title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}">${lucideSvg(TOOL_ICONS[icon] || TOOL_ICONS.generic)}</button>`;
}

function workflowTransitionIcon(action: string) {
  const key = String(action || "");
  if (key === "submit" || key === "resubmit") return "submit";
  if (key === "review" || key === "start_review") return "review";
  if (key === "send_to_approval") return "generic";
  if (key === "approve") return "approve";
  if (key === "return") return "return";
  if (key === "request_correction" || key === "correction") return "correction";
  if (key === "archive") return "archive";
  return "generic";
}

function formField(label: string, control: string, help = "", error = "", className = "", attrs = "") {
  return `<label class="form-field ${className}" ${attrs}><span>${escapeHtml(label)}</span>${control}${help ? `<small class="form-help">${escapeHtml(help)}</small>` : ""}${error ? `<small class="form-error">${escapeHtml(error)}</small>` : ""}</label>`;
}

function monthPickerControl(name = "month", defaultMonth: number | "" = "", allowRange = false, id = "", disabled = false, allowCostCenters = false, forceRange = false) {
  const initialMonths = defaultMonth ? [Number(defaultMonth)] : [];
  const label = monthRangeLabel(initialMonths);
  const idAttr = id ? ` id="${escapeHtml(id)}"` : "";
  const startName = `${name}_start`;
  const endName = `${name}_end`;
  return html`
    <div class="month-picker-field" data-month-picker-field data-month-name="${escapeHtml(name)}" data-month-multi-select="1" data-month-cost-centers-enabled="${allowCostCenters ? "1" : "0"}" data-range-forced="${forceRange ? "1" : "0"}">
      <input${idAttr} type="hidden" name="${escapeHtml(name)}" value="${escapeHtml(defaultMonth)}" data-month-value />
      <input type="hidden" name="${escapeHtml(startName)}" value="${escapeHtml(defaultMonth)}" data-month-start />
      <input type="hidden" name="${escapeHtml(endName)}" value="${escapeHtml(defaultMonth)}" data-month-end />
      <input type="hidden" name="months" value="${escapeHtml(initialMonths.join(","))}" data-months-value />
      ${allowCostCenters ? '<input type="hidden" name="month_cost_centers" value="{}" data-month-cost-centers />' : ""}
      ${allowCostCenters ? '<input type="hidden" name="month_cost_center_amounts" value="{}" data-month-cost-center-amounts />' : ""}
      <button type="button" class="month-picker-button" data-open-month-picker ${disabled ? "disabled" : ""}>${escapeHtml(label)}</button>
    </div>
  `;
}

function setMonthPickerMonths(field: HTMLElement, months: number[]) {
  const normalized = [...new Set(months.filter((month) => month >= 1 && month <= 12))].sort((a, b) => a - b);
  const first = normalized[0] || "";
  const last = normalized[normalized.length - 1] || first;
  const value = field.querySelector<HTMLInputElement>("[data-month-value]");
  const start = field.querySelector<HTMLInputElement>("[data-month-start]");
  const end = field.querySelector<HTMLInputElement>("[data-month-end]");
  const all = field.querySelector<HTMLInputElement>("[data-months-value]");
  const button = field.querySelector<HTMLButtonElement>("[data-open-month-picker]");
  if (value) value.value = String(first || "");
  if (start) start.value = String(first || "");
  if (end) end.value = String(last || "");
  if (all) all.value = normalized.join(",");
  if (button) button.textContent = monthRangeLabel(normalized);
  if (monthCostCentersEnabled(field)) {
    const allowed = new Set(normalized.map(String));
    const values = Object.fromEntries(Object.entries(parseMonthCostCenters(field)).filter(([month]) => allowed.has(month)));
    const amounts = Object.fromEntries(Object.entries(parseMonthCostCenterAmounts(field)).filter(([month]) => allowed.has(month)));
    setMonthCostCenters(field, values);
    setMonthCostCenterAmounts(field, amounts);
  }
}

function renderMonthDialogGrid() {
  if (!activeMonthPicker) return;
  const selected = normalizeMonthListFromField(activeMonthPicker);
  const grid = document.querySelector("#monthDialogGrid");
  const hint = document.querySelector("#monthDialogHint");
  if (hint) hint.textContent = "Нажмите на один или несколько месяцев; повторное нажатие снимает выбор. Затем отметьте КЦ для каждого месяца.";
  if (!grid) return;
  grid.innerHTML = MONTH_NAMES.map((name, index) => {
    const month = index + 1;
    const active = selected.includes(month);
    return `<button type="button" class="${active ? "is-active" : ""}" data-month-option="${month}" role="option" aria-selected="${active}"><strong>${escapeHtml(name)}</strong><small>${month}</small></button>`;
  }).join("");
  renderMonthCostCenterPanel();
}

function openMonthPicker(field: HTMLElement) {
  activeMonthPicker = field;
  if (monthCostCentersEnabled(field)) ensureMonthCostCenterDefaults(field);
  renderMonthDialogGrid();
  const dialog = document.querySelector<HTMLDialogElement>("#monthDialog");
  if (dialog && !dialog.open) dialog.showModal();
}

function ensureMonthCostCenterDefaults(field: HTMLElement) {
  const selected = normalizeMonthListFromField(field);
  const values = parseMonthCostCenters(field);
  const amounts = parseMonthCostCenterAmounts(field);
  const fallback = defaultCostCenterForField(field);
  const fallbackAmount = defaultAmountForField(field);
  let changed = false;
  for (const month of selected) {
    const key = String(month);
    if (!values[key]?.length && fallback) {
      values[key] = [fallback];
      changed = true;
    }
    if (!amounts[key]) amounts[key] = {};
    for (const code of values[key] || []) {
      if (amounts[key][code] === undefined && fallbackAmount) {
        amounts[key][code] = fallbackAmount;
        changed = true;
      }
    }
  }
  if (changed) {
    setMonthCostCenters(field, values);
    setMonthCostCenterAmounts(field, amounts);
  }
}

function renderMonthCostCenterPanel(appliedBulkAmount?: string) {
  const host = document.querySelector("#monthCostCenterPanel");
  if (!host || !activeMonthPicker) return;
  if (!monthCostCentersEnabled(activeMonthPicker)) {
    host.innerHTML = "";
    return;
  }
  ensureMonthCostCenterDefaults(activeMonthPicker);
  const selected = normalizeMonthListFromField(activeMonthPicker);
  const values = parseMonthCostCenters(activeMonthPicker);
  const amounts = parseMonthCostCenterAmounts(activeMonthPicker);
  const centers = availableCostCenters();
  const bulkAmount = appliedBulkAmount ?? String(defaultAmountForField(activeMonthPicker) || "");
  if (!selected.length) {
    host.innerHTML = "";
    return;
  }
  host.innerHTML = html`
    <div class="month-cost-center-bulk">
      <label>
        <span>Групповая сумма</span>
        <input type="number" step="0.01" data-month-cost-center-bulk-amount value="${escapeHtml(bulkAmount)}" />
      </label>
      <button type="button" data-month-cost-center-apply-bulk>Заполнить выбранные КЦ</button>
    </div>
    ${selected.map((month) => {
    const selectedCodes = new Set(values[String(month)] || []);
    const monthAmounts = amounts[String(month)] || {};
    return html`
      <fieldset class="month-cost-center-group" data-month-cost-center-group="${month}">
        <legend>${escapeHtml(monthLabel(month))}</legend>
        <div class="month-cost-center-options">
          ${centers.map((center) => html`
            <label>
              <input type="checkbox" data-month-cost-center-option data-month="${month}" value="${escapeHtml(center.code)}" ${selectedCodes.has(center.code) ? "checked" : ""} />
              <span>${escapeHtml(center.code)} · ${escapeHtml(center.name)}</span>
              <input type="number" step="0.01" data-month-cost-center-amount data-month="${month}" data-cost-center-code="${escapeHtml(center.code)}" value="${escapeHtml(monthAmounts[center.code] || "")}" ${selectedCodes.has(center.code) ? "" : "disabled"} />
            </label>
          `).join("")}
        </div>
      </fieldset>
    `;
  }).join("")}
  `;
}

function updateMonthCostCenterSelection(input: HTMLInputElement) {
  if (!activeMonthPicker) return;
  const month = input.dataset.month;
  if (!month) return;
  const values = parseMonthCostCenters(activeMonthPicker);
  const amounts = parseMonthCostCenterAmounts(activeMonthPicker);
  const checked = [...document.querySelectorAll<HTMLInputElement>(`[data-month-cost-center-option][data-month="${CSS.escape(month)}"]:checked`)].map((item) => item.value);
  values[month] = checked.length ? checked : [defaultCostCenterForField(activeMonthPicker)].filter(Boolean);
  amounts[month] = Object.fromEntries(Object.entries(amounts[month] || {}).filter(([code]) => values[month].includes(code)));
  const fallbackAmount = defaultAmountForField(activeMonthPicker);
  for (const code of values[month]) {
    if (amounts[month][code] === undefined && fallbackAmount) amounts[month][code] = fallbackAmount;
  }
  setMonthCostCenters(activeMonthPicker, values);
  setMonthCostCenterAmounts(activeMonthPicker, amounts);
  renderMonthCostCenterPanel();
}

function updateMonthCostCenterAmount(input: HTMLInputElement) {
  if (!activeMonthPicker) return;
  const month = input.dataset.month;
  const code = input.dataset.costCenterCode;
  if (!month || !code) return;
  const amounts = parseMonthCostCenterAmounts(activeMonthPicker);
  amounts[month] = amounts[month] || {};
  const value = input.value.trim();
  if (value) amounts[month][code] = Number(value);
  else delete amounts[month][code];
  setMonthCostCenterAmounts(activeMonthPicker, amounts);
}

function persistMonthCostCenterPanelInputs() {
  if (!activeMonthPicker) return;
  const amounts = parseMonthCostCenterAmounts(activeMonthPicker);
  document.querySelectorAll<HTMLInputElement>("[data-month-cost-center-amount]").forEach((input) => {
    const month = input.dataset.month;
    const code = input.dataset.costCenterCode;
    if (!month || !code || input.disabled) return;
    amounts[month] = amounts[month] || {};
    const value = input.value.trim();
    if (value) amounts[month][code] = Number(value);
    else delete amounts[month][code];
  });
  setMonthCostCenterAmounts(activeMonthPicker, amounts);
}

function applyMonthCostCenterBulkAmount() {
  if (!activeMonthPicker) return;
  const input = document.querySelector<HTMLInputElement>("[data-month-cost-center-bulk-amount]");
  if (!input || !input.value.trim()) return;
  const amount = Number(input.value);
  const values = parseMonthCostCenters(activeMonthPicker);
  const amounts = parseMonthCostCenterAmounts(activeMonthPicker);
  for (const [month, codes] of Object.entries(values)) {
    amounts[month] = amounts[month] || {};
    for (const code of codes) {
      amounts[month][code] = amount;
    }
  }
  setMonthCostCenterAmounts(activeMonthPicker, amounts);
  renderMonthCostCenterPanel(input.value);
}

function defaultAmountForField(field: HTMLElement | null) {
  const row = field?.closest<HTMLElement>("[data-manual-line]");
  const form = field?.closest("form");
  const amount = row?.querySelector<HTMLInputElement>('[name="amount"]')?.value || form?.querySelector<HTMLInputElement>('[name="billing"]')?.value || "";
  return Number(amount || 0);
}

function chooseMonth(month: number) {
  if (!activeMonthPicker) return;
  const selected = normalizeMonthListFromField(activeMonthPicker);
  const next = selected.includes(month) ? selected.filter((value) => value !== month) : [...selected, month];
  setMonthPickerMonths(activeMonthPicker, next);
  if (monthCostCentersEnabled(activeMonthPicker)) ensureMonthCostCenterDefaults(activeMonthPicker);
  renderRevenueMonthlyBilling();
  renderExplanationMonthlyAmounts();
  renderMonthDialogGrid();
}

function clearMonthPicker() {
  if (!activeMonthPicker) return;
  setMonthPickerMonths(activeMonthPicker, []);
  if (monthCostCentersEnabled(activeMonthPicker)) {
    setMonthCostCenters(activeMonthPicker, {});
    setMonthCostCenterAmounts(activeMonthPicker, {});
  }
  renderRevenueMonthlyBilling();
  renderExplanationMonthlyAmounts();
  renderMonthDialogGrid();
}

function renderRevenueMonthlyBilling() {
  const form = document.querySelector<HTMLFormElement>("#revenueRequestForm");
  const host = document.querySelector<HTMLElement>("#revenueMonthlyBilling");
  if (!form || !host) return;
  const field = form.querySelector<HTMLElement>("[data-month-picker-field]");
  const billingInput = form.querySelector<HTMLInputElement>('[name="billing"]');
  const months = field ? normalizeMonthListFromField(field) : [];
  const costCenterAmounts = parseMonthCostCenterAmounts(field);
  const previous = new Map([...host.querySelectorAll<HTMLInputElement>("[data-monthly-billing-input]")].map((input) => [input.dataset.month || "", input.value]));
  const showMonthly = months.length > 1;
  host.toggleAttribute("hidden", !showMonthly);
  billingInput?.toggleAttribute("required", !showMonthly);
  if (!showMonthly) {
    host.innerHTML = "";
    return;
  }
  const defaultBilling = billingInput?.value || "0";
  host.innerHTML = html`
    <div class="section-head">
      <strong>Бюджет по месяцам</strong>
      <small>АК и рентабельность применяются едино на год.</small>
    </div>
    ${months.map((month) => html`
      <label>
        <span>${escapeHtml(monthLabel(month))}</span>
        <input name="monthly_billing_${month}" data-monthly-billing-input data-month="${month}" type="number" step="0.01" value="${escapeHtml(monthlyBillingDisplayValue({ amountsByMonth: costCenterAmounts, month, previousValue: previous.get(String(month)), fallbackValue: defaultBilling }))}" required />
      </label>
    `).join("")}
  `;
  syncRevenueBillingFromMonthlyInputs(form);
}

function syncRevenueBillingFromMonthlyInputs(form: HTMLFormElement) {
  const billing = form.querySelector<HTMLInputElement>('[name="billing"]');
  const monthlyInputs = [...form.querySelectorAll<HTMLInputElement>("[data-monthly-billing-input]")];
  if (!billing || !monthlyInputs.length) return;
  billing.value = String(totalMonthlyBilling(monthlyInputs.map((input) => input.value)));
  recalculateRevenueDrivers(form);
}

function renderExplanationMonthlyAmounts() {
  const form = document.querySelector<HTMLFormElement>("#explanationNoteForm");
  const host = document.querySelector<HTMLElement>("#explanationMonthlyAmounts");
  if (!form || !host) return;
  const mode = form.querySelector<HTMLSelectElement>('[name="amount_mode"]')?.value || "annual";
  const totalInput = form.querySelector<HTMLInputElement>("[data-explanation-total]");
  const field = form.querySelector<HTMLElement>("[data-month-picker-field]");
  const months = field ? normalizeMonthListFromField(field) : [];
  const previous = new Map([...host.querySelectorAll<HTMLInputElement>("[data-explanation-monthly-input]")].map((input) => [input.dataset.month || "", input.value]));
  const showMonthly = mode === "monthly";
  host.toggleAttribute("hidden", !showMonthly);
  if (totalInput) totalInput.readOnly = showMonthly;
  if (!showMonthly) {
    host.innerHTML = "";
    return;
  }
  const fallback = months.length && totalInput ? Number(totalInput.value || 0) / months.length : 0;
  host.innerHTML = html`
    <div class="section-head">
      <strong>Суммы по месяцам</strong>
      <small>Общая сумма рассчитывается автоматически.</small>
    </div>
    ${months.map((month) => html`
      <label>
        <span>${escapeHtml(monthLabel(month))}</span>
        <input name="monthly_amount_${month}" data-explanation-monthly-input data-month="${month}" type="number" step="0.01" value="${escapeHtml(previous.get(String(month)) ?? String(Math.round(fallback * 100) / 100))}" required />
      </label>
    `).join("")}
    ${months.length ? "" : `<p class="muted">Сначала выберите период.</p>`}
  `;
  syncExplanationTotalFromMonthlyInputs(form);
}

function syncExplanationTotalFromMonthlyInputs(form: HTMLFormElement) {
  const total = form.querySelector<HTMLInputElement>("[data-explanation-total]");
  const inputs = [...form.querySelectorAll<HTMLInputElement>("[data-explanation-monthly-input]")];
  if (!total) return;
  total.value = String(totalMonthlyBilling(inputs.map((input) => input.value)));
}

function revenueBilling(form: HTMLFormElement) {
  return Number(form.querySelector<HTMLInputElement>('[name="billing"]')?.value || 0);
}

function setRevenueDriverValue(form: HTMLFormElement, name: string, value: number) {
  const input = form.querySelector<HTMLInputElement>(`[name="${name}"]`);
  if (input) input.value = Number.isFinite(value) ? String(Math.round(value * 100) / 100) : "0";
}

function setRevenueDriverPercentValue(form: HTMLFormElement, name: string, value: number) {
  const input = form.querySelector<HTMLInputElement>(`[name="${name}"]`);
  if (input) input.value = Number.isFinite(value) ? String(Number(value.toFixed(10))) : "0";
}

function recalculateRevenueDrivers(form: HTMLFormElement, changed?: HTMLInputElement | null) {
  const billing = revenueBilling(form);
  let ak = Number(form.querySelector<HTMLInputElement>('[name="ak_percent"]')?.value || 0) / 100;
  let rc = Number(form.querySelector<HTMLInputElement>('[name="rc_percent"]')?.value || 0) / 100;
  let sk = Number(form.querySelector<HTMLInputElement>('[name="sk_percent"]')?.value || 0) / 100;
  const driver = changed?.dataset.revenueDriver;
  if (driver && changed?.dataset.driverUnit) {
    form.dataset[`${driver}Source`] = changed.dataset.driverUnit;
  }
  const akSource = form.dataset.akSource || "percent";
  const rcSource = form.dataset.rcSource || "percent";
  const skSource = form.dataset.skSource || "percent";
  const akInputAmount = Number(form.querySelector<HTMLInputElement>('[name="ak_amount"]')?.value || 0);
  const rcInputAmount = Number(form.querySelector<HTMLInputElement>('[name="rc_amount"]')?.value || 0);
  const skInputAmount = Number(form.querySelector<HTMLInputElement>('[name="sk_amount"]')?.value || 0);
  const akAmount = akSource === "amount" ? akInputAmount : billing * ak;
  const rcAmount = rcSource === "amount" ? rcInputAmount : billing * rc;
  if (akSource === "amount") {
    ak = billing ? akAmount / billing : 0;
    setRevenueDriverPercentValue(form, "ak_percent", ak * 100);
  } else {
    setRevenueDriverValue(form, "ak_amount", akAmount);
  }
  if (rcSource === "amount") {
    rc = billing ? rcAmount / billing : 0;
    setRevenueDriverPercentValue(form, "rc_percent", rc * 100);
  } else {
    setRevenueDriverValue(form, "rc_amount", rcAmount);
  }
  const skBase = billing * (1 - rc);
  const skAmount = skSource === "amount" ? skInputAmount : skBase * sk;
  if (skSource === "amount") {
    sk = skBase ? skAmount / skBase : 0;
    setRevenueDriverPercentValue(form, "sk_percent", sk * 100);
  } else {
    setRevenueDriverValue(form, "sk_amount", skAmount);
  }
  const profitabilityAmount = akAmount + rcAmount + skAmount;
  const profitabilityPercent = billing + akAmount ? profitabilityAmount / (billing + akAmount) * 100 : 0;
  setRevenueDriverValue(form, "profitability_amount", profitabilityAmount);
  setRevenueDriverValue(form, "profitability_percent", profitabilityPercent);
}

function syncSingleRevenueBillingFromCostCenterAmounts(field: HTMLElement | null) {
  const form = field?.closest<HTMLFormElement>("#revenueRequestForm");
  if (!field || !form) return;
  const months = normalizeMonthListFromField(field);
  if (months.length !== 1) return;
  const total = monthlyCostCenterTotal(parseMonthCostCenterAmounts(field), months[0]);
  const billing = form.querySelector<HTMLInputElement>('[name="billing"]');
  if (billing && total !== null) billing.value = String(total);
}

function toolbar(content: string, className = "", attrs = "") {
  const classes = ["actions", "toolbar", className].filter(Boolean).join(" ");
  return `<div class="${classes}" ${attrs}>${content}</div>`;
}

function scrollPanel(id: string, className = "list compact list-scroll", attrs = "") {
  return `<div id="${escapeHtml(id)}" class="${escapeHtml(className)}" ${attrs}></div>`;
}

function dataTableSample() {
  return `<div class="table-wrap"><table class="data-table"><thead><tr><th>Статья с длинным названием</th><th>КЦ</th><th class="num">Сумма</th></tr></thead><tbody><tr><td>A.10 Доходы от очень длинного клиентского направления</td><td>MICE</td><td class="num">1 200 000</td></tr><tr><td>C.2.1.1 Подрядчики и производственные расходы</td><td>MEDIA_AGENCY</td><td class="num">-850 000</td></tr></tbody></table></div>`;
}

function emptyState(title: string, subtitle = "") {
  return `<div class="empty-state"><strong>${escapeHtml(title)}</strong>${subtitle ? `<small>${escapeHtml(subtitle)}</small>` : ""}</div>`;
}

function loadingState(label: string) {
  return `<div class="state loading-state" aria-busy="true">${escapeHtml(label)}</div>`;
}

function errorState(message: string) {
  return `<div class="error-state" role="alert">${escapeHtml(message)}</div>`;
}

function panel(title: string, body: string, className = "", attrs = "") {
  return `<article class="panel ${className}" ${attrs}><h2>${title}</h2>${body}</article>`;
}

function miniForm(id: string, title: string, fields: string[], extra = "", adminTool = "") {
  const toolAttrs = adminTool ? ` data-admin-tool-panel="${adminTool}"${adminTool === "articles" ? ' class="mini-form is-active"' : ' class="mini-form"'}` : ' class="mini-form"';
  return `<form id="${id}"${toolAttrs}><strong>${title}</strong><input type="hidden" name="id" />${fields.map((field) => {
    const [name, placeholder] = field.split(":");
    const required = ["code", "title", "block", "name"].includes(name) ? "required" : "";
    return formField(placeholder, `<input name="${name}" placeholder="${placeholder}" ${required} />`);
  }).join("")}${extra}${formField("Активность", `<select name="active"><option value="1">Активен</option><option value="0">Неактивен</option></select>`)}${toolbar(actionButton("Сохранить", "primary", { type: "submit" }) + actionButton("Очистить", "", { attrs: `data-admin-clear-form="${id}"` }))}</form>`;
}

function adminRightPanel(tool: string, title: string, listId: string) {
  const active = tool === "articles" ? " is-active" : "";
  return panel(title, `${adminListFilter(tool)}${scrollPanel(listId)}`, `route-panel panel-fill admin-entity-panel${active}`, `data-admin-list-panel="${tool}"`);
}

function adminListFilter(tool: string) {
  return `<div class="admin-list-filter" role="search">
    <input data-admin-list-filter="${escapeHtml(tool)}" aria-label="Поиск по текущему справочнику" placeholder="Поиск по коду, названию или описанию" />
    <small>Фильтрует только текущий список справа.</small>
  </div>`;
}

function adminHelpManualHtml() {
  return `<details class="admin-help-manual" data-admin-help-manual>
    <summary><span>Подробный хелп-мануал по справочникам и сопоставлениям</span><span class="state">Админка без загадок</span></summary>
    <div class="admin-help-body">
      <section>
        <h3>1. Зачем нужен этот раздел</h3>
        <p>Админка управляет тем, как система превращает реальные файлы, заявки и источники в управляемый бюджет. Здесь задаются статьи, кост-центры, пользователи, правила сопоставления, правила аллокации и бизнес-измерения. Ошибка в этих настройках почти всегда проявляется дальше: строка попала не в ту статью, файл не распознал кост-центр, пользователь видит лишний контур или проверка импорта просит создать сопоставление.</p>
        <p>Работайте по принципу: сначала справочник, затем правило, затем импорт или проверка. Если не уверены, не создавайте несколько похожих правил подряд; лучше найдите существующее через поиск справа и проверьте, не покрывает ли оно ваш случай.</p>
      </section>
      <section>
        <h3>2. Статьи бюджета</h3>
        <p><strong>Код</strong> - стабильный идентификатор статьи. Пример: <code>C.2.1.1</code>. Код используют импорты, отчеты, аллокации и ручные заявки. Менять код после эксплуатации опасно: старые строки бюджета останутся привязаны к прежнему коду.</p>
        <p><strong>Название</strong> - человеческое имя статьи, которое видят финансисты и руководители. Пишите предметно: не "Расходы", а "Аренда офиса", "Командировки", "Производственные подрядчики".</p>
        <p><strong>Блок</strong> группирует статьи: A обычно доходы, B CAPEX, C OPEX, D налоги/долги/амортизация, E прочее или специальные управленческие блоки. Блок влияет на фильтры отчетов и типы заявок.</p>
        <p><strong>Родитель</strong> нужен для иерархии. Например, <code>C.2</code> может быть родителем для <code>C.2.1.1</code>. Если родителя нет, статья считается верхнеуровневой.</p>
        <p><strong>Знак</strong>: плюс для доходов или положительных корректировок, минус для расходов. Пример эксплуатации: доходная статья блока A обычно имеет знак плюс, аренда офиса в блоке C - минус.</p>
      </section>
      <section>
        <h3>3. Кост-центры</h3>
        <p><strong>Код</strong> - короткий машинный идентификатор подразделения или контура. Примеры: <code>MICE</code>, <code>BK</code>, <code>OFFICE</code>. Код должен совпадать с тем, что используют импорты, пользователи и отчеты.</p>
        <p><strong>Название</strong> - понятное имя. Пример: "MICE / Event-направление", "Back Office", "Медиабаинг".</p>
        <p><strong>Алиасы</strong> помогают распознавать разные написания из Excel. Если в файлах встречаются "M.I.C.E", "MICE dept", "Event", добавьте их как алиасы. Это уменьшает ручные проверки после импорта.</p>
        <p>Реальный пример: файл поставщика содержит лист "Events", а бюджетный контур называется <code>MICE</code>. Добавьте alias <code>Events</code>, чтобы импорт понимал, куда относить строки.</p>
      </section>
      <section>
        <h3>4. Пользователи и роли</h3>
        <p><strong>Логин</strong> - код пользователя для демо-режима или интеграции. <strong>Имя</strong> показывается в интерфейсе, комментариях и истории. <strong>Роль</strong> определяет доступ: наблюдатель читает, контролер проверяет, администратор настраивает, согласующий утверждает.</p>
        <p><strong>Кост-центр пользователя</strong> ограничивает рабочий контур. Если руководитель подразделения должен видеть только свой бюджет, задайте ему кост-центр. Финансовые роли обычно могут работать шире, но это зависит от backend navigation.</p>
        <p>Пример эксплуатации: если сотрудник MICE создает заявку, но видит BK, проверьте его кост-центр и роль. Если роль верная, смотрите backend navigation и доступные cost centers.</p>
      </section>
      <section>
        <h3>5. Правила сопоставления</h3>
        <p>Сопоставление отвечает на вопрос: "Когда импорт видит такой источник, в какую статью и кост-центр положить строку?".</p>
        <p><strong>Тип источника</strong> описывает происхождение: compiled_budget, source_form, explanation_note, summary или внутренний тип интеграции. <strong>Поле сопоставления</strong> говорит, на что смотрим: статья из файла, название строки, лист, колонка, supplier или другое поле. <strong>Значение источника</strong> - конкретная строка, которую нужно распознать.</p>
        <p><strong>Целевая статья</strong> - куда попадет сумма. <strong>Целевой кост-центр</strong> задавайте только если правило действительно всегда относится к одному контуру. <strong>Знак суммы</strong> нужен, когда источник хранит расходы положительными числами, а бюджет должен показывать их как минус.</p>
        <p>Пример: в файле написано "Office rent", поле сопоставления <code>row_title</code>, значение <code>Office rent</code>, целевая статья <code>C.2.1.1</code>, знак минус. После этого похожие строки импорта перестанут попадать в проверки.</p>
      </section>
      <section>
        <h3>6. Правила аллокации</h3>
        <p>Аллокация распределяет общую сумму на статью и кост-центр по базе: рабочие места, площадь, ФОТ, фиксированная доля, ручная доля или прямое отнесение.</p>
        <p><strong>Название</strong> должно объяснять бизнес-смысл: "Офисная аренда по площади", "IT лицензии по рабочим местам". <strong>Лист</strong> и <strong>строка источника</strong> помогают связать правило с Excel-источником. <strong>База всего</strong> и <strong>База КЦ</strong> задают долю. Например, всего 295 рабочих мест, у MICE 11.23; система рассчитает долю и сумму.</p>
        <p><strong>Ручная доля</strong> используйте только когда нет надежной базы. Она удобна для временных управленческих распределений, но ее нужно регулярно пересматривать.</p>
      </section>
      <section>
        <h3>7. Бизнес-измерения</h3>
        <p>Бизнес-измерения нужны для блока A и доходных заявок: клиент, медиа, тип договора, юрлицо. Они делают заявки сопоставимыми: вместо свободного текста "клиент Х" пользователь выбирает нормализованное значение.</p>
        <p><strong>Код</strong> используйте короткий и стабильный. <strong>Название</strong> должно совпадать с коммерческим употреблением. <strong>Алиасы</strong> добавляйте для вариантов из договоров и медиапланов.</p>
      </section>
      <section>
        <h3>8. Практический порядок работы</h3>
        <ol>
          <li>Найдите нужный объект через поиск справа.</li>
          <li>Если объекта нет, выберите вкладку инструмента и заполните форму.</li>
          <li>Сохраните и убедитесь, что объект появился в списке.</li>
          <li>Если пришли из проверки импорта, вернитесь в workflow и обновите центр проверок.</li>
          <li>Запустите импорт или проверку еще раз только после того, как справочник и правило сохранены.</li>
        </ol>
      </section>
      <section>
        <h3>9. Частые ошибки</h3>
        <p>Не создавайте две статьи с почти одинаковым смыслом. Не используйте человеческое название как код. Не задавайте кост-центр в mapping rule, если правило должно работать для разных подразделений. Не ставьте плюс расходной статье только потому, что Excel хранит сумму положительной: для этого есть знак сопоставления.</p>
      </section>
      <section>
        <h3>10. Мини-сценарии эксплуатации</h3>
        <p><strong>Новый клиент в доходной заявке:</strong> добавьте business dimension типа client, затем проверьте форму доходной заявки.</p>
        <p><strong>Импорт ругается на неизвестную строку:</strong> откройте деталь проверки, нажмите "Открыть сопоставления", создайте mapping rule и повторите проверку.</p>
        <p><strong>Сумма офиса должна распределяться по площади:</strong> создайте allocation rule с base_type area, задайте total/base и целевую статью.</p>
        <p><strong>Пользователь видит не тот контур:</strong> проверьте его роль, cost center и navigation от backend.</p>
      </section>
    </div>
  </details>`;
}

function updatePageHeader() {
  if (state.deniedWorkplace) {
    const deniedMeta = WORKPLACE_META[state.deniedWorkplace];
    text("#pageEyebrow", "Доступ ограничен");
    text("#pageTitle", "Раздел недоступен");
    text("#pageSubtitle", `${roleLabel(state.me?.actor?.role)} не имеет доступа к разделу "${deniedMeta.title}".`);
    text("#pagePrimaryAction", "На рабочий стол");
    return;
  }
  const meta = WORKPLACE_META[state.workplace];
  text("#pageEyebrow", meta.eyebrow);
  text("#pageTitle", meta.title);
  text("#pageSubtitle", meta.subtitle);
  text("#pagePrimaryAction", meta.action);
}

function routeFromLocation() {
  const route = routeFromPath(window.location.pathname, (localStorage.getItem("budgetWorkplace") as Workplace) || "dashboard");
  const sheetTab = new URLSearchParams(window.location.search).get("tab");
  return sheetTab ? { ...route, sheetTab } : route;
}

function setRoute(workplace: Workplace, detailId?: string) {
  const path = pathForWorkplace(workplace, detailId);
  if (window.location.pathname !== path) window.history.pushState({ workplace, detailId }, "", path);
}

function setSheetRoute(viewId: string, tabId?: string | null) {
  const path = `/sheets/${encodeURIComponent(viewId)}${tabId ? `?tab=${encodeURIComponent(tabId)}` : ""}`;
  if (`${window.location.pathname}${window.location.search}` !== path) {
    window.history.pushState({ workplace: "sheets", sheetViewId: viewId, sheetTab: tabId }, "", path);
  }
}

function setWorkplace(workplace: Workplace, options: { updateUrl?: boolean } = {}) {
  const denied = !canOpenWorkplace(workplace);
  state.deniedWorkplace = denied ? workplace : null;
  if (denied) {
    const meta = WORKPLACE_META[workplace];
    document.querySelectorAll("[data-workplace]").forEach((element) => element.classList.remove("is-active"));
    document.querySelectorAll("[data-workplace-nav]").forEach((element) => element.classList.remove("is-active"));
    document.querySelector<HTMLElement>("[data-workplace-denied]")?.classList.add("is-active");
    text("#deniedRouteTitle", `Нет доступа: ${meta.title}`);
    text("#deniedRouteReason", `${roleLabel(state.me?.actor?.role)} видит только разделы, возвращенные backend navigation. Данные чужого контура не запрашиваются.`);
    updatePageHeader();
    if (options.updateUrl !== false) setRoute(workplace);
    return;
  }
  state.workplace = workplace;
  state.deniedWorkplace = null;
  localStorage.setItem("budgetWorkplace", workplace);
  document.querySelector<HTMLElement>("[data-workplace-denied]")?.classList.remove("is-active");
  document.querySelectorAll("[data-workplace]").forEach((element) => {
    element.classList.toggle("is-active", element.getAttribute("data-workplace") === workplace);
  });
  document.querySelectorAll("[data-workplace-nav]").forEach((element) => {
    const isActive = element.getAttribute("data-workplace-nav") === workplace;
    element.classList.toggle("is-active", isActive);
    if (isActive) element.setAttribute("aria-current", "page");
    else element.removeAttribute("aria-current");
  });
  updatePageHeader();
  if (options.updateUrl !== false) setRoute(workplace);
}

function renderWorkplaceNavigation() {
  const nav = document.querySelector("#workplaceNav");
  if (!nav) return;
  const items = state.me?.navigation?.length ? [...state.me.navigation] : [{ id: "dashboard" as Workplace, label: "Рабочий стол" }];
  if (state.me?.demo_auth_enabled && !items.some((item) => item.id === "ui_kit")) {
    items.push({ id: "ui_kit", label: "UI kit" });
  }
  nav.innerHTML = items.map((item) => `<button type="button" data-workplace-nav="${item.id}">${escapeHtml(item.label)}</button>`).join("");
  nav.querySelectorAll<HTMLButtonElement>("[data-workplace-nav]").forEach((button) => {
    button.addEventListener("click", () => setWorkplace(button.dataset.workplaceNav as Workplace));
  });
}

function applyCapabilities() {
  document.querySelectorAll<HTMLElement>("[data-capability]").forEach((element) => {
    const capability = element.dataset.capability || "";
    element.classList.toggle("is-hidden", !hasCapability(capability));
  });
}

function syncVisibility() {
  applyCapabilities();
}

function text(selector: string, value: unknown) {
  const element = document.querySelector(selector);
  if (element) element.textContent = String(value ?? "");
}

function item(title: unknown, subtitle = "", attrs = "") {
  return `<div class="item" ${attrs}><strong>${escapeHtml(title)}</strong>${subtitle ? `<small>${subtitle}</small>` : ""}</div>`;
}

function canEditBudgetLine(version: any) {
  const role = state.me?.actor?.role;
  return role !== undefined && ["finance_controller", "finance_admin"].includes(role) && hasCapability("workflow_action") && !version?.locked && !["approved", "archived"].includes(String(version?.status || ""));
}

function revenueDriverPayload(line: any) {
  const payload = parseJsonObject(line?.source_payload_json);
  return Object.values(payload).some(Boolean) && ["billing", "profitability", "ak_percent", "income", "suppliers"].some((key) => payload[key] !== undefined)
    ? payload
    : null;
}

const DASHBOARD_METRIC_LABELS: Record<string, string> = {
  campaigns: "Кампаний",
  budget_lines: "Строк бюджета",
  imports_total: "Импортов всего",
  imports_running: "Импорт выполняется",
  imports_queued: "Импортов в очереди",
  jobs_queued: "Задач в очереди",
  jobs_running: "Задач выполняется",
  jobs_completed: "Задач завершено",
  jobs_failed: "Задач с ошибкой",
  jobs_canceled: "Задач отменено",
  system_jobs_queued: "Системных задач в очереди",
  system_jobs_running: "Системных задач выполняется",
  system_jobs_completed: "Системных задач завершено",
  system_jobs_failed: "Системных задач с ошибкой",
  system_jobs_canceled: "Системных задач отменено",
  requests_total: "Заявок всего",
  requests_in_review: "Заявок на проверке",
  notifications_unread: "Непрочитанных уведомлений",
  explanation_generic_lines: "Строк пояснительных расчетов",
  source_sheets: "Листов источника"
};

function dashboardMetricLabel(key: string) {
  return DASHBOARD_METRIC_LABELS[key] || key.replaceAll("_", " ");
}

function metricTile(label: string, value: unknown, attrs = "") {
  return `<div class="metric" ${attrs}><strong>${escapeHtml(value ?? "0")}</strong><small>${escapeHtml(label)}</small></div>`;
}

function activeLabel(value: unknown) {
  return Number(value ?? 1) === 0 ? "выключен" : "активен";
}

function signLabel(value: unknown) {
  return Number(value ?? 1) < 0 ? "расход / минус" : "доход / плюс";
}

const MATCH_KEY_LABELS: Record<string, string> = {
  sheet: "лист",
  row: "строка",
  article: "статья",
  cost_center: "кост-центр",
  text: "текст",
  source_sheet: "лист источника",
  source_row: "строка источника"
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  compiled_budget: "готовый бюджет",
  source_form: "исходная форма",
  explanation_note: "пояснительная",
  summary: "сводная форма",
  summary_c: "свод C",
  business_variant_1: "бизнес-вариант 1",
  business_variant_2: "бизнес-вариант 2",
  manual_request: "ручная заявка",
  revenue_request: "доходная заявка",
  revenue: "доходная заявка"
};

const CAMPAIGN_SUBMISSION_LABELS: Record<string, string> = {
  before_window: "подача еще не началась",
  inside_window: "идет подача",
  after_window: "период подачи завершен",
  no_window: "окно подачи не задано"
};

const REPORT_KPI_LABELS: Record<string, string> = {
  total_amount: "Итого",
  revenue_amount: "Доходы",
  expense_amount: "Расходы",
  capex_amount: "CAPEX",
  variance_amount: "Отклонение",
  source_rows: "Строк источника",
  budget_lines: "Строк бюджета",
  cost_centers: "Кост-центров"
};

const EVENT_ACTION_LABELS: Record<string, string> = {
  created: "Создано",
  submitted: "Подано",
  approved: "Утверждено",
  returned: "Возвращено",
  archived: "В архиве",
  correction: "Корректировка",
  request_correction: "Запрошена корректировка",
  comment: "Комментарий"
};

function sourceTypeLabel(value: unknown) {
  const key = String(value || "");
  return SOURCE_TYPE_LABELS[key] || requestTypeLabel(key);
}

function campaignSubmissionLabel(value: unknown) {
  const key = String(value || "");
  return CAMPAIGN_SUBMISSION_LABELS[key] || key.replaceAll("_", " ") || "состояние подачи не указано";
}

function reportKpiLabel(value: unknown) {
  const key = String(value || "");
  return REPORT_KPI_LABELS[key] || dashboardMetricLabel(key);
}

function eventActionLabel(value: unknown) {
  const key = String(value || "");
  return EVENT_ACTION_LABELS[key] || statusLabel(key);
}

function matchKeyLabel(value: unknown) {
  const key = String(value || "");
  return MATCH_KEY_LABELS[key] || key.replaceAll("_", " ");
}

function mappingRuleTitle(rule: any) {
  return `${sourceTypeLabel(rule.source_type)} · ${matchKeyLabel(rule.match_key)}`;
}

function mappingRuleSubtitle(rule: any) {
  const sourceValue = rule.match_value ? `значение: ${rule.match_value}` : "значение не задано";
  const targetArticle = rule.target_article_code ? `статья ${rule.target_article_code}` : "статья не выбрана";
  const targetCostCenter = rule.target_cost_center_code ? ` · кост-центр ${rule.target_cost_center_code}` : "";
  return `${sourceValue} → ${targetArticle}${targetCostCenter}`;
}

function firstReadyAction(actions: any[]) {
  return actions.find((action) => action?.ready !== false) || actions[0] || null;
}

async function loadMe(generation?: number) {
  const currentBootstrap = () => generation === undefined || generation === bootstrapGeneration;
  let me: MeResponse;
  try {
    me = await api<MeResponse>("/api/me");
  } catch (error) {
    if (error instanceof Error && error.message.includes("Демо-аутентификация")) {
      setApiContext({ sessionAuth: true, user: "" });
      me = await api<MeResponse>("/api/me");
    } else {
      throw error;
    }
  }
  if (!currentBootstrap()) return false;
  state.me = me;
  const actor = state.me.actor;
  state.loginRequired = state.me.demo_auth_enabled === false && !actor.code;
  text("#sidebarActor", actor.display_name || actor.code || roleLabel(actor.role));
  text("#actorState", `${roleLabel(actor.role)}${actor.cost_center_code ? ` · ${actor.cost_center_code}` : ""}`);
  document.querySelector("#loginForm")?.classList.toggle("is-hidden", !state.loginRequired);
  document.querySelector("#logoutButton")?.classList.toggle("is-hidden", state.me.demo_auth_enabled !== false || !actor.code);
  renderWorkplaceNavigation();
  applyCapabilities();
  const role = document.querySelector<HTMLSelectElement>("#role");
  if (role) role.value = getApiContext().role;
  document.querySelectorAll(".role-picker").forEach((element) => {
    element.classList.toggle("is-hidden", state.me?.demo_auth_enabled === false);
  });
  if (state.loginRequired) return true;
  await loadDictionaries();
  if (!currentBootstrap()) return false;
  renderUserPicker();
  renderCostCenterOptions();
  return true;
}

async function loadDashboard() {
  const dashboard = await api<Record<string, any>>("/api/dashboard/workplace");
  const campaign = dashboard.active_campaign as Campaign | null;
  const systemActivity = dashboard.system_activity || {};
  const activeJob = systemActivity.active_job || dashboard.active_job;
  const systemBusy = Boolean(systemActivity.busy);
  const scopedBusy = Boolean(systemActivity.scoped_busy);
  text("#systemState", systemBusy ? `Система занята${scopedBusy ? " в вашем контуре" : ""}` : "Система готова");
  text("#activeCampaign", campaign ? `${campaign.year} · ${campaign.scenario} · ${statusLabel(campaign.status)} · ${campaignSubmissionLabel(campaign.submission_state)}` : "Активная кампания не выбрана");
  const counts = dashboard.counts || dashboard.counters || {};
  const roleWorkplace = dashboard.role_workplace || {};
  const quickActions = dashboard.quick_actions || primaryActionsFromRoleWorkplace(roleWorkplace);
  const nextAction = firstReadyAction(quickActions);
  text("#dashboardHeroTitle", campaign ? `Кампания ${campaign.year} · ${campaign.scenario} · ${statusLabel(campaign.status)}` : "Активная кампания не выбрана");
  text("#dashboardHeroSubtitle", `${roleLabel(state.me?.actor?.role)} · ${roleWorkplace.cost_center_code || state.me?.actor?.cost_center_code || "общий контур"} · ${systemBusy ? "есть активные системные операции" : "операционный контур готов"}`);
  render("#dashboardHeroAction", nextAction ? openWorkplaceButton(nextAction.label || nextAction.title || "Открыть следующий шаг", nextAction.workplace || "dashboard", "primary", nextAction.ready === false) : openWorkplaceButton("Открыть задачи", "workflow"));
  render("#metrics", Object.entries(counts).map(([key, value]) => metricTile(dashboardMetricLabel(key), value, `data-metric-key="${escapeHtml(key)}"`)).join("") || emptyState("Метрик пока нет", "Появятся после загрузки рабочего контура."));
  render("#activeJob", activeJob ? `${activeJob.status || ""} · ${activeJob.message || activeJob.file_name || "активная задача"}` : "Нет активной задачи");
  render("#quickActions", renderActionList(quickActions, 4));
  render("#roleWorkplace", renderRoleStatus(roleWorkplace, counts));
  render("#roleWidgets", (dashboard.role_widgets || []).slice(0, 2).map((widget: any) => metricTile(widget.label || widget.title || "Показатель", widget.value ?? widget.count ?? "—")).join("") || metricTile("Задачи в работе", counts.requests_in_review || 0));
  render("#submissionStatus", (dashboard.request_statuses || []).map((row: any) => item(statusLabel(row.status), `${row.cost_center_code || "общий"} · назначено: ${roleLabel(row.assigned_role)} · ${row.count || 0}`)).join("") || item("Заявок пока нет"));
  renderAttention(dashboard.attention_items || dashboard.attention || {});
  await Promise.allSettled([loadTasks(), loadNotifications(), loadImports(), loadQueue()]);
}

function renderActionList(actions: any[], limit = 6) {
  return actions.slice(0, limit).map((action) => `<div class="item action-item"><strong>${escapeHtml(action.label || action.title || "Действие")}</strong><small>${escapeHtml(action.description || action.reason || "")}</small>${openWorkplaceButton("Открыть", action.workplace || "dashboard", "", action.ready === false)}</div>`).join("") || emptyState("Нет срочных действий", "Рабочий день можно начать с текущих задач или отчетов.");
}

function renderRoleWorkplace(workplace: Record<string, any>) {
  if (!Object.keys(workplace).length) return emptyState("Рабочее место настраивается", "Ролевой контекст появится после загрузки профиля.");
  return [
    item(workplace.mission || "Фокус роли", `КЦ: ${workplace.cost_center_code || "общий контур"} · кампания: ${workplace.campaign_status || "не выбрана"}`),
    ...primaryActionsFromRoleWorkplace(workplace).map((action: any) => `<div class="item"><strong>${escapeHtml(action.label || "Действие")}</strong>${openWorkplaceButton("Открыть", action.workplace || "dashboard", "", action.ready === false)}</div>`)
  ].join("");
}

function renderRoleStatus(workplace: Record<string, any>, counts: Record<string, any>) {
  if (!Object.keys(workplace).length) return emptyState("Рабочее место настраивается", "Виджеты роли появятся после загрузки профиля.");
  const role = roleLabel(workplace.role || state.me?.actor?.role);
  const scope = workplace.cost_center_code || state.me?.actor?.cost_center_code || "общий контур";
  const campaign = workplace.campaign_status ? statusLabel(workplace.campaign_status) : "кампания не выбрана";
  const activeCount = counts.requests_in_review ?? counts.jobs_running ?? 0;
  return `<div class="role-status-card"><strong>${escapeHtml(role)}</strong><small>${escapeHtml(scope)} · ${escapeHtml(campaign)}</small><span class="chip">${escapeHtml(activeCount)} в работе</span></div>`;
}

function renderAttention(attention: Record<string, any>) {
  const values = Object.values(attention).flat().filter(Boolean) as any[];
  render("#attention", values.map((entry) => item(entry.title || entry.label || entry.message || "Требует внимания", entry.message || entry.reason || "")).join("") || emptyState("Открытых блокеров нет", "Критичные препятствия не найдены."));
}

async function loadNotifications() {
  const notifications = await api<any[]>("/api/notifications");
  render("#notifications", notifications.slice(0, 8).map((note) => `<div class="item event-item"><strong>${escapeHtml(note.title)}</strong><small>${escapeHtml(note.message || "")}</small><button type="button" data-notification-read="${note.id}">Прочитано</button></div>`).join("") || item("Новых уведомлений нет"));
}

async function markNotificationRead(id: string) {
  await api(`/api/notifications/${id}/read`, { method: "POST", body: JSON.stringify({}) });
  showToast("Уведомление отмечено прочитанным", "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("notification_read").join(",");
  await Promise.allSettled([loadNotifications(), loadDashboard()]);
}

async function trackedDownload(path: string, filename: string) {
  const result = await downloadFile(path, filename);
  root.dataset.lastDownloadPath = result.path;
  root.dataset.lastDownloadFilename = result.filename;
  root.dataset.lastDownloadContentType = result.contentType;
  root.dataset.lastDownloadSize = String(result.size);
  showToast(`Скачан файл ${filename}`, "success");
  return result;
}

async function loadTasks() {
  const tasks = await api<BudgetRequest[]>("/api/tasks");
  render("#tasks", tasks.slice(0, 8).map(renderRequestItem).join("") || item("Назначенных задач нет"));
}

async function loadDictionaries() {
  state.dictionaries = await api<Dictionaries>("/api/dictionaries");
  renderAdminLists();
  await loadFormulaAudit();
  renderExplanationArticleOptions();
}

async function loadFormulaAudit() {
  if (state.me?.actor?.role !== "finance_admin") {
    render("#formulaAudit", item("Аудит формул доступен финансовому администратору"));
    return;
  }
  const audit = await api<any>("/api/admin/formula-audit");
  const counts = Object.entries(audit.summary?.status_counts || {})
    .map(([status, count]) => `<span class="chip">${escapeHtml(status)}: ${escapeHtml(String(count))}</span>`)
    .join("");
  const rows = (audit.items || []).slice(0, 80).map((entry: any) =>
    item(
      `${entry.import_id} · ${entry.sheet}!${entry.address}`,
      `${entry.parity_status} · ${entry.ownership} · ${entry.audit_reason || ""}`
    )
  ).join("");
  render("#formulaAudit", `<div class="formula-audit-summary">${counts || '<span class="chip">нет формул</span>'}</div>${rows || item("Формульный аудит пуст")}`);
}

async function loadAudit() {
  const rows = await api<any[]>("/api/audit");
  render("#audit", rows.slice(0, 100).map((entry) => item(`${entry.action} · ${entry.entity_type}`, `${entry.actor_role || ""} · ${entry.created_at || ""}`)).join("") || item("Журнал аудита пуст"));
}

function renderUserPicker() {
  const select = document.querySelector<HTMLSelectElement>("#user");
  if (!select || !state.dictionaries) return;
  const currentUser = getApiContext().user;
  const users = state.dictionaries.users || [];
  let userOptions = users.map((user) => `<option value="${escapeHtml(user.code)}" data-role="${user.role}">${escapeHtml(user.display_name)} · ${roleLabel(user.role)}${user.cost_center_code ? ` · ${user.cost_center_code}` : ""}</option>`).join("");
  if (currentUser && !users.some((user) => user.code === currentUser) && state.me?.actor?.code === currentUser) {
    const actor = state.me.actor;
    userOptions = `<option value="${escapeHtml(actor.code)}" data-role="${actor.role}">${escapeHtml(actor.display_name || actor.code)} · ${roleLabel(actor.role)}${actor.cost_center_code ? ` · ${actor.cost_center_code}` : ""}</option>${userOptions}`;
  }
  select.innerHTML = `<option value="">Демо-роль</option>` + userOptions;
  select.value = getApiContext().user;
}

function renderCostCenterOptions() {
  const centers = availableCostCenters();
  const referenceCenters = referenceCostCenters();
  const operationalOptions = costCenterOptions(centers);
  const referenceOptions = costCenterOptions(referenceCenters);
  const optionalOptions = `<option value="">Все доступные</option>${operationalOptions}`;
  for (const selector of ["#uploadCostCenter", "#manualCostCenter", "#revenueCostCenter", "#explanationCostCenter"]) {
    const select = document.querySelector<HTMLSelectElement>(selector);
    if (select) select.innerHTML = operationalOptions;
  }
  for (const selector of ["#campaignCostCenters", "#userCostCenter", "#mappingCostCenter", "#allocationCostCenter"]) {
    const select = document.querySelector<HTMLSelectElement>(selector);
    if (select) select.innerHTML = ["#userCostCenter", "#mappingCostCenter", "#allocationCostCenter"].includes(selector) ? `<option value="">Не задан</option>${referenceOptions}` : referenceOptions;
  }
  for (const selector of ["#reportCostCenter", "#exportCostCenter"]) {
    const select = document.querySelector<HTMLSelectElement>(selector);
    if (select) select.innerHTML = optionalOptions;
  }
  renderCostCenterScopeNotes(centers, referenceCenters);
  renderReportArticleOptions();
  renderBusinessDimensionOptions();
}

function availableCostCenters() {
  return availableCostCentersFrom(state.me, state.dictionaries);
}

function referenceCostCenters() {
  return referenceCostCentersFrom(state.me, state.dictionaries);
}

function costCenterOptions(centers: CostCenter[]) {
  return costCenterOptionsHtml(centers);
}

function renderCostCenterScopeNotes(centers: CostCenter[], referenceCenters = centers) {
  const note = costCenterScopeNote(state.me, centers, referenceCenters);
  for (const selector of ["#uploadCostCenterScope", "#manualCostCenterScope", "#revenueCostCenterScope", "#explanationCostCenterScope"]) {
    text(selector, note);
  }
}

function renderAdminLists() {
  const data = state.dictionaries;
  if (!data) return;
  const articles = filterAdminRows("articles", data.articles, (article) => `${article.code} ${article.title} ${article.block}`);
  const centers = filterAdminRows("cost_centers", data.cost_centers, (center) => `${center.code} ${center.name} ${center.aliases_json || ""}`);
  const mappings = filterAdminRows("mapping", data.mapping_rules, (rule: any) => `${rule.source_type} ${rule.match_key} ${rule.match_value} ${rule.target_article_code || ""} ${rule.target_cost_center_code || ""}`);
  const users = filterAdminRows("users", data.users, (user) => `${user.display_name} ${user.code} ${roleLabel(user.role)} ${user.cost_center_code || ""}`);
  const allocations = filterAdminRows("allocations", data.allocation_rules, (rule: any) => `${rule.name} ${rule.source_sheet || ""} ${rule.source_row || ""} ${rule.target_article_code || ""} ${rule.target_cost_center_code || ""}`);
  const clients = filterAdminRows("clients", data.clients || [], (client: any) => `${client.code} ${client.name} ${client.legal_name || ""} ${client.inn || ""} ${client.client_type || ""} ${client.client_category || ""}`);
  const dimensions = filterAdminRows("business_dimensions", data.business_dimensions, (dimension: any) => `${dimension.dimension_type} ${dimension.code} ${dimension.name}`);
  render("#articleList", articles.slice(0, 80).map((article) => adminEditableItem("articles", "articleForm", article.code, article as any, `${article.code} · ${article.title}`, `Блок ${article.block} · ${signLabel(article.sign)} · ${activeLabel((article as any).active)}`)).join("") || item("Статей не найдено"));
  render("#costCenterList", centers.map((center) => adminEditableItem("cost-centers", "costCenterForm", center.code, center as any, `${center.code} · ${center.name}`, `алиасы: ${escapeHtml(center.aliases_json || "[]")} · ${activeLabel(center.active)}`)).join("") || item("Кост-центры не найдены"));
  render("#mappingList", mappings.map((rule: any) => adminEditableItem("mapping-rules", "mappingForm", rule.id, rule, mappingRuleTitle(rule), mappingRuleSubtitle(rule))).join("") || item("Правил пока нет"));
  render("#userList", users.map((user) => adminEditableItem("users", "userForm", user.code, user as any, `${user.display_name} · ${roleLabel(user.role)}`, `${user.code}${user.cost_center_code ? ` · ${user.cost_center_code}` : ""} · ${activeLabel(user.active)}`)).join("") || item("Пользователи скрыты для роли"));
  render("#allocationList", allocations.map((rule: any) => adminEditableItem("allocation-rules", "allocationForm", rule.id, rule, rule.name, `${rule.source_sheet || ""}:${rule.source_row || ""} → ${rule.target_article_code || ""} · ${rule.target_cost_center_code || ""} · ${activeLabel(rule.active)}`)).join("") || item("Правил пока нет"));
  render("#clientList", clients.map((client: any) => adminEditableItem("clients", "clientForm", client.id, client, `${client.code} · ${client.name}`, `${client.inn || "ИНН не задан"} · ${client.client_type || "тип не задан"} · ${client.client_category || "категория не задана"} · ${activeLabel(client.active)}`)).join("") || item("Клиентов пока нет"));
  render("#businessDimensionList", dimensions.slice(0, 100).map((dimension: any) => adminEditableItem("business-dimensions", "businessDimensionForm", dimension.id, dimension, `${dimension.dimension_type} · ${dimension.code}`, `${dimension.name} · ${activeLabel(dimension.active)}`)).join("") || item("Измерений пока нет"));
}

function adminEditableItem(dictionary: string, formId: string, key: string | number, row: Record<string, unknown>, title: string, subtitle = "") {
  const payload = encodeURIComponent(JSON.stringify(row));
  const attrs = `data-admin-edit="${escapeHtml(dictionary)}" data-admin-form="${escapeHtml(formId)}" data-admin-key="${escapeHtml(String(key))}" data-admin-payload="${payload}"`;
  return `<div class="item admin-editable-item" ${attrs} role="button" tabindex="0"><div class="admin-editable-main"><strong>${escapeHtml(title)}</strong>${subtitle ? `<small>${subtitle}</small>` : ""}</div><div class="actions admin-icon-actions">${workflowToolButton("Править", "edit", attrs)}${workflowToolButton("Деактивировать", "deactivate", `data-admin-deactivate="${escapeHtml(dictionary)}" data-admin-key="${escapeHtml(String(key))}"`)}${workflowToolButton("Удалить", "delete", `data-admin-delete="${escapeHtml(dictionary)}" data-admin-key="${escapeHtml(String(key))}"`, false, "danger")}</div></div>`;
}

function filterAdminRows<T>(tool: string, rows: T[], searchableText: (row: T) => string) {
  const query = document.querySelector<HTMLInputElement>(`[data-admin-list-filter="${tool}"]`)?.value.trim().toLowerCase() || "";
  if (!query) return rows;
  return rows.filter((row) => searchableText(row).toLowerCase().includes(query));
}

function renderExplanationArticleOptions() {
  const select = document.querySelector<HTMLSelectElement>("#explanationArticle");
  if (!select) return;
  const previous = select.value;
  select.innerHTML = articleOptions((state.dictionaries?.articles || []).filter((article) => ["B", "C", "D", "E"].includes(article.block)));
  if (previous) select.value = previous;
  syncExplanationAllocationPolicy();
}

function syncExplanationAllocationPolicy() {
  const form = document.querySelector<HTMLFormElement>("#explanationNoteForm");
  const article = form?.querySelector<HTMLSelectElement>("#explanationArticle")?.value || "";
  const isOfficeAllocation = ["C.2.1", "C.2.1.1", "C.2.2", "C.2.2.1"].includes(article);
  const allocationMode = form?.querySelector<HTMLInputElement>("[name='allocation_mode']");
  const allCostCenters = form?.querySelector<HTMLInputElement>("#explanationAllCostCenters");
  const sourceRow = form?.querySelector<HTMLSelectElement>("[name='source_row']");
  const officePolicies = form?.querySelectorAll<HTMLElement>(".explanation-office-policy");
  const scope = form?.querySelector<HTMLElement>("#explanationAllocationScope");
  if (allocationMode) allocationMode.value = isOfficeAllocation ? "effective_workplaces_v1" : "";
  if (allCostCenters) {
    allCostCenters.disabled = !isOfficeAllocation;
    if (!isOfficeAllocation) allCostCenters.checked = false;
  }
  if (sourceRow) sourceRow.value = article === "C.2.2" || article === "C.2.2.1" ? "149" : "129";
  officePolicies?.forEach((officePolicy) => { officePolicy.hidden = !isOfficeAllocation; });
  if (scope) scope.textContent = isOfficeAllocation
    ? allCostCenters?.checked
      ? "Годовая сумма будет распределена по всем КЦ действующей базы рабочих мест. Предпросмотр покажет долю и сумму каждого КЦ."
      : "Общая база, база КЦ и доля — вычисляемые значения действующей базы рабочих мест. В предпросмотре и Excel будет только сумма выбранного КЦ."
    : "Для этой статьи сумма не распределяется по базе рабочих мест: в предпросмотре и Excel она будет сохранена полностью только в выбранном КЦ.";
}

function articleOptions(articles: Article[]) {
  return articles.map((article) => `<option value="${escapeHtml(article.code)}">${escapeHtml(article.code)} · ${escapeHtml(article.title)}</option>`).join("");
}

function renderReportArticleOptions() {
  const report = document.querySelector<HTMLSelectElement>("#reportArticle");
  if (report) report.innerHTML = `<option value="">Все статьи</option>${articleOptions(state.dictionaries?.articles || [])}`;
  const exportArticle = document.querySelector<HTMLSelectElement>("#exportArticle");
  if (exportArticle) exportArticle.innerHTML = `<option value="">Все статьи</option>${articleOptions(state.dictionaries?.articles || [])}`;
  const mapping = document.querySelector<HTMLSelectElement>("#mappingArticle");
  if (mapping) mapping.innerHTML = articleOptions(state.dictionaries?.articles || []);
  const allocation = document.querySelector<HTMLSelectElement>("#allocationArticle");
  if (allocation) allocation.innerHTML = articleOptions((state.dictionaries?.articles || []).filter((article) => article.block === "C"));
  const explanation = document.querySelector<HTMLSelectElement>("#explanationArticle");
  if (explanation) {
    const previous = explanation.value;
    explanation.innerHTML = articleOptions((state.dictionaries?.articles || []).filter((article) => ["B", "C", "D", "E"].includes(article.block)));
    if (previous) explanation.value = previous;
    syncExplanationAllocationPolicy();
  }
  document.querySelectorAll<HTMLSelectElement>('#manualLineEditor select[name="article_code"]').forEach((select) => {
    const previous = select.value;
    select.innerHTML = articleOptions(state.dictionaries?.articles || []);
    if (previous) select.value = previous;
  });
}

function renderBusinessDimensionOptions() {
  const dimensions = state.dictionaries?.business_dimensions || [];
  const byType = (type: string) =>
    dimensions
      .filter((item: any) => item.dimension_type === type && item.active !== 0)
      .map((item: any) => `<option value="${escapeHtml(item.name || item.code)}">${escapeHtml(item.code)} · ${escapeHtml(item.name)}</option>`)
      .join("");
  const client = document.querySelector<HTMLSelectElement>("#revenueClient");
  if (client) client.innerHTML = byType("client") || `<option value="Новый клиент">Новый клиент</option>`;
  const media = document.querySelector<HTMLSelectElement>("#revenueMedia");
  if (media) media.innerHTML = byType("media") || `<option value="MICE">MICE</option>`;
  const contract = document.querySelector<HTMLSelectElement>("#revenueContractType");
  if (contract) {
    const options = byType("contract_type");
    if (options) contract.innerHTML = options;
  }
  const fillRequiredDimension = (selector: string, type: string) => {
    const select = document.querySelector<HTMLSelectElement>(selector);
    if (!select) return false;
    const items = dimensions.filter((item: any) => item.dimension_type === type && item.active !== 0);
    select.innerHTML = `<option value="">${items.length ? "Выберите…" : "Справочник пуст — заполните в разделе справочников"}</option>${items
      .map((item: any) => `<option value="${escapeHtml(item.code)}">${escapeHtml(item.code)} · ${escapeHtml(item.name)}</option>`)
      .join("")}`;
    return items.length > 0;
  };
  const clientDimensionsReady = [
    fillRequiredDimension("#newRevenueClientCategory", "client_category"),
    fillRequiredDimension("#newRevenueClientResponsible", "responsible"),
    fillRequiredDimension("#newRevenueClientAgency", "agency"),
  ].every(Boolean);
  const createClientButton = document.querySelector<HTMLButtonElement>('#revenueClientForm button[type="submit"]');
  if (createClientButton) createClientButton.disabled = !clientDimensionsReady;
  if (!clientDimensionsReady) render("#revenueClientResult", "Сначала заполните справочники категорий, ответственных менеджеров и агентств.");
}

function openRevenueClientDialog() {
  const dialog = document.querySelector<HTMLDialogElement>("#revenueClientDialog");
  if (!dialog) return;
  render("#revenueClientResult", "");
  renderBusinessDimensionOptions();
  if (!dialog.open) dialog.showModal();
  dialog.querySelector<HTMLInputElement>('input[name="name"]')?.focus();
}

async function submitRevenueClient(event: SubmitEvent) {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const payload = Object.fromEntries(new FormData(form).entries());
  const result = await api<any>("/api/requests/revenue/clients", { method: "POST", body: JSON.stringify(payload) });
  await loadDictionaries();
  const select = document.querySelector<HTMLSelectElement>("#revenueClient");
  const client = result.client || result.business_dimension || result;
  const candidates = [client.name, client.code, payload.name].map(String);
  const option = [...(select?.options || [])].find((item) => candidates.includes(item.value) || candidates.some((candidate) => item.textContent?.includes(candidate)));
  if (select && option) select.value = option.value;
  render("#revenueClientResult", `Клиент «${escapeHtml(client.name || payload.name)}» создан и выбран.`);
  showToast("Клиент создан и выбран", "success");
  form.reset();
  document.querySelector<HTMLDialogElement>("#revenueClientDialog")?.close();
}

async function loadCampaigns() {
  const campaigns = await api<Campaign[]>("/api/campaigns");
  state.campaigns = campaigns;
  render(
    "#campaigns",
    campaigns
      .map((campaign, index) => renderCampaignItem(campaign, index))
      .join("") || emptyState("Кампаний пока нет", "Создайте первую через плюс в заголовке раздела.")
  );
  renderReportCampaignOptions();
}

function renderReportCampaignOptions() {
  const campaigns = state.campaigns || [];
  const options = `<option value="">Активная бюджетная кампания</option>` + campaigns.map((campaign) => {
    const period = campaign.year ? `${campaign.year} · ` : "";
    return `<option value="${campaign.id}">${escapeHtml(period + campaign.scenario)} · ${escapeHtml(statusLabel(campaign.status))}</option>`;
  }).join("");
  for (const selector of ["#reportCampaign", "#exportCampaign"]) {
    const select = document.querySelector<HTMLSelectElement>(selector);
    if (select) select.innerHTML = options;
  }
}

function renderBudgetCampaignOptions(campaigns: Campaign[], activeCampaignId?: string | null) {
  const select = document.querySelector<HTMLSelectElement>("#budgetCampaign");
  if (!select) return "";
  const allowedIds = new Set(campaigns.map((campaign) => String(campaign.id)));
  const candidate = state.budgetCampaignId || select.value || activeCampaignId || String(campaigns[0]?.id || "");
  const selectedId = allowedIds.has(String(candidate)) ? String(candidate) : String(activeCampaignId || campaigns[0]?.id || "");
  select.innerHTML = campaigns.map((campaign) => `<option value="${escapeHtml(String(campaign.id))}">${escapeHtml(`${campaign.year} · ${campaign.scenario}`)} · ${escapeHtml(statusLabel(campaign.status))}</option>`).join("");
  select.value = selectedId;
  state.budgetCampaignId = selectedId || null;
  return selectedId;
}

function renderCampaignItem(campaign: Campaign, index: number) {
  const period = `${campaign.submission_start || "без старта"} - ${campaign.submission_end || "без завершения"}`;
  const forms = (campaign.template_types || []).map(requestTypeLabel).join(", ") || "формы не выбраны";
  const centers = (campaign.cost_center_codes || []).join(", ") || "все доступные кост-центры";
  return html`
    <button type="button" class="item campaign-item" data-campaign-detail="${index}" aria-label="Открыть кампанию ${escapeHtml(String(campaign.year))} ${escapeHtml(campaign.scenario)}">
      <span class="campaign-item-main">
        <strong>${escapeHtml(campaign.year)} · ${escapeHtml(campaign.scenario)}</strong>
        ${statusChip(campaign.status)}
      </span>
      <small>${escapeHtml(period)} · ${escapeHtml(forms)}</small>
      <span class="campaign-item-meta">${escapeHtml(centers)}</span>
    </button>
  `;
}

function openCampaignDialog() {
  const dialog = document.querySelector<HTMLDialogElement>("#campaignDialog");
  if (!dialog) return;
  render("#campaignResult", "");
  if (!dialog.open) dialog.showModal();
  dialog.querySelector<HTMLInputElement>('input[name="year"]')?.focus();
}

function closeCampaignDialog() {
  const dialog = document.querySelector<HTMLDialogElement>("#campaignDialog");
  if (dialog?.open) dialog.close();
}

function openCampaignDetail(index: string) {
  const campaign = state.campaigns?.[Number(index)];
  if (!campaign) return;
  const forms = (campaign.template_types || []).map(requestTypeLabel).join(", ") || "формы не выбраны";
  const centers = (campaign.cost_center_codes || []).join(", ") || "все доступные кост-центры";
  openSidePanel(html`
    <h2>${escapeHtml(campaign.year)} · ${escapeHtml(campaign.scenario)}</h2>
    <div class="drawer-lead">Кампания открыта из списка. Здесь видны период подачи, доступные формы и контур кост-центров.</div>
    <div class="progress-track">
      ${statusChip(campaign.status)}
      <span class="chip">${escapeHtml(campaignSubmissionLabel(campaign.submission_state))}</span>
      <span class="chip">кампания ${escapeHtml(campaign.id)}</span>
    </div>
    <div class="list compact">
      ${item("Период подачи", `${campaign.submission_start || "без старта"} - ${campaign.submission_end || "без завершения"}`)}
      ${item("Формы", forms)}
      ${item("Кост-центры", centers)}
      ${item("Активная версия", campaign.active_version_id ? `#${campaign.active_version_id}` : "не назначена")}
    </div>
  `);
}

async function loadImports() {
  const imports = await api<any[]>("/api/imports");
  render("#imports", imports.map((entry) => {
    const title = entry.upload_title || entry.file_name || "Импорт";
    const subtitle = [
      entry.upload_title && entry.file_name ? `файл: ${entry.file_name}` : "",
      requestTypeLabel(entry.template_type),
      entry.cost_center_code || "",
      entry.upload_comment ? `комментарий: ${entry.upload_comment}` : ""
    ].filter(Boolean).join(" · ");
    const actions = toolbar(
      `${actionButton("Детали", "", { attrs: `data-import-detail="${entry.id}"` })}${actionButton("Исходный файл", "", { attrs: `data-import-download="${entry.id}" data-file-name="${escapeHtml(entry.file_name || "import.xlsx")}"` })}`
    );
    return `<div class="item" data-import-id="${entry.id}"><strong>${escapeHtml(title)} · ${statusLabel(entry.status)}</strong><small>${escapeHtml(subtitle)}</small>${actions}</div>`;
  }).join("") || emptyState("Импортов пока нет", "После загрузки файлы появятся здесь с деталями и исходным Excel."));
}

async function openImportDetail(id: string, options: { updateUrl?: boolean; preserveFocus?: boolean } = {}) {
  if (options.updateUrl !== false) setRoute("upload", id);
  const detail = await api<any>(`/api/imports/${id}`);
  const batch = detail.batch || detail.import || detail;
  const summary = detail.summary || {};
  const validation = summary.validation_items || detail.validation_items || detail.validation || [];
  const actions = batch.actions || detail.actions || {};
  const result = detail.result || {};
  const currentValidation = new Map((state.validationItems || []).map((entry: any) => [String(entry.id), entry]));
  for (const entry of validation) currentValidation.set(String(entry.id), entry);
  state.validationItems = [...currentValidation.values()];
  const createdLines = summary.created_lines ?? result.created_lines ?? batch.rows_imported ?? batch.imported_rows ?? "не указано";
  const createdVersionId = summary.created_version_id ?? result.created_version_id ?? batch.version_id;
  const createdRequestId = summary.created_request_id ?? result.created_request_id ?? result.request_id ?? batch.request_id;
  const coverage = result.coverage || {};
  const coverageCounts = coverage.counts || {};
  const validationByCell = new Map<string, any>(validation.filter((entry: any) => entry.sheet && entry.cell).map((entry: any) => [`${entry.sheet}!${entry.cell}`.toUpperCase(), entry]));
  const coverageRows = Array.isArray(coverage.rows) ? coverage.rows : [];
  const coverageHtml = coverageRows
    .filter((row: any) => ["unmapped_nonzero", "mapped_imported", "ignored"].includes(String(row.status || "")))
    .slice(0, 20)
    .map((row: any) => {
      const key = `${row.sheet}!${row.cell}`.toUpperCase();
      const validationItem = validationByCell.get(key);
      const actionsHtml = validationItem?.status === "open"
        ? `<div class="actions"><button type="button" data-validation-map="${validationItem.id}">Импортировать строку</button><button type="button" data-validation-ignore="${validationItem.id}">Игнорировать</button></div>`
        : "";
      return `<div class="item">
        <strong>${escapeHtml(`${row.sheet || ""}!${row.cell || ""} · ${statusLabel(row.status || "")}`)}</strong>
        <small>${escapeHtml(row.label || "")} · ${money(row.amount || 0)}${row.article_code ? ` · ${escapeHtml(row.article_code)}` : ""}</small>
        ${actionsHtml}
      </div>`;
    })
    .join("");
  const coverageSummary = coverage.counts
    ? item("Coverage строк", `imported: ${coverageCounts.imported || 0} · unmapped: ${coverageCounts.unmapped_nonzero || 0} · mapped: ${coverageCounts.mapped_imported || 0} · ignored: ${coverageCounts.ignored || 0}`)
    : "";
  const rollbackComposer = actions.rollback?.ready === false
    ? ""
    : `<div class="danger-note" data-destructive-confirmation="rollback-import">Откат изменит импорт, версию и связанную заявку. Проверьте причину перед действием.</div><label class="rollback-reason">Причина отката<textarea data-import-rollback-reason="${id}" rows="2" placeholder="Например: загружен неверный файл">Ошибочная загрузка</textarea></label>`;
  openSidePanel(`<h2>Импорт #${escapeHtml(batch.id || id)}: ${escapeHtml(batch.upload_title || batch.file_name || "файл")}</h2><div class="progress-track">${statusChip(batch.status)}<span class="chip">${requestTypeLabel(batch.template_type)}</span><span class="chip">${escapeHtml(batch.cost_center_code || "")}</span></div><div class="list compact">${item("Название загрузки", batch.upload_title || "не задано")}${item("Комментарий", batch.upload_comment || "не задан")}${item("Исходный файл", batch.file_name || "не указан")}</div>${rollbackComposer}<div class="actions"><button type="button" data-import-download="${id}" data-file-name="${escapeHtml(batch.file_name || "import.xlsx")}">Исходный файл</button><button type="button" data-import-commit="${id}" ${actions.commit?.ready === false ? "disabled" : ""}>Отправить на проверку</button><button type="button" data-import-rollback="${id}" ${actions.rollback?.ready === false ? "disabled" : ""}>Откатить</button></div><h3>Проверки импорта</h3><div class="list">${validation.map((entry: any) => item(`${entry.code || "Проверка"} · ${statusLabel(entry.status)}`, `${escapeHtml(entry.message || "")} · ${escapeHtml(entry.sheet || "")}${entry.cell ? `!${escapeHtml(entry.cell)}` : ""} · ${escapeHtml(entry.resolution || "")}`)).join("") || item("Проверок по импорту нет")}</div><h3>Coverage строк</h3><div class="list">${coverageSummary}${coverageHtml || item("Coverage недоступен")}</div><h3>Результат</h3><div class="list">${item("Распознанных строк", createdLines)}${item("Версия", createdVersionId ? `#${createdVersionId}` : "не создана")}${item("Заявка", createdRequestId ? `#${createdRequestId}${summary.created_request_status ? ` · ${statusLabel(summary.created_request_status)}` : ""}` : "не создана")}</div>`, { preserveFocus: options.preserveFocus });
  state.activeImportDetailId = String(batch.id || id);
  state.activeRequestDetailId = null;
}

async function commitImport(id: string) {
  await api(`/api/imports/${id}/commit`, { method: "POST", body: JSON.stringify({}) });
  showToast("Импорт отправлен на проверку", "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("commit_import").join(",");
  await Promise.allSettled([openImportDetail(id), loadImports(), loadRequests(), loadTasks()]);
  scheduleLiveRefresh("commit_import");
}

async function rollbackImport(id: string) {
  const field = document.querySelector<HTMLTextAreaElement>(`[data-import-rollback-reason="${CSS.escape(id)}"]`);
  const reason = field ? field.value : window.prompt("Причина отката импорта", "Ошибочная загрузка") || "";
  if (!reason.trim()) {
    field?.focus();
    return showToast("Введите причину отката", "error");
  }
  await api(`/api/imports/${id}/rollback`, { method: "POST", body: JSON.stringify({ reason: reason.trim() }) });
  showToast("Импорт откатан", "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("rollback_import").join(",");
  await Promise.allSettled([openImportDetail(id), loadImports(), loadRequests(), loadTasks()]);
  scheduleLiveRefresh("rollback_import");
}

async function loadQueue() {
  const jobs = await api<any[]>("/api/queue");
  render("#queue", jobs.map(renderQueueJob).join("") || emptyState("Очередь пуста", "Новые импорты появятся здесь после постановки файла в обработку."));
  updateLiveRefreshFromQueue(jobs);
}

function renderQueueJob(job: any) {
  const cancel = job.actions?.cancel || {};
  const retry = job.actions?.retry || {};
  const reasons = [cancel.ready === false ? cancel.reason : "", retry.ready === false ? retry.reason : ""].filter(Boolean);
  const importLabel = job.import_id ? ` · импорт #${job.import_id}` : "";
  const cancelWarning = cancel.ready ? `<small class="danger-note inline" data-destructive-confirmation="queue-cancel">Отмена остановит задачу и связанный импорт.</small>` : "";
  return `<div class="item queue-job" data-queue-job="${job.id}"><strong>Задача ${job.id} · ${statusLabel(job.status)}</strong><small>${escapeHtml(job.message || "Обработка файла")}${importLabel} · перед ней: ${job.jobs_ahead ?? 0}</small>${cancelWarning}<div class="actions"><button type="button" data-queue-cancel="${job.id}" ${cancel.ready ? "" : "disabled"}>Отменить</button><button type="button" data-queue-retry="${job.id}" ${retry.ready ? "" : "disabled"}>Повторить</button></div>${reasons.length ? `<small class="muted">${escapeHtml([...new Set(reasons)].join(" · "))}</small>` : ""}</div>`;
}

function updateLiveRefreshFromQueue(jobs: any[]) {
  const active = hasActiveQueueJobs(jobs);
  root.dataset.activeQueueJobs = active ? "true" : "false";
  if (active) scheduleLiveRefresh("queue_active", LIVE_REFRESH_WINDOW_MS);
}

function shouldKeepLiveRefresh() {
  return Date.now() < liveRefreshUntil || root.dataset.activeQueueJobs === "true";
}

function scheduleLiveRefresh(reason: string, durationMs = LIVE_REFRESH_WINDOW_MS) {
  liveRefreshUntil = Math.max(liveRefreshUntil, Date.now() + durationMs);
  root.dataset.lastLiveRefreshReason = reason;
  if (liveRefreshTimer === null) {
    liveRefreshTimer = window.setInterval(() => {
      liveRefreshTick().catch((error) => showToast(error.message, "error"));
    }, LIVE_REFRESH_INTERVAL_MS);
  }
}

async function liveRefreshTick() {
  if (state.loginRequired || liveRefreshInFlight) return;
  liveRefreshInFlight = true;
  try {
    const sidePanelOpen = document.querySelector("#sidePanel")?.classList.contains("is-open");
    const refreshes: Promise<unknown>[] = [
      loadQueue(),
      loadImports(),
      loadDashboard(),
      loadTasks()
    ];
    if (state.workplace === "workflow") refreshes.push(loadRequests(), loadApprovalQueue(), loadValidationCenter());
    if (state.workplace === "reports") refreshes.push(loadLines(), loadSummaries(), loadCostCenterBudget());
    if (state.workplace === "budget") refreshes.push(loadBudgetWorkspace({ preserveSelection: true }));
    if (state.workplace === "sheets") refreshes.push(loadSheetCells());
    if (controlVersionValue()) refreshes.push(loadControlChecks());
    if (sidePanelOpen && state.activeImportDetailId) refreshes.push(openImportDetail(state.activeImportDetailId, { preserveFocus: true }));
    if (sidePanelOpen && state.activeRequestDetailId) refreshes.push(openRequestDetail(state.activeRequestDetailId, { updateUrl: false, preserveFocus: true }));
    await Promise.allSettled(refreshes);
  } finally {
    liveRefreshInFlight = false;
    if (!shouldKeepLiveRefresh() && liveRefreshTimer !== null) {
      window.clearInterval(liveRefreshTimer);
      liveRefreshTimer = null;
    }
  }
}

function requestQuery(offset = 0) {
  return toQuery({
    status: value("#requestStatus"),
    request_type: value("#requestType"),
    assigned_role: value("#requestAssignedRole"),
    q: value("#requestSearch"),
    limit: value("#requestLimit") || 25,
    offset
  });
}

async function loadRequests(offset = 0) {
  const payload = await api<any>(`/api/requests${requestQuery(offset)}`);
  const items: BudgetRequest[] = Array.isArray(payload) ? payload : payload.items || [];
  render("#requestListMeta", Array.isArray(payload) ? `Показано: ${items.length}` : `Показано: ${items.length} из ${payload.total ?? items.length}`);
  render("#requests", renderRequestTable(items, "Заявок пока нет"));
}

async function loadApprovalQueue() {
  const payload = await api<any>("/api/requests?status=pending_approval&assigned_role=approver&limit=10&offset=0");
  const items: BudgetRequest[] = payload.items || payload || [];
  render("#approvalMetrics", `<div class="metric"><strong>${items.length}</strong><small>Можно утвердить</small></div>`);
  render("#approvalQueue", renderRequestTable(items, "Очередь согласования пуста"));
}

function renderRequestItem(request: BudgetRequest) {
  const actions = (request.available_actions || []).map((action) => `<button type="button" data-transition="${action.action}" data-request="${request.id}" ${action.ready === false ? "disabled" : ""}>${escapeHtml(action.label)}</button>`).join("");
  return `<div class="item"><div class="request-card-head"><strong>Заявка #${request.id}: ${escapeHtml(request.title || "")}</strong>${statusChip(request.status)}</div><small>${request.cost_center_code || "общий"} · ${requestTypeLabel(request.request_type)} · назначено: ${roleLabel(request.assigned_role)}</small><div class="actions"><button type="button" data-request-detail="${request.id}">Карточка</button>${actions}</div>${blockedActionText(request)}</div>`;
}

function renderRequestTable(items: BudgetRequest[], emptyText: string) {
  if (!items.length) return item(emptyText);
  return `<div class="table-wrap table-scroll workflow-table-scroll"><table class="data-table workflow-request-table"><thead><tr><th>Заявка</th><th>Статус</th><th>Тип</th><th>КЦ</th><th>Назначено</th><th class="num">Сумма</th><th>Индикаторы</th><th>Обновлено</th><th>Инструменты</th></tr></thead><tbody>${items.map(renderRequestTableRow).join("")}</tbody></table></div>`;
}

function renderRequestTableRow(request: BudgetRequest) {
  const actions = (request.available_actions || [])
    .map((action) => {
      const label = action.label || action.action;
      const reason = action.reason ? `${label}: ${action.reason}` : label;
      const className = action.action === "archive" ? "is-danger" : "";
      return workflowToolButton(
        String(reason),
        workflowTransitionIcon(action.action),
        `data-transition="${escapeHtml(action.action)}" data-request="${escapeHtml(request.id)}"`,
        action.ready === false,
        className
      );
    })
    .join("");
  return `<tr data-request-row="${request.id}">
    <td><div class="workflow-request-title"><strong>#${request.id}</strong><span>${escapeHtml(request.title || "")}</span></div></td>
    <td>${statusChip(request.status)}</td>
    <td>${escapeHtml(requestTypeLabel(request.request_type))}</td>
    <td>${escapeHtml(request.cost_center_code || "общий")}</td>
    <td>${escapeHtml(roleLabel(request.assigned_role))}</td>
    <td class="num">${money(request.amount_total || 0)}</td>
    <td>${requestIndicators(request)}${blockedActionText(request)}</td>
    <td>${escapeHtml(dateLabel(request.updated_at))}</td>
    <td><div class="workflow-tools">${workflowToolButton("Открыть карточку заявки", "card", `data-request-detail="${escapeHtml(request.id)}"`)}${workflowToolButton("Открыть Excel-вид заявки", "sheet", `data-request-sheet="${escapeHtml(request.id)}"`)}${request.can_delete ? workflowToolButton("Удалить неутверждённую заявку", "delete", `data-request-delete="${escapeHtml(request.id)}"`, false, "is-danger") : ""}${actions}</div></td>
  </tr>`;
}

function requestIndicators(request: BudgetRequest) {
  const openControls = Number(request.open_control_count || 0);
  const blocked = Number(request.blocked_actions?.length || 0);
  const available = Number(request.available_actions?.length || 0);
  return `<div class="workflow-indicators">
    <span>${Number(request.line_count || 0)} строк</span>
    <span class="${openControls ? "is-warning" : "is-ok"}">${openControls ? `${openControls} проверок` : "проверки закрыты"}</span>
    ${blocked ? `<span class="is-danger">${blocked} блокера</span>` : ""}
    ${available ? `<span>${available} действий</span>` : ""}
  </div>`;
}

function blockedActionText(request: BudgetRequest) {
  return (request.blocked_actions || []).map((action) => `<small class="failed">${escapeHtml(action.label)} недоступно: ${escapeHtml(action.reason || "есть блокировки")}</small>`).join("");
}

function renderBudgetLineItem(line: any, request: any, version: any) {
  const editable = canEditBudgetLine(version);
  const payload = parseJsonObject(line.source_payload_json);
  const correctionCount = Array.isArray(payload.correction_history) ? payload.correction_history.length : 0;
  const meta = [
    lineTypeLabel(line.line_type),
    periodLabel(line.month),
    sourceTypeLabel(line.source_type),
    line.cost_center_code || "",
    correctionCount ? `корректировок: ${correctionCount}` : ""
  ].filter(Boolean).join(" · ");
  const actionLabel = editable ? "Редактировать" : "Открыть";
  const readonlyReason = editable ? "" : `<small class="muted">Версия ${statusLabel(version?.status)} защищена от прямой правки.</small>`;
  return html`
    <div class="item budget-line-item" data-edit-line="${line.id}" data-request="${request.id}">
      <div class="request-card-head">
        <strong>${escapeHtml(line.article_code)} · ${money(line.amount)}</strong>
        <button type="button" data-edit-line="${line.id}" data-request="${request.id}">${actionLabel}</button>
      </div>
      <small>${escapeHtml(meta)}</small>
      ${line.article_title ? `<small>${escapeHtml(line.article_title)}</small>` : ""}
      ${readonlyReason}
    </div>
  `;
}

async function openRequestDetail(id: string, options: { updateUrl?: boolean; preserveFocus?: boolean } = {}) {
  if (options.updateUrl !== false) setRoute("workflow", id);
  const detail = await api<any>(`/api/requests/${id}`);
  const request = detail.request || {};
  const version = detail.version || {};
  const commentRequiredActions = (request.available_actions || []).filter((action: any) => ["return", "request_correction", "archive"].includes(action.action));
  const transitionCommentComposer = commentRequiredActions.length
    ? `<div class="comment-composer"><label>Комментарий к workflow-действию<textarea data-transition-comment="${request.id}" rows="3" placeholder="Обязателен для возврата, корректировки или архивации"></textarea></label></div>`
    : "";
  const actions = (request.available_actions || []).map((action: any) => `<button type="button" class="${action.action === "archive" ? "danger" : ""}" data-transition="${action.action}" data-request="${request.id}" ${action.ready === false ? "disabled" : ""}>${escapeHtml(action.label || action.action)}</button>`).join("");
  const commentComposer = canCollaborateOnRequests()
    ? `<div class="comment-composer"><label>Новый комментарий<textarea data-request-comment-body="${request.id}" rows="3" placeholder="Напишите комментарий к заявке"></textarea></label><button type="button" data-request-comment="${request.id}">Добавить комментарий</button></div>`
    : "";
  const attachmentComposer = canCollaborateOnRequests()
    ? `<div class="attachment-composer"><label>Файл вложения<input type="file" data-request-attachment-file="${request.id}" /></label><button type="button" data-request-attachment="${request.id}">Добавить вложение</button></div>`
    : "";
  openSidePanel(`<h2>Заявка #${request.id}: ${escapeHtml(request.title)}</h2><div class="progress-track request-status-track">${statusChip(request.status)}<span class="chip">этап: ${statusLabel(request.current_step || request.status)}</span><span class="chip">версия: ${statusLabel(version.status)}</span></div>${transitionCommentComposer}<div class="actions"><button type="button" data-request-sheet="${request.id}">Excel-вид</button>${actions}</div>${blockedActionText(request)}<h3>Строки бюджета</h3><div class="list">${(detail.lines || []).map((line: any) => renderBudgetLineItem(line, request, version)).join("") || item("Строк нет")}</div><h3>Проверки и блокировки</h3><div class="list">${(detail.control_checks || []).map((check: any) => item(check.message, `${statusLabel(check.status)} · ${check.code}`)).join("") || item("Открытых проверок нет")}</div><h3>Комментарии</h3><div class="list">${(detail.comments || []).map((comment: any) => item(comment.body, `${comment.actor_label || comment.actor_role || ""} · ${comment.created_at || ""}`)).join("") || item("Комментариев нет")}</div>${commentComposer}<h3>Вложения</h3><div class="list">${(detail.attachments || []).map((attachment: any) => item(attachment.file_name, `${money(attachment.file_size)} байт · ${attachment.uploaded_by || ""}`, `data-attachment="${attachment.id}"`)).join("") || item("Вложений нет")}</div>${attachmentComposer}<div class="actions">${(detail.attachments || []).map((attachment: any) => `<button type="button" data-attachment-download="${attachment.id}" data-request="${id}" data-file-name="${escapeHtml(attachment.file_name || "attachment")}">Скачать ${escapeHtml(attachment.file_name || "файл")}</button>`).join("")}</div><h3>История</h3><div class="list">${(detail.events || []).map((event: any) => item(eventActionLabel(event.action || event.status), `${event.actor_label || event.actor_role || ""} · ${event.created_at || ""}`)).join("") || item("История пока пуста")}</div>`, { preserveFocus: options.preserveFocus });
  state.activeRequestDetailId = String(request.id || id);
  state.activeImportDetailId = null;
}

function renderCorrectionHistory(line: any, drilldown: any) {
  const payload = { ...parseJsonObject(line.source_payload_json), ...parseJsonObject(drilldown?.payload) };
  const history = Array.isArray(payload.correction_history) ? payload.correction_history : [];
  if (!history.length) return item("История корректировок пуста", "После сохранения изменения появятся здесь");
  return history.map((entry: any, index: number) => item(
    `${index + 1}. ${entry.reason || "Корректировка"}`,
    `${entry.actor || ""} · сумма: ${money(entry.before?.amount)} → ${money(entry.after?.amount)} · период: ${periodLabel(entry.before?.month)} → ${periodLabel(entry.after?.month)}`
  )).join("");
}

async function openBudgetLineEditor(lineId: string, requestId: string) {
  if (!lineId || !requestId) return;
  const [detail, drilldown] = await Promise.all([
    api<any>(`/api/requests/${requestId}`),
    api<any>(`/api/budget-lines/${lineId}/drilldown`).catch(() => null)
  ]);
  const request = detail.request || {};
  const version = detail.version || {};
  const line = (detail.lines || []).find((entry: any) => String(entry.id) === String(lineId));
  if (!line) {
    showToast("Строка не найдена в заявке", "error");
    return;
  }
  const editable = canEditBudgetLine(version);
  const revenuePayload = revenueDriverPayload(line);
  const readonlyNote = editable
    ? `<p class="drawer-lead">${revenuePayload ? "Измените биллинг, коэффициенты или месяц и обязательно укажите причину. Связанные доходные строки будут пересчитаны." : "Измените сумму или месяц и обязательно укажите причину. Она попадет в audit и историю корректировок."}</p>`
    : `<p class="danger-note">Эта версия сейчас ${escapeHtml(statusLabel(version.status))}. Прямая правка недоступна; создайте или откройте корректировочную заявку.</p>`;
  const editableFields = revenuePayload
    ? html`
      <label>Биллинг<input name="billing" type="number" step="0.01" value="${escapeHtml(revenuePayload.billing ?? line.amount)}" ${editable ? "" : "disabled"} /></label>
      <label>Рентабельность, %<input name="profitability" type="number" min="0" max="100" step="0.01" value="${escapeHtml(fractionToPercentLabel(revenuePayload.profitability))}" ${editable ? "" : "disabled"} /></label>
      <label>АК, %<input name="ak_percent" type="number" min="0" max="100" step="0.01" value="${escapeHtml(fractionToPercentLabel(revenuePayload.ak_percent))}" ${editable ? "" : "disabled"} /></label>
      <div class="form-field"><span>Месяц</span>${monthPickerControl("month", line.month || "", false, "", !editable)}</div>
    `
    : html`
      <label>Сумма<input name="amount" type="number" step="0.01" value="${escapeHtml(line.amount)}" ${editable ? "" : "disabled"} /></label>
      <div class="form-field"><span>Месяц</span>${monthPickerControl("month", line.month || "", false, "", !editable)}</div>
    `;
  const revenueMetrics = revenuePayload
    ? html`
      <div class="metric"><strong>${money(revenuePayload.income)}</strong><small>Доход</small></div>
      <div class="metric"><strong>${money(revenuePayload.suppliers)}</strong><small>Поставщики</small></div>
      <div class="metric"><strong>${escapeHtml(percentageLabel(revenuePayload.profitability))}</strong><small>Рентабельность</small></div>
    `
    : "";
  openSidePanel(html`
    <h2>Строка бюджета #${escapeHtml(line.id)}</h2>
    <div class="progress-track">${statusChip(request.status)}<span class="chip">${escapeHtml(line.article_code)}</span><span class="chip">${escapeHtml(sourceTypeLabel(line.source_type))}</span></div>
    ${readonlyNote}
    <div class="line-editor-summary">
      <div class="metric"><strong>${money(line.amount)}</strong><small>Текущая сумма</small></div>
      <div class="metric"><strong>${escapeHtml(periodLabel(line.month))}</strong><small>Период</small></div>
      <div class="metric"><strong>${escapeHtml(line.cost_center_code || "общий")}</strong><small>Кост-центр</small></div>
      ${revenueMetrics}
    </div>
    <form class="line-editor-form" data-line-editor="${escapeHtml(line.id)}" data-request="${escapeHtml(request.id)}">
      <label>Статья<input value="${escapeHtml(line.article_title ? `${line.article_code} · ${line.article_title}` : line.article_code)}" disabled /></label>
      <label>Источник<input value="${escapeHtml(sourceTypeLabel(line.source_type))}" disabled /></label>
      ${editableFields}
      <label class="wide">Причина корректировки<textarea name="reason" rows="4" placeholder="Например: уточнен прогноз клиента, исправлен период размещения" ${editable ? "required" : "disabled"}></textarea></label>
      <div class="actions wide">
        <button type="button" data-request-detail="${escapeHtml(request.id)}">Назад к заявке</button>
        <button type="button" class="primary" data-save-line="${escapeHtml(line.id)}" data-request="${escapeHtml(request.id)}" ${editable ? "" : "disabled"}>Сохранить корректировку</button>
      </div>
    </form>
    <h3>История корректировок</h3>
    <div class="list">${renderCorrectionHistory(line, drilldown)}</div>
  `);
}

async function saveBudgetLine(lineId: string, requestId: string) {
  const form = document.querySelector<HTMLFormElement>(`[data-line-editor="${CSS.escape(lineId)}"]`);
  if (!form) return showToast("Форма корректировки не найдена", "error");
  const amount = form.querySelector<HTMLInputElement>('[name="amount"]')?.value;
  const billing = form.querySelector<HTMLInputElement>('[name="billing"]')?.value;
  const profitability = form.querySelector<HTMLInputElement>('[name="profitability"]')?.value;
  const akPercent = form.querySelector<HTMLInputElement>('[name="ak_percent"]')?.value;
  const month = form.querySelector<HTMLInputElement>('[name="month"]')?.value;
  const reason = form.querySelector<HTMLTextAreaElement>('[name="reason"]')?.value.trim() || "";
  if (!reason) {
    form.querySelector<HTMLTextAreaElement>('[name="reason"]')?.focus();
    return showToast("Укажите причину корректировки", "error");
  }
  const payload: Record<string, string> = { month: month || "", reason };
  if (billing !== undefined) payload.billing = billing;
  if (profitability !== undefined) payload.profitability = String(percentToFraction(profitability, "Рентабельность"));
  if (akPercent !== undefined) payload.ak_percent = String(percentToFraction(akPercent, "АК"));
  if (amount !== undefined) payload.amount = amount;
  await api(`/api/budget-lines/${lineId}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
  showToast("Строка бюджета обновлена", "success");
  await Promise.allSettled([loadRequests(), loadApprovalQueue(), loadTasks(), loadDashboard(), loadLines(), loadSummaries(), loadCostCenterBudget()]);
  await openRequestDetail(requestId);
  scheduleLiveRefresh("budget_line_update", 30000);
}

async function transitionRequest(requestId: string, action: string) {
  const commentField = document.querySelector<HTMLTextAreaElement>(`[data-transition-comment="${CSS.escape(requestId)}"]`);
  const comment = ["return", "request_correction", "archive"].includes(action) ? (commentField ? commentField.value : window.prompt("Комментарий к действию") || "") : "";
  if (["return", "request_correction", "archive"].includes(action) && !comment.trim()) {
    commentField?.focus();
    showToast("Для этого действия нужен комментарий", "error");
    return;
  }
  await api(`/api/requests/${requestId}/transition`, { method: "POST", body: JSON.stringify({ action, comment }) });
  showToast("Статус заявки обновлен", "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("request_transition").join(",");
  await Promise.allSettled([loadRequests(), loadApprovalQueue(), loadTasks(), loadDashboard(), openRequestDetail(requestId, { updateUrl: false })]);
  scheduleLiveRefresh("request_transition");
}

async function deleteRequest(requestId: string) {
  if (!window.confirm("Удалить неутверждённую заявку вместе с её черновыми строками? Это действие нельзя отменить.")) return;
  await api(`/api/requests/${requestId}`, { method: "DELETE" });
  closeSidePanel();
  showToast("Неутверждённая заявка удалена", "success");
  await Promise.allSettled([loadRequests(), loadApprovalQueue(), loadTasks(), loadDashboard(), loadLines(), loadSummaries(), loadCostCenterBudget()]);
  scheduleLiveRefresh("request_deleted");
}

async function addRequestComment(requestId: string) {
  const field = document.querySelector<HTMLTextAreaElement>(`[data-request-comment-body="${CSS.escape(requestId)}"]`);
  const body = field ? field.value : window.prompt("Комментарий к заявке");
  if (!body?.trim()) {
    field?.focus();
    return showToast("Введите комментарий", "error");
  }
  await api(`/api/requests/${requestId}/comments`, { method: "POST", body: JSON.stringify({ body: body.trim(), comment_type: "comment" }) });
  if (field) field.value = "";
  showToast("Комментарий добавлен", "success");
  await openRequestDetail(requestId);
}

async function uploadRequestAttachment(requestId: string) {
  const field = document.querySelector<HTMLInputElement>(`[data-request-attachment-file="${CSS.escape(requestId)}"]`);
  const file = field?.files?.[0] || await pickFile();
  if (!file) {
    field?.focus();
    return showToast("Выберите файл вложения", "error");
  }
  const form = new FormData();
  form.set("file", file);
  await api(`/api/requests/${requestId}/attachments`, { method: "POST", body: form });
  if (field) field.value = "";
  showToast("Вложение добавлено", "success");
  await openRequestDetail(requestId);
}

function pickFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.addEventListener("change", () => resolve(input.files?.[0] || null), { once: true });
    input.click();
  });
}

async function queueAction(jobId: string, action: "cancel" | "retry") {
  await api(`/api/queue/${jobId}/${action}`, { method: "POST", body: JSON.stringify({}) });
  showToast(action === "cancel" ? "Задача отменена" : "Задача поставлена на повтор", "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("queue_action").join(",");
  await Promise.allSettled([loadQueue(), loadImports(), loadDashboard()]);
  scheduleLiveRefresh("queue_action");
}

async function loadValidationCenter(panel = activeWorkflowSection()) {
  const fieldValue = (selector: string) => panel?.querySelector<HTMLInputElement | HTMLSelectElement>(selector)?.value || "";
  const query = toQuery({
    status: fieldValue("#validationStatus"),
    severity: fieldValue("#validationSeverity"),
    template_type: fieldValue("#validationTemplate"),
    import_id: fieldValue("#validationImportId"),
    q: fieldValue("#validationSearch")
  });
  const items = await api<any[]>(`/api/import-validation${query}`);
  state.validationItems = items;
  const visibleOpenIds = new Set(items.filter((entry) => entry.status === "open").map((entry) => String(entry.id)));
  state.selectedValidationIds = (state.selectedValidationIds || []).filter((id) => visibleOpenIds.has(id));
  const host = panel?.querySelector<HTMLElement>("#validation");
  if (host) host.innerHTML = items.map(renderValidationItem).join("") || item("Открытых проверок по импорту нет");
  bindValidationDetailButtons(panel);
  bindValidationSelection(panel);
}

function renderValidationItem(entry: any) {
  const open = entry.status === "open";
  const selected = open && (state.selectedValidationIds || []).includes(String(entry.id));
  const canMap = open && entry.actions?.create_mapping?.ready !== false && entry.import_id;
  const canIgnore = open && entry.actions?.ignore?.ready !== false;
  return `<div class="item validation-item" data-validation-item="${entry.id}">
    <label><input type="checkbox" data-validation-select="${entry.id}" ${selected ? "checked" : ""} ${open ? "" : "disabled"} /> ${severityBadge(entry.severity)} ${escapeHtml(entry.code || "")}</label>
    <small>${escapeHtml(entry.message || "")} · ${escapeHtml(entry.file_name || "")}</small>
    <small>${escapeHtml(entry.sheet || "")}${entry.cell ? `!${escapeHtml(entry.cell)}` : ""} · ${statusLabel(entry.status)}</small>
    <div class="actions">
      <button type="button" data-validation-detail="${entry.id}">Детали</button>
      <button type="button" data-resolve="${entry.id}" ${entry.actions?.resolve?.ready === false ? "disabled" : ""}>Закрыть</button>
      <button type="button" data-validation-map="${entry.id}" ${canMap ? "" : "disabled"}>Импортировать строку</button>
      <button type="button" data-validation-ignore="${entry.id}" ${canIgnore ? "" : "disabled"}>Игнорировать</button>
    </div>
  </div>`;
}

function openValidationDetail(id: string) {
  const entry = (state.validationItems || []).find((item) => String(item.id) === String(id));
  if (!entry) return showToast("Проверка не найдена в текущей выборке", "error");
  openSidePanel(`<h2>Проверка #${escapeHtml(entry.id)}</h2>
    <div class="progress-track">${severityBadge(entry.severity)}<span class="chip">${escapeHtml(entry.code || "без кода")}</span><span class="chip">${statusLabel(entry.status)}</span></div>
    <div class="list">
      ${item("Сообщение", entry.message || "нет описания")}
      ${item("Файл", entry.file_name || "не указан")}
      ${item("Импорт", entry.import_id ? `#${entry.import_id}` : "не указан")}
      ${item("Шаблон", requestTypeLabel(entry.template_type))}
      ${item("Ячейка", `${entry.sheet || ""}${entry.cell ? `!${entry.cell}` : ""}` || "не указана")}
    </div>
    <label class="comment-composer">Решение<textarea data-validation-resolution="${entry.id}" rows="4">${escapeHtml(entry.resolution || "Проверено финансовым контролером")}</textarea></label>
    <div class="actions">
      <button type="button" data-resolve="${entry.id}" ${entry.actions?.resolve?.ready === false ? "disabled" : ""}>Закрыть проверку</button>
      <button type="button" data-validation-map="${entry.id}" ${entry.actions?.create_mapping?.ready === false ? "disabled" : ""}>Импортировать строку</button>
      <button type="button" data-validation-ignore="${entry.id}" ${entry.actions?.ignore?.ready === false ? "disabled" : ""}>Игнорировать</button>
      <button type="button" data-admin-tool-open="mapping">Открыть сопоставления</button>
    </div>`);
}

function bindValidationDetailButtons(panel = activeWorkflowSection()) {
  panel?.querySelectorAll<HTMLButtonElement>("[data-validation-detail]").forEach((button) => {
    button.addEventListener("click", () => openValidationDetail(button.dataset.validationDetail || ""));
  });
}

function bindValidationSelection(panel = activeWorkflowSection()) {
  panel?.querySelectorAll<HTMLInputElement>("[data-validation-select]").forEach((input) => {
    input.addEventListener("change", () => {
      const selected = new Set(state.selectedValidationIds || []);
      const id = input.dataset.validationSelect || "";
      if (!id) return;
      if (input.checked) selected.add(id);
      else selected.delete(id);
      state.selectedValidationIds = [...selected];
    });
  });
}

async function resolveValidationItem(id: string) {
  const field = document.querySelector<HTMLTextAreaElement>(`[data-validation-resolution="${CSS.escape(id)}"]`);
  const resolution = field ? field.value : window.prompt("Решение по проверке", "Проверено финансовым контролером") || "";
  if (!resolution.trim()) {
    field?.focus();
    return showToast("Введите решение по проверке", "error");
  }
  await api(`/api/import-validation/${id}/resolve`, { method: "POST", body: JSON.stringify({ resolution: resolution.trim() }) });
  showToast("Проверка закрыта", "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("validation_resolve").join(",");
  await loadValidationCenter();
  scheduleLiveRefresh("validation_resolve", 30000);
}

async function ignoreValidationItem(id: string) {
  const entry = (state.validationItems || []).find((item) => String(item.id) === String(id));
  const defaultResolution = entry?.sheet && entry?.cell
    ? `Строка ${entry.sheet}!${entry.cell} осознанно не импортируется`
    : "Строка осознанно не импортируется";
  const resolution = window.prompt("Причина игнорирования", defaultResolution) || "";
  if (!resolution.trim()) return showToast("Введите причину игнорирования", "error");
  await api(`/api/import-validation/${id}/resolve`, {
    method: "POST",
    body: JSON.stringify({ resolution: resolution.trim(), resolution_action: "ignore" })
  });
  showToast("Проверка проигнорирована", "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("validation_ignore").join(",");
  await Promise.allSettled([loadValidationCenter(), entry?.import_id ? openImportDetail(String(entry.import_id)) : Promise.resolve()]);
  scheduleLiveRefresh("validation_ignore", 30000);
}

async function createMappingFromValidationItem(id: string) {
  const entry = (state.validationItems || []).find((item) => String(item.id) === String(id));
  if (!entry?.import_id) return showToast("У проверки нет связанного импорта", "error");
  const targetArticle = window.prompt("Системная статья", entry.template_type === "summary_c" ? "C.3.1.3" : "A.10.1") || "";
  if (!targetArticle.trim()) return showToast("Укажите системную статью", "error");
  const targetCostCenter = window.prompt("Целевой кост-центр", "MICE") || "";
  const amountSign = window.prompt("Знак суммы: 1 или -1", "1") || "1";
  const payload = {
    validation_id: Number(id),
    mapping_rule: {
      target_article_code: targetArticle.trim(),
      target_cost_center_code: targetCostCenter.trim(),
      amount_sign: Number(amountSign) === -1 ? -1 : 1,
      description: `Из проверки импорта #${entry.import_id}${entry.sheet && entry.cell ? ` ${entry.sheet}!${entry.cell}` : ""}`
    }
  };
  await api(`/api/imports/${entry.import_id}/mapping`, { method: "POST", body: JSON.stringify(payload) });
  showToast("Строка импортирована по правилу сопоставления", "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("validation_mapping").join(",");
  await Promise.allSettled([loadValidationCenter(), openImportDetail(String(entry.import_id)), loadSummaries()]);
  scheduleLiveRefresh("validation_mapping");
}

async function bulkResolveValidation(panel = activeWorkflowSection()) {
  if (!panel) return;
  const ids = [...panel.querySelectorAll<HTMLInputElement>("[data-validation-select]:checked")].map((input) => input.dataset.validationSelect).filter(Boolean);
  if (!ids.length) return showToast("Выберите проверки", "error");
  const field = panel.querySelector<HTMLTextAreaElement>("#bulkValidationResolution");
  const resolution = field ? field.value : window.prompt("Решение для выбранных проверок", "Массово проверено финансовым контролером") || "";
  if (!resolution.trim()) {
    field?.focus();
    return showToast("Введите решение для выбранных проверок", "error");
  }
  await api("/api/import-validation/bulk-resolve", { method: "POST", body: JSON.stringify({ ids, resolution: resolution.trim() }) });
  showToast("Выбранные проверки закрыты", "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("bulk_validation_resolve").join(",");
  await loadValidationCenter(panel);
  scheduleLiveRefresh("bulk_validation_resolve", 30000);
}

async function resetUserData() {
  const expected = "ОЧИСТИТЬ ДАННЫЕ";
  const confirmationInput = document.querySelector<HTMLInputElement>("#adminResetConfirmation");
  const confirmation = confirmationInput?.value.trim() || "";
  if (confirmation !== expected) {
    confirmationInput?.focus();
    render("#adminResult", `<strong>Очистка не выполнена</strong><p>Введите точную фразу ${escapeHtml(expected)} в поле подтверждения.</p>`);
    showToast("Очистка отменена: подтверждение не совпало", "error");
    return;
  }
  const button = document.querySelector<HTMLButtonElement>("#adminResetUserData");
  if (button) button.disabled = true;
  render("#adminResult", "<strong>Очистка выполняется…</strong>");
  let result: any;
  try {
    result = await api<any>("/api/admin/reset-user-data", { method: "POST", body: JSON.stringify({ confirmation }) });
  } catch (error) {
    render("#adminResult", `<strong>Очистка не выполнена</strong><p>${escapeHtml(error instanceof Error ? error.message : String(error))}</p>`);
    throw error;
  } finally {
    if (button) button.disabled = false;
  }
  const deleted = result.deleted || {};
  render("#adminResult", `<strong>Пользовательские данные очищены</strong><pre>${escapeHtml(JSON.stringify({ deleted, removed_files: result.removed_files || 0, preserved: result.reference_tables_preserved || [] }, null, 2))}</pre>`);
  showToast("Заявки, бюджеты и сопоставления очищены", "success");
  if (confirmationInput) confirmationInput.value = "";
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("admin_reset_user_data").join(",");
  await Promise.allSettled([loadDictionaries(), loadDashboard(), loadRequests(), loadImports(), loadQueue(), loadLines(), loadValidationCenter(), loadAudit(), loadCampaigns(), loadVersions()]);
}

async function loadVersions() {
  const versions = await api<any[]>("/api/versions");
  const options = `<option value="">Активная версия кампании</option>` + versions.map((version) => `<option value="${version.id}">#${version.id} · ${statusLabel(version.status)} · ${version.cost_center_code || "общий"}</option>`).join("");
  for (const selector of ["#baseVersion", "#compareVersion", "#controlVersion", "#reportVersion", "#exportVersion"]) {
    const select = document.querySelector<HTMLSelectElement>(selector);
    if (!select) continue;
    const selected = select.value;
    select.innerHTML = options;
    if (selected && Array.from(select.options).some((option) => option.value === selected)) select.value = selected;
  }
}

async function loadLines() {
  const lines = await api<any[]>(`/api/budget-lines${state.workplace === "reports" ? reportQuery({ report_scope: "1" }) : ""}`);
  text("#reportLinesMeta", `Показано строк: ${lines.length}`);
  render("#lines", lines.map((line) => `<tr data-report-line-row="${line.id}"><td>${line.id}</td><td><strong>${escapeHtml(line.article_code)}</strong></td><td>${escapeHtml(line.block)}</td><td>${escapeHtml(lineTypeLabel(line.line_type))}</td><td>${escapeHtml(periodLabel(line.month))}</td><td class="num">${money(line.amount)}</td><td>${escapeHtml(sourceTypeLabel(line.source_type))}</td><td><button type="button" data-drill="${line.id}" title="Открыть детализацию суммы">Детали</button></td></tr>`).join(""));
}

function reportParams(extra: Record<string, string> = {}) {
  const prefix = state.workplace === "exports" ? "export" : "report";
  const field = (name: string) => value(`#${prefix}${name}`);
  const planFact = document.querySelector<HTMLInputElement>(`#${prefix}PlanFact`)?.checked ? "1" : "";
  return {
    year: field("Year"),
    campaign_id: field("Campaign"),
    cost_center_code: field("CostCenter"),
    version_id: field("Version"),
    status: field("Status"),
    line_type: field("LineType"),
    plan_fact: planFact,
    block: field("Block"),
    article_code: field("Article"),
    month: field("Month"),
    ...extra
  };
}

function planFactReportEnabled() {
  const prefix = state.workplace === "exports" ? "export" : "report";
  return Boolean(document.querySelector<HTMLInputElement>(`#${prefix}PlanFact`)?.checked);
}

function reportQuery(extra: Record<string, string> = {}) {
  return toQuery(reportParams(extra));
}

// A single report refresh renders both the consolidated and cost-center
// surfaces.  They share the same consolidated scope, so coalesce the in-flight
// read instead of issuing an identical request for each surface.
const consolidatedReportReads = new Map<string, Promise<any>>();

function loadConsolidatedReport() {
  const query = reportQuery();
  const pending = consolidatedReportReads.get(query);
  if (pending) return pending;
  const request = api<any>(`/api/reports/consolidated-budget${query}`);
  consolidatedReportReads.set(query, request);
  request.then(
    () => { if (consolidatedReportReads.get(query) === request) consolidatedReportReads.delete(query); },
    () => { if (consolidatedReportReads.get(query) === request) consolidatedReportReads.delete(query); }
  );
  return request;
}

async function loadSummaries() {
  renderReportLoading("#summaryA", "#summaryAMeta", "Свод A загружается");
  renderReportLoading("#summaryC", "#summaryCMeta", "Свод C загружается");
  const [summaryA, summaryC, consolidated] = await Promise.allSettled([
    api<any[]>(`/api/reports/summary-a${reportQuery()}`),
    api<any[]>(`/api/reports/summary-c${reportQuery()}`),
    loadConsolidatedReport()
  ]);
  const scope = consolidated.status === "fulfilled" ? consolidated.value.scope : null;
  if (consolidated.status === "fulfilled") {
    render("#consolidatedKpis", renderConsolidatedKpis(consolidated.value));
    render("#consolidatedRows", (consolidated.value.rows || []).map((row: any) => reportDrillItem(row, `${row.cost_center_code || ""} · ${reportAmountSubtitle(row, `год: ${money(row.year_amount || row.amount)}`)}`)).join("") || reportEmptyState("Консолидированный бюджет", scope));
  } else {
    render("#consolidatedKpis", "");
    render("#consolidatedRows", errorState(errorMessage(consolidated.reason)));
  }
  renderReportResult("#summaryA", "#summaryAMeta", "Свод A", summaryA, scope);
  renderReportResult("#summaryC", "#summaryCMeta", "Свод C", summaryC, scope);
}

function renderConsolidatedKpis(consolidated: any) {
  if (planFactReportEnabled() && consolidated.plan_kpis && consolidated.fact_kpis) {
    return Object.keys(consolidated.plan_kpis).map((key) => `<div class="metric"><strong>${money(consolidated.difference_kpis?.[key] || 0)}</strong><small>${escapeHtml(reportKpiLabel(key))} · план ${money(consolidated.plan_kpis[key])} · факт ${money(consolidated.fact_kpis[key])}</small></div>`).join("");
  }
  return Object.entries(consolidated.kpis || {}).map(([key, value]) => `<div class="metric"><strong>${money(value)}</strong><small>${escapeHtml(reportKpiLabel(key))}</small></div>`).join("");
}

function renderSummary(selector: string, rows: any[]) {
  render(selector, rows.map((row) => reportDrillItem(row, `${reportAmountSubtitle(row, `итого: ${money(row.amount || row.year_amount)}`)} · строк: ${row.source_rows || 0}`)).join(""));
}

function renderReportResult(selector: string, metaSelector: string, title: string, result: PromiseSettledResult<any[]>, scope: any = null) {
  if (result.status === "rejected") {
    text(metaSelector, "Ошибка загрузки");
    render(selector, errorState(errorMessage(result.reason)));
    return;
  }
  const rows = result.value || [];
  text(metaSelector, rowCountLabel(rows.length));
  if (!rows.length) {
    render(selector, reportEmptyState(title, scope));
    return;
  }
  renderSummary(selector, rows);
}

function renderReportLoading(selector: string, metaSelector: string, label: string) {
  text(metaSelector, "Загрузка");
  render(selector, loadingState(label));
}

function reportEmptyState(title: string, scope: any = null) {
  const filters = reportFilterLabel();
  const scopeLabel = reportScopeLabel(scope);
  const subtitle = [scopeLabel, filters].filter(Boolean).join(" · ");
  return emptyState(`${title}: данных нет`, subtitle || "В текущем контуре отчета нет строк бюджета.");
}

function reportScopeLabel(scope: any = null) {
  if (!scope) return "Скоуп отчета не определен";
  if (scope.explicit_version_id) return `версия #${scope.explicit_version_id}`;
  if (scope.uses_active_version) {
    const versions = (scope.active_version_ids || []).join(", ");
    return versions ? `активные утвержденные версии: ${versions}` : "активная утвержденная версия";
  }
  return `кампания #${scope.campaign_id || "?"}`;
}

function reportFilterLabel() {
  const params = reportParams();
  const labels = [
    params.campaign_id ? `кампания #${params.campaign_id}` : "",
    params.version_id ? `версия #${params.version_id}` : "",
    params.cost_center_code ? `КЦ ${params.cost_center_code}` : "",
    params.block ? `блок ${params.block}` : "",
    params.article_code ? `статья ${params.article_code}` : "",
    params.month ? monthLabel(params.month) : "",
    params.line_type && params.line_type !== "all" ? lineTypeLabel(params.line_type) : "",
    params.status ? statusLabel(params.status) : "",
  ].filter(Boolean);
  return labels.length ? `фильтры: ${labels.join(", ")}` : "";
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error || "Не удалось загрузить данные");
}

function rowCountLabel(count: number) {
  const abs = Math.abs(count);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  const word = mod10 === 1 && mod100 !== 11 ? "строка" : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? "строки" : "строк";
  return `${count} ${word}`;
}

function reportAmountSubtitle(row: any, fallback: string) {
  if (row.plan_amount === undefined && row.fact_amount === undefined) return fallback;
  return `план: ${money(row.plan_amount)} · факт: ${money(row.fact_amount)} · разница: ${money(row.difference_amount)}`;
}

function reportDrillItem(row: any, subtitle: string) {
  return `<div class="item report-drill-item" data-report-drill="${escapeHtml(row.article_code || "")}" data-article="${escapeHtml(row.article_code || "")}" data-block="${escapeHtml(row.block || "")}" data-cost-center-code="${escapeHtml(row.cost_center_code || "")}"><strong>${escapeHtml(`${row.article_code || ""} · ${row.article_title || ""}`)}</strong><small>${escapeHtml(subtitle)}</small>${actionButton("Детали", "", { attrs: 'data-report-drill-action="true"' })}</div>`;
}

async function loadCostCenterBudget() {
  renderReportLoading("#costCenterBudget", "#costCenterBudgetMeta", "Бюджет кост-центра загружается");
  const [rowsResult, scopeResult] = await Promise.allSettled([
    api<any[]>(`/api/reports/cost-center-budget${reportQuery()}`),
    loadConsolidatedReport()
  ]);
  const scope = scopeResult.status === "fulfilled" ? scopeResult.value.scope : null;
  if (rowsResult.status === "rejected") {
    text("#costCenterBudgetMeta", "Ошибка загрузки");
    render("#costCenterBudget", errorState(errorMessage(rowsResult.reason)));
    return;
  }
  const rows = rowsResult.value || [];
  text("#costCenterBudgetMeta", rowCountLabel(rows.length));
  render("#costCenterBudget", rows.map((row) => item(`${row.article_code || ""} · ${row.article_title || ""}`, `${row.cost_center_code || ""} · ${reportAmountSubtitle(row, `год: ${money(row.year_amount || row.amount)}`)} · Q1: ${money(row.q1 || row.quarters?.Q1)} · отклонение ${money(row.variance || 0)}`)).join("") || reportEmptyState("Бюджет кост-центра", scope));
}

function budgetCampaignId() {
  return value("#budgetCampaign") || state.budgetCampaignId || "";
}

function budgetScopeMode(): "selection" | "effective" {
  const mode = value("#budgetScopeMode") || state.budgetScopeMode || "selection";
  return mode === "effective" ? "effective" : "selection";
}

function budgetPreviewPayload(campaignId: string, profileId: string, tabId: string) {
  const effective = budgetScopeMode() === "effective";
  return {
    ...budgetTemplateRequestPayload(campaignId, effective ? [] : (state.budgetIncludedRequestIds || []), profileId, tabId),
    scope_mode: budgetScopeMode(),
  };
}

function syncBudgetScopeControls() {
  const effective = budgetScopeMode() === "effective";
  const modeSelect = document.querySelector<HTMLSelectElement>("#budgetScopeMode");
  if (modeSelect) modeSelect.value = effective ? "effective" : "selection";
  for (const selector of ["#budgetSelectionTitle", "#budgetSavedSelection", "#saveBudgetSelection"]) {
    const control = document.querySelector<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>(selector);
    if (control) control.disabled = effective;
  }
  const scopeNote = document.querySelector<HTMLElement>("#budgetScopeModeNote");
  if (scopeNote) scopeNote.dataset.budgetScopeMode = effective ? "effective" : "selection";
  text("#budgetScopeModeNote", effective
    ? "Показаны effective-версии текущего утверждённого бюджета. Ручной выбор и сохранение сборки отключены."
    : "Показана рабочая сборка по выбранным заявкам. Официальный бюджет используется только для сравнения.");
}

function budgetIncludedIdsFromPreview(preview: any) {
  return (preview?.requests || []).filter((request: any) => request.included).map((request: any) => String(request.id));
}

function budgetTemplateCatalogProfiles() {
  const catalog = Array.isArray(state.budgetTemplateCatalog) ? state.budgetTemplateCatalog : [];
  return catalog
    .filter((item: any) => item && item.available !== false)
    .map((item: any) => {
      const known = getBudgetTemplateProfile(item.id);
      const sheets = Array.isArray(item.sheets) && item.sheets.length ? item.sheets : [];
      return {
        ...known,
        id: item.id || known.id,
        label: item.title || item.label || known.label,
        workbook: item.source_workbook || known.workbook,
        description: item.sha256 ? `sha256 ${String(item.sha256).slice(0, 12)}` : known.description,
        defaultTab: sheets[0]?.name || known.defaultTab,
        tabs: sheets.length
          ? sheets.map((sheet: any) => {
              const knownTab = known.tabs.find((tab: any) => tab.sheet === sheet.name || tab.id === sheet.name) || known.tabs[0] || {};
              return {
                id: knownTab.id || sheet.name,
                label: sheet.name || knownTab.label,
                sheet: sheet.name || knownTab.sheet,
                range: sheet.range || knownTab.range,
                freeze: sheet.freeze || knownTab.freeze,
              };
            })
          : known.tabs,
      };
    });
}

function renderBudgetTemplateCatalog() {
  const profiles = budgetTemplateCatalogProfiles();
  const catalogLoaded = Array.isArray(state.budgetTemplateCatalog);
  const fallback = profiles.length ? profiles : catalogLoaded ? [] : BUDGET_TEMPLATE_PROFILES;
  const select = document.querySelector<HTMLSelectElement>("#budgetTemplateProfile");
  if (!select) return;
  if (!fallback.length) {
    select.innerHTML = `<option value="">Нет доступных шаблонов</option>`;
    select.disabled = true;
    text("#budgetTemplateProfileNote", "API не вернул доступных шаблонов итогового бюджета");
    return;
  }
  select.disabled = false;
  const previous = select.value || state.budgetTemplateProfileId || "";
  select.innerHTML = fallback.map((profile: any) => `<option value="${escapeHtml(profile.id)}">${escapeHtml(profile.label)}</option>`).join("");
  const next = fallback.some((profile: any) => profile.id === previous) ? previous : fallback[0]?.id || "new_form";
  select.value = next;
  state.budgetTemplateProfileId = next;
}

async function loadBudgetTemplates() {
  const payload = await api<any>("/api/budget-workspace/templates");
  state.budgetTemplateCatalog = payload.items || [];
  renderBudgetTemplateCatalog();
}

function activeBudgetTemplateState() {
  renderBudgetTemplateCatalog();
  const select = document.querySelector<HTMLSelectElement>("#budgetTemplateProfile");
  const offeredIds = new Set(Array.from(select?.options || []).map((option) => option.value));
  const candidate = value("#budgetTemplateProfile") || state.budgetTemplateProfileId || "new_form";
  const selectedProfile = offeredIds.size && !offeredIds.has(candidate) ? Array.from(offeredIds)[0] : candidate;
  const selected = coerceBudgetTemplateState(selectedProfile, state.budgetTemplateTabId || "");
  state.budgetTemplateProfileId = selected.profileId;
  state.budgetTemplateTabId = selected.tabId;
  localStorage.setItem("budgetTemplateProfile", selected.profileId);
  localStorage.setItem("budgetTemplateTab", selected.tabId);
  if (select) select.value = selected.profileId;
  return selected;
}

function renderBudgetTemplateProfileNote() {
  const { profileId, tabId } = activeBudgetTemplateState();
  const profile = getBudgetTemplateProfile(profileId);
  const tab = getBudgetTemplateTab(profileId, tabId);
  const catalogItem = (state.budgetTemplateCatalog || []).find((item: any) => item.id === profileId);
  const workbook = catalogItem?.source_workbook || profile.workbook;
  const hash = catalogItem?.sha256 ? ` · ${String(catalogItem.sha256).slice(0, 12)}` : "";
  const note = document.querySelector<HTMLElement>("#budgetTemplateProfileNote");
  if (note) {
    note.dataset.budgetTemplateProfile = profileId;
    note.dataset.budgetTemplateTab = tabId;
    note.dataset.budgetTemplateRange = tab.range;
  }
  text("#budgetTemplateProfileNote", `${workbook} · лист ${tab.sheet} · ${tab.range}${hash} · ${profile.description}`);
}

async function loadBudgetWorkspace(options: { preserveSelection?: boolean } = {}) {
  if (!canOpenWorkplace("budget")) return;
  const [, campaignPayload] = await Promise.all([
    loadBudgetTemplates(),
    api<any>("/api/budget-workspace/campaigns"),
  ]);
  const budgetCampaigns = Array.isArray(campaignPayload?.items) ? campaignPayload.items as Campaign[] : [];
  state.budgetCampaigns = budgetCampaigns;
  const selectedCampaignId = renderBudgetCampaignOptions(budgetCampaigns, campaignPayload?.active_campaign?.id ? String(campaignPayload.active_campaign.id) : null);
  activeBudgetTemplateState();
  const campaignId = selectedCampaignId || budgetCampaignId();
  if (!campaignId) {
    state.budgetCampaignId = null;
    state.budgetIncludedRequestIds = undefined;
    render("#budgetRequests", emptyState("Бюджетные кампании недоступны", "Нет кампаний в доступном контуре."));
    render("#budgetTemplate", "");
    render("#budgetKpis", "");
    render("#budgetWarnings", "");
    render("#budgetFacetTotals", "");
    return;
  }
  const query = campaignId ? `?campaign_id=${encodeURIComponent(campaignId)}` : "";
  const requestPayload = await api<any>(`/api/budget-workspace/requests${query}`);
  const campaign = requestPayload.campaign || {};
  const requests: BudgetRequest[] = requestPayload.items || [];
  syncBudgetScopeControls();
  state.budgetCampaignId = String(campaign.id || campaignId || "");
  const select = document.querySelector<HTMLSelectElement>("#budgetCampaign");
  if (select && state.budgetCampaignId) select.value = state.budgetCampaignId;
  if (budgetScopeMode() === "selection" && (!options.preserveSelection || !state.budgetIncludedRequestIds)) {
    state.budgetIncludedRequestIds = requests.filter((request) => request.default_included !== false).map((request) => String(request.id));
  }
  await loadBudgetPreview(requests);
  await loadBudgetSavedSelections();
}

async function loadBudgetPreview(knownRequests?: BudgetRequest[]) {
  const campaignId = budgetCampaignId();
  renderBudgetTemplateProfileNote();
  if (!campaignId) {
    render("#budgetRequests", emptyState("Выберите бюджетную кампанию", "После выбора появятся заявки для сборки."));
    render("#budgetTemplate", "");
    render("#budgetKpis", "");
    return;
  }
  const { profileId, tabId } = activeBudgetTemplateState();
  const preview = await api<any>("/api/budget-workspace/preview", {
    method: "POST",
    body: JSON.stringify(budgetPreviewPayload(campaignId, profileId, tabId))
  });
  state.budgetPreview = preview;
  if (preview?.scope?.scope_mode !== "effective") {
    state.budgetIncludedRequestIds = budgetIncludedIdsFromPreview(preview);
  }
  renderBudgetPreview(preview, knownRequests);
}

function renderBudgetPreview(preview: any, knownRequests?: BudgetRequest[]) {
  const requests = (preview.requests || knownRequests || []) as BudgetRequest[];
  const { profileId, tabId } = activeBudgetTemplateState();
  const profile = getBudgetTemplateProfile(profileId);
  const tab = getBudgetTemplateTab(profileId, tabId);
  const templateRows = rowsForBudgetTemplateTab(preview, profileId, tabId);
  const effective = preview?.scope?.scope_mode === "effective";
  text("#budgetRequestsMeta", effective
    ? `Утверждённых заявок: ${requests.length} · effective-версий: ${(preview.scope?.active_version_ids || []).length}`
    : `Заявок: ${requests.length} · включено: ${requests.filter((request: any) => request.included).length}`);
  text("#budgetTemplateMeta", `${profile.label} · ${tab.sheet} · строк: ${templateRows.length}`);
  renderBudgetTemplateProfileNote();
  render("#budgetKpis", renderBudgetKpis(preview));
  render("#budgetRequests", renderBudgetRequestTable(requests, effective));
  render("#budgetTemplate", renderBudgetTemplateRows(templateRows, effective));
  render("#budgetWarnings", renderBudgetWarnings(preview.warnings || []));
  render("#budgetFacetTotals", renderBudgetFacetTotals(preview.totals || {}));
}

async function loadBudgetSavedSelections() {
  const campaignId = budgetCampaignId();
  if (!campaignId) {
    renderBudgetSavedSelections([]);
    return;
  }
  const payload = await api<any>(`/api/budget-workspace/selections${toQuery({ campaign_id: campaignId })}`);
  state.budgetSavedSelections = payload.items || [];
  renderBudgetSavedSelections(state.budgetSavedSelections || []);
}

function renderBudgetSavedSelections(selections: any[]) {
  const select = document.querySelector<HTMLSelectElement>("#budgetSavedSelection");
  if (!select) return;
  const selectedId = String(state.budgetSavedSelectionId || "");
  select.innerHTML = `<option value="">Новая сборка</option>${(selections || []).map((selection: any) => `<option value="${escapeHtml(selection.id)}"${String(selection.id) === selectedId ? " selected" : ""}>#${escapeHtml(selection.id)} · ${escapeHtml(selection.title || "Сборка")} · ${escapeHtml(selection.template_id || "new_form")}</option>`).join("")}`;
  text("#budgetSavedSelectionNote", selections.length ? `Сохраненных сборок: ${selections.length}` : "Сохраненных сборок пока нет");
}

function renderBudgetKpis(preview: any) {
  const totals = preview.totals || {};
  const kpis = totals.kpis || {};
  return [
    [preview?.scope?.scope_mode === "effective" ? "Итого активного бюджета" : "Итого сборки", totals.grand_total],
    ["Официальный бюджет", totals.official_total],
    ["Дельта", totals.delta_from_official],
    ["EBITDA", kpis.ebitda],
    ["Чистая прибыль", kpis.net_profit],
  ].map(([label, value]) => `<div class="metric"><strong>${money(value)}</strong><small>${escapeHtml(label)}</small></div>`).join("");
}

function renderBudgetRequestTable(requests: BudgetRequest[], effective = false) {
  if (!requests.length) return emptyState(effective ? "Активный бюджет пока не утверждён" : "В кампании нет заявок", effective ? "После утверждения effective-версий они появятся здесь." : "Поданные бюджеты появятся здесь после загрузки или ручной заявки.");
  return `<div class="table-wrap table-scroll budget-request-scroll"><table class="data-table budget-request-table"><thead><tr><th>${effective ? "В бюджете" : "Вкл."}</th><th>Заявка</th><th>Статус</th><th>Тип</th><th>КЦ</th><th class="num">Сумма</th><th>Индикаторы</th><th>Источник</th><th>Инструменты</th></tr></thead><tbody>${requests.map((request) => renderBudgetRequestRow(request, effective)).join("")}</tbody></table></div>`;
}

function renderBudgetRequestRow(request: BudgetRequest, effective = false) {
  const included = Boolean((request as any).included);
  const source = [request.upload_title, request.source_template ? requestTypeLabel(request.source_template) : "", request.source_file].filter(Boolean).join(" · ");
  const indicators = [
    `${Number(request.line_count || 0)} строк`,
    Number(request.open_control_count || 0) ? `${request.open_control_count} проверок` : "проверки закрыты",
    Number(request.open_import_warning_count || 0) ? `${request.open_import_warning_count} имп. замечаний` : ""
  ].filter(Boolean).join(" · ");
  return `<tr data-budget-request-row="${request.id}" class="${included ? "is-included" : "is-excluded"}">
    <td>${effective ? `<span class="status-chip status-approved">Да</span>` : `<label class="toggle-cell" title="${included ? "Исключить из сборки" : "Включить в сборку"}"><input type="checkbox" data-budget-request-toggle="${request.id}" ${included ? "checked" : ""} /><span>${included ? "Да" : "Нет"}</span></label>`}</td>
    <td><div class="workflow-request-title"><strong>#${request.id}</strong><span>${escapeHtml(request.title || "")}</span></div></td>
    <td>${statusChip(request.status)}</td>
    <td>${escapeHtml(requestTypeLabel(request.request_type))}</td>
    <td>${escapeHtml(request.cost_center_code || "общий")}</td>
    <td class="num">${money(request.amount_total || 0)}</td>
    <td><div class="workflow-indicators"><span>${escapeHtml(indicators)}</span></div></td>
    <td>${escapeHtml(source || "ручной ввод")}</td>
    <td><div class="workflow-tools">${workflowToolButton("Открыть карточку заявки", "card", `data-request-detail="${escapeHtml(request.id)}"`)}${workflowToolButton("Открыть Excel-вид заявки", "sheet", `data-request-sheet="${escapeHtml(request.id)}"`)}${effective ? "" : workflowToolButton(included ? "Исключить из сборки" : "Включить в сборку", included ? "deactivate" : "approve", `data-budget-request-toggle-button="${escapeHtml(request.id)}"`)}</div></td>
  </tr>`;
}

function renderBudgetTemplateRows(rows: any[], effective = false) {
  if (!rows.length) return emptyState("Шаблонная матрица пуста", effective ? "У активного утверждённого бюджета пока нет строк." : "Включите хотя бы одну заявку с бюджетными строками.");
  const amountLabel = effective ? "Сумма активного бюджета" : "Сумма preview";
  return `<div class="table-wrap table-scroll budget-template-scroll"><table class="data-table budget-template-table"><thead><tr><th>Строка</th><th>Показатель шаблона</th><th>Связь</th><th class="num">${amountLabel}</th></tr></thead><tbody>${rows.map((row) => `<tr><td>${escapeHtml(row.row)}</td><td><strong>${escapeHtml(row.label)}</strong></td><td>${escapeHtml(row.article_codes?.length ? row.article_codes.join(", ") : row.formula || `блок ${row.block || ""}`)}</td><td class="num">${money(row.amount)}</td></tr>`).join("")}</tbody></table></div>`;
}

function renderBudgetWarnings(warnings: any[]) {
  if (!warnings.length) return "";
  return `<div class="list compact">${warnings.map((warning) => item(warning.code || "Предупреждение", warning.message || "")).join("")}</div>`;
}

function renderBudgetFacetTotals(totals: any) {
  const block = (totals.by_block || []).map((row: any) => item(`Блок ${row.block}`, `год: ${money(row.amount)} · Q1: ${money((row.months?.["1"] || 0) + (row.months?.["2"] || 0) + (row.months?.["3"] || 0))}`)).join("") || item("Блоки", "нет данных");
  const centers = (totals.by_cost_center || []).map((row: any) => item(row.cost_center_code || "общий", money(row.amount))).join("") || item("Кост-центры", "нет данных");
  const months = (totals.by_month || []).map((row: any) => item(monthLabel(row.month), money(row.amount))).join("") || item("Месяцы", "нет данных");
  return `<div class="list compact">${block}</div><div class="list compact">${centers}</div><div class="list compact">${months}</div>`;
}

async function toggleBudgetRequest(requestId: string, checked?: boolean) {
  if (budgetScopeMode() === "effective") return showToast("Состав активного утверждённого бюджета меняется через утверждение версий", "error");
  const ids = new Set<string>(state.budgetIncludedRequestIds || budgetIncludedIdsFromPreview(state.budgetPreview));
  const nextChecked = checked ?? !ids.has(String(requestId));
  if (nextChecked) ids.add(String(requestId));
  else ids.delete(String(requestId));
  state.budgetIncludedRequestIds = [...ids];
  await loadBudgetPreview();
}

async function saveBudgetSelection() {
  if (budgetScopeMode() === "effective") return showToast("Активный утверждённый бюджет нельзя сохранить как ручную сборку", "error");
  const campaignId = budgetCampaignId();
  if (!campaignId) return showToast("Выберите бюджетную кампанию", "error");
  const { profileId, tabId } = activeBudgetTemplateState();
  const fallbackTitle = `Сборка ${state.budgetPreview?.campaign?.scenario || campaignId}`;
  const title = value("#budgetSelectionTitle") || fallbackTitle;
  const result = await api<any>("/api/budget-workspace/selections", {
    method: "POST",
    body: JSON.stringify({
      ...budgetTemplateRequestPayload(campaignId, state.budgetIncludedRequestIds || [], profileId, tabId),
      title: title.trim(),
      filters: state.budgetPreview?.filters || {},
      totals: state.budgetPreview?.totals || {},
    })
  });
  showToast(`Сборка сохранена #${result.selection?.id || ""}`, "success");
  await loadBudgetSavedSelections();
}

function tabIdForSavedSheet(templateId: string, sheet: string) {
  const profile = getBudgetTemplateProfile(templateId);
  return profile.tabs.find((tab: any) => tab.sheet === sheet || tab.id === sheet)?.id || profile.defaultTab;
}

async function restoreBudgetSelection(selectionId: string) {
  if (!selectionId) return;
  if (budgetScopeMode() === "effective") {
    return showToast("Сохранённую ручную сборку нельзя восстановить в режиме активного бюджета", "error");
  }
  const payload = await api<any>(`/api/budget-workspace/selections/${encodeURIComponent(selectionId)}`);
  const selection = payload.selection || {};
  state.budgetSavedSelectionId = String(selection.id || selectionId || "") || null;
  const includedIds = (payload.items || []).filter((item: any) => Number(item.included) === 1).map((item: any) => String(item.request_id));
  state.budgetCampaignId = String(selection.campaign_id || budgetCampaignId() || "");
  state.budgetIncludedRequestIds = includedIds;
  state.budgetTemplateProfileId = selection.template_id || selection.template?.id || "new_form";
  state.budgetTemplateTabId = tabIdForSavedSheet(state.budgetTemplateProfileId || "new_form", selection.template_sheet || selection.template?.sheet || "");
  const campaignSelect = document.querySelector<HTMLSelectElement>("#budgetCampaign");
  if (campaignSelect && state.budgetCampaignId) campaignSelect.value = state.budgetCampaignId;
  const titleInput = document.querySelector<HTMLInputElement>("#budgetSelectionTitle");
  if (titleInput) titleInput.value = selection.title || titleInput.value;
  renderBudgetTemplateCatalog();
  await loadBudgetWorkspace({ preserveSelection: true });
  showToast(`Сборка #${selection.id} восстановлена`, "success");
}

async function exportBudgetPreview() {
  const campaignId = budgetCampaignId();
  if (!campaignId) return showToast("Выберите бюджетную кампанию", "error");
  const { profileId, tabId } = activeBudgetTemplateState();
  const query = {
    campaign_id: campaignId,
    scope_mode: budgetScopeMode(),
    ...(budgetScopeMode() === "selection" ? { included_request_ids: (state.budgetIncludedRequestIds || []).join(",") } : {}),
    ...budgetTemplateExportQuery(profileId, tabId)
  };
  await trackedDownload(`/api/budget-workspace/export.xlsx${toQuery(query)}`, "budget-workspace.xlsx");
}

async function openBudgetTemplateView() {
  const campaignId = budgetCampaignId();
  if (!campaignId) return showToast("Выберите бюджетную кампанию", "error");
  const { profileId, tabId } = activeBudgetTemplateState();
  const rawData = await api<any>("/api/budget-workspace/template-view", {
    method: "POST",
    body: JSON.stringify(budgetPreviewPayload(campaignId, profileId, tabId))
  });
  const data = adaptBudgetTemplateViewData(rawData, state.budgetPreview, profileId, tabId);
  state.budgetTemplateViewData = rawData;
  openSidePanel(html`
    <h2>Итоговый бюджет: вид шаблона</h2>
    <p id="budgetTemplateModalMeta" class="drawer-lead">${escapeHtml(budgetTemplateModalMeta(data))}</p>
    <div id="budgetTemplateModalTabs" class="sheet-tabs budget-template-tabs" aria-label="Листы итогового бюджета"></div>
    <div class="budget-template-modal" data-budget-template-view="${escapeHtml(profileId)}" data-budget-template-active-tab="${escapeHtml(tabId)}">
      <div id="budgetTemplateGridHost" class="sheet-grid-host budget-template-grid-host" role="grid" aria-label="Итоговый бюджет в верстке шаблона"></div>
    </div>
  `, { variant: "fullscreen" });
  renderBudgetTemplateGrid(data);
  renderBudgetTemplateModalTabs(data);
}

function budgetTemplateModalMeta(data: any) {
  return `${data.source_workbook || "Новая форма_Бюджет 2026.xlsx"} · ${data.sheet || "Новая форма"} · ${data.range || "A1:R81"} · ${money(data.totals?.grand_total || state.budgetPreview?.totals?.grand_total || 0)}`;
}

function renderBudgetTemplateModalTabs(data: any) {
  const active = data.tab_id || state.budgetTemplateTabId || "";
  const tabs = data.tabs || [];
  render("#budgetTemplateModalTabs", `<div class="budget-template-tab-list" role="tablist" aria-label="Листы шаблона">${tabs.map((tab: any) => `<button type="button" role="tab" data-budget-template-tab="${escapeHtml(tab.id)}" aria-selected="${tab.id === active ? "true" : "false"}" class="${tab.id === active ? "is-active" : ""}">${escapeHtml(tab.label || tab.sheet || tab.id)}</button>`).join("")}</div><label class="budget-template-sheet-select"><span>Лист</span><select id="budgetTemplateModalSheet">${tabs.map((tab: any) => `<option value="${escapeHtml(tab.id)}" ${tab.id === active ? "selected" : ""}>${escapeHtml(tab.label || tab.sheet || tab.id)}</option>`).join("")}</select></label>`);
  document.querySelector<HTMLSelectElement>("#budgetTemplateModalSheet")?.addEventListener("change", (event) => {
    switchBudgetTemplateModalTab((event.currentTarget as HTMLSelectElement).value);
  });
}

function switchBudgetTemplateModalTab(tabId: string) {
  if (!tabId) return;
  const profileId = state.budgetTemplateProfileId || "new_form";
  const selected = coerceBudgetTemplateState(profileId, tabId);
  state.budgetTemplateTabId = selected.tabId;
  localStorage.setItem("budgetTemplateTab", selected.tabId);
  const rawData = state.budgetTemplateViewData || {};
  const data = adaptBudgetTemplateViewData(rawData, state.budgetPreview, selected.profileId, selected.tabId);
  const host = document.querySelector<HTMLElement>("[data-budget-template-view]");
  if (host) host.dataset.budgetTemplateActiveTab = selected.tabId;
  text("#budgetTemplateModalMeta", budgetTemplateModalMeta(data));
  renderBudgetTemplateModalTabs(data);
  renderBudgetTemplateGrid(data);
}

function cssDeclarations(style: Record<string, unknown> = {}) {
  const declarations: string[] = [];
  const allowed: Record<string, string> = {
    background: "background",
    color: "color",
    fontWeight: "font-weight",
    textAlign: "text-align",
    alignItems: "align-items",
    whiteSpace: "white-space",
  };
  for (const [key, cssName] of Object.entries(allowed)) {
    const value = style[key];
    if (value !== undefined && value !== null && String(value).trim()) declarations.push(`${cssName}:${escapeHtml(String(value))}`);
  }
  return declarations.join(";");
}

function renderBudgetTemplateGrid(data: any) {
  const range = data.range || "A1:R81";
  const grid = rangeToGrid(range);
  const viewId = data.profile_id || data.active_template?.id || "budget_template_final";
  const tabId = data.tab_id || data.active_sheet || data.sheet || "main";
  const persisted = readSheetDimensions(viewId, tabId);
  const dimensions = {
    columns: { ...(data.dimensions?.columns || {}), ...(persisted.columns || {}) },
    rows: { ...(data.dimensions?.rows || {}), ...(persisted.rows || {}) },
  };
  const byAddress = new Map((data.cells || []).map((cell: any) => [cell.address, cell]));
  const coveredByMerge = new Set<string>();
  for (const cell of data.cells || []) {
    const colSpan = Number(cell.merge?.colspan || 1);
    const rowSpan = Number(cell.merge?.rowspan || 1);
    if (colSpan <= 1 && rowSpan <= 1) continue;
    for (let row = Number(cell.row || 0); row < Number(cell.row || 0) + rowSpan; row += 1) {
      for (let column = Number(cell.column || 0); column < Number(cell.column || 0) + colSpan; column += 1) {
        const address = columnLabel(column) + row;
        if (address !== cell.address) coveredByMerge.add(address);
      }
    }
  }
  const columnHeaders = grid.columns.map((column) => `<div class="sheet-axis sheet-col-axis" style="grid-column:${column - grid.start.column + 2};grid-row:1;"><span>${escapeHtml(compactCellDisplay(columnLabel(column)))}</span>${sheetResizeHandle("column", column, viewId, tabId)}</div>`).join("");
  const rowHeaders = grid.rows.map((row) => `<div class="sheet-axis sheet-row-axis" style="grid-column:1;grid-row:${row - grid.start.row + 2};"><span>${row}</span>${sheetResizeHandle("row", row, viewId, tabId)}</div>`).join("");
  const body = (data.cells || []).map((cell: any) => {
    const colSpan = Number(cell.merge?.colspan || 1);
    const rowSpan = Number(cell.merge?.rowspan || 1);
    const gridColumn = `${cell.column - grid.start.column + 2} / span ${colSpan}`;
    const gridRow = `${cell.row - grid.start.row + 2} / span ${rowSpan}`;
    const templateStyle = cell.template_style || {};
    const classes = ["sheet-cell", "budget-template-cell", `cell-${cell.kind || "empty"}`, templateStyle.budgetRow ? "budget-template-highlight" : "", ...(cell.markers || [])].filter(Boolean);
    const inline = `grid-column:${gridColumn};grid-row:${gridRow};${cssDeclarations(templateStyle)}`;
    const display = compactCellDisplay(cell.display ?? cell.value ?? "", 42);
    return `<button type="button" class="${classes.join(" ")}" style="${inline}" data-budget-template-cell="${escapeHtml(cell.address)}" title="${escapeHtml(cell.address)} ${escapeHtml(cell.display || "")}" role="gridcell"><span>${escapeHtml(display)}</span></button>`;
  }).join("");
  const missing = grid.rows.map((row) => grid.columns.map((column) => {
    const address = columnLabel(column) + row;
    if (byAddress.has(address) || coveredByMerge.has(address)) return "";
    return `<button type="button" class="sheet-cell budget-template-cell cell-empty" style="grid-column:${column - grid.start.column + 2};grid-row:${row - grid.start.row + 2};" data-budget-template-cell="${address}" role="gridcell"><span></span></button>`;
  }).join("")).join("");
  render("#budgetTemplateGridHost", `<div class="sheet-grid budget-template-grid" data-sheet-range="${escapeHtml(range)}" style="grid-template-columns: var(--sheet-row-header-w) ${sheetTrackTemplate(grid.columns, dimensions.columns, SHEET_DEFAULT_COL_WIDTH, SHEET_MIN_COL_WIDTH, SHEET_MAX_COL_WIDTH)}; grid-template-rows: var(--sheet-row-h) ${sheetTrackTemplate(grid.rows, dimensions.rows, SHEET_DEFAULT_ROW_HEIGHT, SHEET_MIN_ROW_HEIGHT, SHEET_MAX_ROW_HEIGHT)};">${columnHeaders}${rowHeaders}${body}${missing}</div>`);
}

async function openDrilldown(id: string) {
  const data = await api<any>(`/api/budget-lines/${id}/drilldown`);
  const payload = parseJsonObject(data.payload);
  const context = [
    payload.client ? `клиент: ${payload.client}${payload.client_code ? ` (${payload.client_code})` : ""}` : "",
    payload.media ? `медиа: ${payload.media}${payload.media_cost_center_code ? ` → КЦ ${payload.media_cost_center_code}` : ""}` : "",
    payload.contract_type ? `договор: ${payload.contract_type}` : "",
    payload.normalization_status ? `нормализация: ${JSON.stringify(payload.normalization_status)}` : "",
    payload.cost_center_match_status ? `КЦ match: ${payload.cost_center_match_status}` : "",
  ].filter(Boolean).join(" · ");
  openSidePanel(`<h2>Детализация суммы</h2><p class="drawer-lead">Показываем источник выбранной строки бюджета: файл, лист, строку, связанную заявку, версию и бизнес-контекст нормализации.</p><div class="list">${item(`${data.line?.article_code || ""} · ${money(data.line?.amount)}`, `${lineTypeLabel(data.line?.line_type)} · ${periodLabel(data.line?.month)} · ${data.line?.cost_center_code || "без КЦ"} · ${escapeHtml(sourceTypeLabel(data.line?.source_type))}`)}${item("Источник", `файл: ${payload.file_name || data.line?.source_file || ""} · лист: ${payload.source_sheet || data.line?.source_sheet || ""} · строка: ${payload.source_row || data.line?.source_row || ""} · ячейка: ${payload.source_cell || data.line?.source_cell || ""}`)}${context ? item("Бизнес-контекст", context) : ""}${item("Версия", data.version ? `#${data.version.id} · ${statusLabel(data.version.status)} · ${sourceTypeLabel(data.version.source_type)}` : "нет")}${item("Заявка", data.request ? `#${data.request.id} · ${statusLabel(data.request.status)} · ${requestTypeLabel(data.request.request_type)}` : "нет")}${item("История корректировок", correctionHistoryRows(data))}</div>`);
}

function correctionHistoryRows(data: any) {
  return (data.corrections || []).map((entry: any) => `#${entry.id}`).join(", ") || "нет";
}

async function openReportDrilldown(article: string, block: string, costCenterCode: string, offset = 0) {
  // Summary A/C rows are aggregates and therefore have no row-level cost
  // centre.  Do not let that empty data attribute overwrite the cost centre
  // selected in the visible report filters; a non-empty KPI row still narrows
  // the drilldown to its own exact centre.
  const costCenterScope: Record<string, string> = costCenterCode ? { cost_center_code: costCenterCode } : {};
  const data = await api<any>(`/api/reports/drilldown${reportQuery({ article_code: article, block, ...costCenterScope, limit: "25", offset: String(offset) })}`);
  openSidePanel(`<h2>Детализация отчета</h2><p class="drawer-lead">Разбираем выбранную статью по строкам-источникам. Сначала смотрите итог и количество строк, затем проверяйте конкретные суммы и кост-центры.</p><div class="report-drill-summary">${reportDrilldownMetrics(data)}<div class="metric"><strong>${money(data.source_rows || 0)}</strong><small>Строк источника</small></div><div class="metric"><strong id="reportDrilldownShown">${(data.lines || []).length}</strong><small>Показано</small></div></div><div id="reportDrilldownLines" class="list">${(data.lines || []).map(renderReportDrilldownLine).join("") || item("Строк для выбранной детализации нет")}</div><div id="reportDrilldownMore" class="actions">${data.has_more ? `<button type="button" data-report-drill-more="${data.next_offset}" data-article="${escapeHtml(article)}" data-block="${escapeHtml(block)}" data-cost-center-code="${escapeHtml(costCenterCode)}">Показать еще</button>` : ""}</div>`);
}

function reportDrilldownMetrics(data: any) {
  if (data.plan_total !== null && data.plan_total !== undefined) {
    return `<div class="metric"><strong>${money(data.plan_total)}</strong><small>План</small></div><div class="metric"><strong>${money(data.fact_total)}</strong><small>Факт</small></div><div class="metric"><strong>${money(data.difference_total)}</strong><small>Разница</small></div>`;
  }
  return `<div class="metric"><strong>${money(data.total)}</strong><small>Итого по выборке</small></div>`;
}

async function appendReportDrilldownMore(article: string, block: string, costCenterCode: string, offset: string) {
  const costCenterScope: Record<string, string> = costCenterCode ? { cost_center_code: costCenterCode } : {};
  const data = await api<any>(`/api/reports/drilldown${reportQuery({ article_code: article, block, ...costCenterScope, limit: "25", offset })}`);
  const lines = document.querySelector("#reportDrilldownLines");
  if (lines) lines.insertAdjacentHTML("beforeend", (data.lines || []).map(renderReportDrilldownLine).join(""));
  const shown = document.querySelector("#reportDrilldownShown");
  if (shown) shown.textContent = String(Number(shown.textContent || 0) + (data.lines || []).length);
  render("#reportDrilldownMore", data.has_more ? `<button data-report-drill-more="${data.next_offset}" data-article="${escapeHtml(article)}" data-block="${escapeHtml(block)}" data-cost-center-code="${escapeHtml(costCenterCode)}">Показать еще</button>` : "");
}

function renderReportDrilldownLine(line: any) {
  return item(`${line.article_code} · ${money(line.amount)}`, `${lineTypeLabel(line.line_type)} · ${periodLabel(line.month)} · ${line.cost_center_code || "без КЦ"} · ${sourceTypeLabel(line.source_type) || "источник не указан"}`, `data-report-drill-line-id="${escapeHtml(line.id || "")}"`);
}

function sheetViewForSourceType(sourceType?: string | null) {
  const source = String(sourceType || "");
  if (source === "business_variant_1") return "business_source_variant_1";
  if (source === "business_variant_2") return "business_source_variant_2";
  if (source === "summary_c") return "summary_c_filled";
  if (source === "explanation_note") return "explanation_allocation_basis";
  if (source === "manual_request" || source === "revenue_request" || source === "compiled_budget" || source === "legacy_excel_import") return "cost_center_budget_filled";
  return "cost_center_budget_filled";
}

async function loadSheetViews() {
  if (!canOpenWorkplace("sheets")) return;
  const [views, requests] = await Promise.all([getSheetViews(), api<BudgetRequest[]>("/api/requests")]);
  state.sheetViews = views;
  render("#sheetViewSelect", (state.sheetViews || []).map((view) => `<option value="${escapeHtml(view.id)}">${escapeHtml(view.title)}</option>`).join(""));
  const versions = new Map<string, BudgetRequest>();
  for (const request of requests || []) {
    if (request.version_id) versions.set(String(request.version_id), request);
  }
  const versionSelect = document.querySelector<HTMLSelectElement>("#sheetVersionSelect");
  if (versionSelect) {
    const selected = state.sheetVersionId || "";
    const options = [...versions.entries()]
      .sort(([left], [right]) => Number(right) - Number(left))
      .map(([versionId, request]) => `<option value="${escapeHtml(versionId)}">#${escapeHtml(versionId)} · заявка #${escapeHtml(request.id)} · ${escapeHtml(request.title || "без названия")}</option>`)
      .join("");
    versionSelect.innerHTML = `<option value="">Все версии</option>${options}`;
    state.sheetVersionId = versions.has(selected) ? selected : "";
    versionSelect.value = state.sheetVersionId || "";
  }
  const requested = state.pendingSheetViewId;
  const first = requested && state.sheetViews?.some((view) => view.id === requested) ? requested : state.sheetViews?.[0]?.id;
  if (first) await selectSheetView(first);
  else render("#sheetGridHost", emptyState("Нет доступных Excel-видов", "Для текущей роли backend не вернул доступных таблиц."));
}

async function selectSheetView(viewId: string) {
  if (!viewId) return;
  state.activeSheetView = await getSheetView(viewId);
  const select = document.querySelector<HTMLSelectElement>("#sheetViewSelect");
  if (select) select.value = viewId;
  render("#sheetTabSelect", (state.activeSheetView.tabs || []).map((tab) => `<option value="${escapeHtml(tab.id)}">${escapeHtml(tab.label)}</option>`).join(""));
  const requestedTab = state.pendingSheetTab && state.activeSheetView.tabs?.some((tab) => tab.id === state.pendingSheetTab) ? state.pendingSheetTab : null;
  state.activeSheetTab = requestedTab || state.activeSheetView.default_tab || state.activeSheetView.tabs?.[0]?.id || null;
  state.pendingSheetTab = null;
  state.sheetRange = activeSheetTab()?.default_range || activeSheetTab()?.used_range || "A1:W90";
  const tabSelect = document.querySelector<HTMLSelectElement>("#sheetTabSelect");
  if (tabSelect && state.activeSheetTab) tabSelect.value = state.activeSheetTab;
  const rangeInput = document.querySelector<HTMLInputElement>("#sheetRangeInput");
  if (rangeInput && state.sheetRange) rangeInput.value = state.sheetRange;
  await loadSheetCells();
}

function activeSheetTab(): SheetTab | undefined {
  return state.activeSheetView?.tabs?.find((tab) => tab.id === state.activeSheetTab);
}

function activeSheetVersionId() {
  return state.sheetVersionId || value("#sheetVersionSelect") || "";
}

type SheetDimensionState = {
  columns?: Record<string, number>;
  rows?: Record<string, number>;
};

function sheetDimensionKey(viewId: string, tabId: string, versionId = "") {
  void versionId;
  return `budget.sheet.dimensions.${viewId}.${tabId}`;
}

function readSheetDimensions(viewId: string, tabId: string, versionId = ""): SheetDimensionState {
  try {
    const parsed = JSON.parse(localStorage.getItem(sheetDimensionKey(viewId, tabId, versionId)) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeSheetDimension(viewId: string, tabId: string, axis: "column" | "row", index: number, size: number, versionId = "") {
  const state = readSheetDimensions(viewId, tabId, versionId);
  const key = axis === "column" ? "columns" : "rows";
  const min = axis === "column" ? SHEET_MIN_COL_WIDTH : SHEET_MIN_ROW_HEIGHT;
  const max = axis === "column" ? SHEET_MAX_COL_WIDTH : SHEET_MAX_ROW_HEIGHT;
  state[key] = { ...(state[key] || {}), [index]: clampSheetSize(size, min, max) };
  localStorage.setItem(sheetDimensionKey(viewId, tabId, versionId), JSON.stringify(state));
}

function sheetGridStyle(grid: any, dimensions: SheetDimensionState) {
  const columns = sheetTrackTemplate(grid.columns, dimensions.columns || {}, SHEET_DEFAULT_COL_WIDTH, SHEET_MIN_COL_WIDTH, SHEET_MAX_COL_WIDTH);
  const rows = sheetTrackTemplate(grid.rows, dimensions.rows || {}, SHEET_DEFAULT_ROW_HEIGHT, SHEET_MIN_ROW_HEIGHT, SHEET_MAX_ROW_HEIGHT);
  return `grid-template-columns: var(--sheet-row-header-w) ${columns}; grid-template-rows: var(--sheet-row-h) ${rows};`;
}

function sheetResizeHandle(axis: "column" | "row", index: number, viewId: string, tabId: string, versionId = "") {
  const label = axis === "column" ? `Изменить ширину колонки ${columnLabel(index)}` : `Изменить высоту строки ${index}`;
  return `<button type="button" class="sheet-resize-handle sheet-resize-${axis}" data-sheet-resize-axis="${axis}" data-sheet-resize-index="${index}" data-sheet-resize-view="${escapeHtml(viewId)}" data-sheet-resize-tab="${escapeHtml(tabId)}" data-sheet-resize-version="${escapeHtml(versionId)}" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}"></button>`;
}

function applySheetDimensionsToElement(grid: HTMLElement, viewId: string, tabId: string, versionId = "") {
  const dimensions = readSheetDimensions(viewId, tabId, versionId);
  const shape = rangeToGrid(grid.dataset.sheetRange || "A1:A1");
  grid.style.gridTemplateColumns = `var(--sheet-row-header-w) ${sheetTrackTemplate(shape.columns, dimensions.columns || {}, SHEET_DEFAULT_COL_WIDTH, SHEET_MIN_COL_WIDTH, SHEET_MAX_COL_WIDTH)}`;
  grid.style.gridTemplateRows = `var(--sheet-row-h) ${sheetTrackTemplate(shape.rows, dimensions.rows || {}, SHEET_DEFAULT_ROW_HEIGHT, SHEET_MIN_ROW_HEIGHT, SHEET_MAX_ROW_HEIGHT)}`;
}

function startSheetResize(event: PointerEvent) {
  const handle = (event.target as HTMLElement).closest<HTMLElement>("[data-sheet-resize-axis]");
  if (!handle || event.button !== 0) return;
  const grid = handle.closest<HTMLElement>(".sheet-grid");
  if (!grid) return;
  event.preventDefault();
  event.stopPropagation();
  const axis = handle.dataset.sheetResizeAxis === "row" ? "row" : "column";
  const index = Number(handle.dataset.sheetResizeIndex || 0);
  const viewId = handle.dataset.sheetResizeView || "";
  const tabId = handle.dataset.sheetResizeTab || "";
  const versionId = handle.dataset.sheetResizeVersion || "";
  const start = axis === "column" ? event.clientX : event.clientY;
  const startSize = axis === "column" ? handle.parentElement?.getBoundingClientRect().width || SHEET_DEFAULT_COL_WIDTH : handle.parentElement?.getBoundingClientRect().height || SHEET_DEFAULT_ROW_HEIGHT;
  const min = axis === "column" ? SHEET_MIN_COL_WIDTH : SHEET_MIN_ROW_HEIGHT;
  const max = axis === "column" ? SHEET_MAX_COL_WIDTH : SHEET_MAX_ROW_HEIGHT;
  document.body.classList.add(axis === "column" ? "is-resizing-sheet-column" : "is-resizing-sheet-row");
  handle.setPointerCapture?.(event.pointerId);
  const move = (moveEvent: PointerEvent) => {
    const delta = (axis === "column" ? moveEvent.clientX : moveEvent.clientY) - start;
    const size = clampSheetSize(startSize + delta, min, max);
    writeSheetDimension(viewId, tabId, axis, index, size, versionId);
    // A sheet fetch may replace the grid between pointerdown and pointermove.
    // Persisting a preference to a detached node makes the drag look successful
    // only after the next rerender. Resolve the live grid before painting it.
    const liveGrid = grid.isConnected
      ? grid
      : document.querySelector<HTMLElement>("#sheetGridHost .sheet-grid, #requestSheetGridHost .sheet-grid, #budgetTemplateGridHost .sheet-grid") || grid;
    applySheetDimensionsToElement(liveGrid, viewId, tabId, versionId);
  };
  const stop = () => {
    document.body.classList.remove("is-resizing-sheet-column", "is-resizing-sheet-row");
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", stop);
    window.removeEventListener("pointercancel", stop);
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", stop, { once: true });
  window.addEventListener("pointercancel", stop, { once: true });
}

async function loadSheetCells() {
  const view = state.activeSheetView;
  const tab = activeSheetTab();
  if (!view || !tab) return;
  // All sheet-tab entry points (native select, tab button and routed restore)
  // converge here. Keep the native control aligned with the authoritative
  // state so its visible value never lags a click on the tab strip.
  const tabSelect = document.querySelector<HTMLSelectElement>("#sheetTabSelect");
  if (tabSelect) tabSelect.value = tab.id;
  const generation = ++sheetLoadGeneration;
  const range = value("#sheetRangeInput") || tab.default_range || tab.used_range;
  state.sheetRange = range;
  const versionId = activeSheetVersionId();
  const data = await getSheetCells(view.id, { tab: tab.id, range, ...(versionId ? { version_id: versionId } : {}) });
  // Do not let a stale response split the visible projection: grid and meta
  // must always describe the same view/tab/range tuple.
  if (generation !== sheetLoadGeneration) return;
  const renderedRange = data.range || range || tab.used_range;
  state.sheetRange = renderedRange;
  renderSheetGrid(data.cells || [], renderedRange, tab);
  renderSheetTabs(view, tab.id);
  text("#sheetMeta", `${view.source_workbook || ""} · ${tab.label} · ${versionId ? `версия #${versionId} · ` : "все версии · "}${renderedRange} · ${data.cells?.length || 0} ячеек`);
}

function renderSheetGrid(cells: SheetCell[], range: string, tab: SheetTab) {
  const grid = rangeToGrid(range);
  const byAddress = new Map(cells.map((cell) => [cell.address, cell]));
  const viewId = state.activeSheetView?.id || "";
  const tabId = tab.id;
  const dimensions = readSheetDimensions(viewId, tabId);
  const columnHeaders = grid.columns.map((column) => `<div class="sheet-axis sheet-col-axis" style="grid-column:${column - grid.start.column + 2};grid-row:1;"><span>${escapeHtml(compactCellDisplay(columnLabel(column)))}</span>${sheetResizeHandle("column", column, viewId, tabId)}</div>`).join("");
  const rowHeaders = grid.rows.map((row) => `<div class="sheet-axis sheet-row-axis" style="grid-column:1;grid-row:${row - grid.start.row + 2};"><span>${row}</span>${sheetResizeHandle("row", row, viewId, tabId)}</div>`).join("");
  const body = grid.rows.map((row) => grid.columns.map((column) => {
    const address = columnLabel(column) + row;
    const cell = byAddress.get(address) || { address, row, column, display: "", kind: "empty", markers: [] } as SheetCell;
    const classes = ["sheet-cell", `cell-${cell.kind}`, ...(cell.style || "").split(/\s+/).filter(Boolean), ...frozenPaneClasses(cell, tab.freeze || {})];
    const selected = state.selectedSheetCell === address ? " is-selected" : "";
    return `<button type="button" class="${classes.join(" ")}${selected}" style="grid-column:${column - grid.start.column + 2};grid-row:${row - grid.start.row + 2};" data-sheet-address="${address}" data-sheet-row="${row}" data-sheet-column="${column}" title="${escapeHtml(address)} ${escapeHtml(cell.display || "")}" role="gridcell" aria-selected="${state.selectedSheetCell === address}"><span>${escapeHtml(compactCellDisplay(cell.display ?? cell.value ?? "", 24))}</span>${sheetSourcePip(cell)}${cell.has_detail ? '<i class="sheet-cell-dot"></i>' : ""}</button>`;
  }).join("")).join("");
  render("#sheetGridHost", `<div class="sheet-grid" data-sheet-range="${escapeHtml(range)}" style="${sheetGridStyle(grid, dimensions)}">${columnHeaders}${rowHeaders}${body}</div>`);
}

function sheetSourcePip(cell: SheetCell) {
  const binding = cell.binding || {};
  const sourceDisplay = binding.workbook_cell_display;
  if (sourceDisplay === undefined || sourceDisplay === null || String(sourceDisplay).trim() === "") return "";
  const mainDisplay = String(cell.display ?? cell.value ?? "").replace(/\s+/g, " ").trim();
  const normalizedSource = String(sourceDisplay).replace(/\s+/g, " ").trim();
  if (normalizedSource === mainDisplay) return "";
  const sourceCell = binding.workbook_cell_address || binding.source_cell || cell.address;
  const sourceLabel = compactCellDisplay(sourceDisplay, 14);
  const detail = `Excel ${sourceCell}: ${sourceDisplay}`;
  return `<small class="sheet-source-pip" title="${escapeHtml(detail)}" aria-label="${escapeHtml(detail)}"><span>${escapeHtml(sourceLabel)}</span></small>`;
}

function columnLabel(column: number) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let value = column;
  let label = "";
  while (value > 0) {
    const index = (value - 1) % 26;
    label = alphabet[index] + label;
    value = Math.floor((value - 1) / 26);
  }
  return label || "A";
}

function renderSheetTabs(view: SheetView, activeTab: string) {
  render("#sheetTabs", (view.tabs || []).map((tab) => `<button type="button" class="${tab.id === activeTab ? "is-active" : ""}" data-sheet-tab="${escapeHtml(tab.id)}" role="tab" aria-selected="${tab.id === activeTab}">${escapeHtml(tab.label)}</button>`).join(""));
}

async function openSheetCell(address: string) {
  const view = state.activeSheetView;
  const tab = activeSheetTab();
  if (!view || !tab) return;
  state.selectedSheetCell = address;
  await loadSheetCells();
  const versionId = activeSheetVersionId();
  const detail = await getSheetCellDetail(view.id, address, { tab: tab.id, ...(versionId ? { version_id: versionId } : {}) });
  renderSheetDetail(detail);
}

function renderSheetDetail(detail: SheetCellDetail) {
  const actions = actionsForRole(detail.actions || [], state.me?.actor?.role || "observer");
  render("#sheetDetail", `<div class="sheet-detail-head"><span class="eyebrow">${escapeHtml(detail.address)}</span><h3>${escapeHtml(detail.title)}</h3><p>${escapeHtml(detail.summary)}</p></div>${renderFormulaDetail((detail as any).formula_detail)}<div class="sheet-detail-meta">${renderSheetObjects(detail.objects || [])}</div><div class="sheet-actions">${actions.map((action: any) => renderSheetActionButton(action, detail, "sheet")).join("")}</div>`);
}

function renderSheetActionButton(action: any, detail: any, scope: "sheet" | "request") {
  const binding = detail.cell?.binding || {};
  const formula = detail.formula_detail?.formula || detail.cell?.formula || "";
  const attrs = [
    scope === "sheet" ? `data-sheet-action="${escapeHtml(action.id)}"` : `data-request-sheet-action="${escapeHtml(action.id)}" data-request-sheet-address="${escapeHtml(detail.address)}"`,
    `data-sheet-action-kind="${escapeHtml(action.kind || "")}"`,
    `data-sheet-action-target="${escapeHtml(action.target || "")}"`,
    `data-budget-line-id="${escapeHtml(binding.budget_line_id || "")}"`,
    `data-request-id="${escapeHtml(binding.request_id || "")}"`,
    `data-import-id="${escapeHtml(binding.import_id || "")}"`,
    `data-copy-text="${escapeHtml(action.id === "copy_formula" ? formula : detail.address)}"`,
  ].join(" ");
  return `<button type="button" class="${escapeHtml(action.cssClass)}" ${attrs} ${action.ready ? "" : "disabled"} title="${escapeHtml(action.reason || action.label)}">${escapeHtml(action.label)}</button>`;
}

function renderFormulaDetail(formulaDetail: any) {
  if (!formulaDetail?.formula) return "";
  const audit = formulaDetail.audit || {};
  const dependencies = Array.isArray(formulaDetail.dependencies) ? formulaDetail.dependencies : [];
  const dependencyText = dependencies
    .flatMap((entry: any) => [...(entry.sheet_refs || []), ...(entry.refs || [])])
    .slice(0, 16)
    .join(", ");
  return `<div class="formula-card" data-formula-card>
    <div><span class="eyebrow">Formula</span><code>${escapeHtml(formulaDetail.formula)}</code></div>
    <div class="formula-card-grid">
      ${item("Excel cached", formulaDetail.cached_value ?? "")}
      ${item("Backend value", audit.backend_value ?? "")}
      ${item("Parity", audit.parity_status || "not calculated")}
      ${item("Dependencies", dependencyText || "нет ссылок")}
    </div>
    ${audit.audit_reason ? `<small class="muted">${escapeHtml(audit.audit_reason)}</small>` : ""}
  </div>`;
}

function renderSheetObjects(objects: Record<string, unknown>[]) {
  const visible = objects.filter((object) => object.id);
  return visible.map((object) => item(`${object.type || "object"} #${object.id}`, String(object.label || ""))).join("") || item("Системная связь", "Для этой ячейки нет связанной заявки или строки.");
}

async function copyText(text: string, successMessage: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
  } else {
    const input = document.createElement("textarea");
    input.value = text;
    input.style.position = "fixed";
    input.style.opacity = "0";
    document.body.append(input);
    input.select();
    document.execCommand("copy");
    input.remove();
  }
  showToast(successMessage, "success");
}

async function dispatchSheetNavigation(actionId: string, button?: HTMLElement) {
  if (actionId === "open_drilldown") {
    const lineId = button?.dataset.budgetLineId || "";
    if (!lineId) return showToast("У ячейки нет строки бюджета для детализации", "error");
    await openDrilldown(lineId);
    return;
  }
  if (actionId === "open_request") {
    const requestId = button?.dataset.requestId || "";
    if (!requestId) return showToast("У ячейки нет связанной заявки", "error");
    setWorkplace("workflow");
    await openRequestDetail(requestId);
    return;
  }
  if (actionId === "open_import") {
    const importId = button?.dataset.importId || "";
    if (!importId) return showToast("У ячейки нет связанного импорта", "error");
    setWorkplace("upload");
    await openImportDetail(importId);
    return;
  }
  showToast("Для этого перехода нет связанного объекта", "error");
}

async function executeSheetAction(actionId: string, button?: HTMLElement) {
  const kind = button?.dataset.sheetActionKind || "";
  if (kind === "clipboard" || actionId === "copy_address" || actionId === "copy_formula") {
    const text = button?.dataset.copyText || state.selectedSheetCell || "";
    if (!text) return showToast("Нечего копировать", "error");
    await copyText(text, actionId === "copy_formula" ? "Формула скопирована" : "Адрес скопирован");
    return;
  }
  if (kind === "navigate" || ["open_drilldown", "open_request", "open_import"].includes(actionId)) {
    await dispatchSheetNavigation(actionId, button);
    return;
  }
  const view = state.activeSheetView;
  const tab = activeSheetTab();
  const address = state.selectedSheetCell;
  if (!view || !tab || !address) return;
  let payload: Record<string, unknown> = { action: actionId };
  if (actionId === "add_comment") {
    const body = window.prompt("Комментарий к ячейке")?.trim() || "";
    if (!body) return showToast("Введите комментарий", "error");
    payload = { ...payload, body };
  }
  if (actionId === "resolve_validation") payload = { ...payload, resolution: window.prompt("Решение проверки") || "Закрыто из Excel-like таблицы" };
  if (actionId === "edit_amount") {
    const amountText = window.prompt("Новая сумма");
    const reason = window.prompt("Причина корректировки")?.trim() || "";
    const amount = Number(amountText);
    if (amountText === null || amountText.trim() === "" || !Number.isFinite(amount) || !reason) return showToast("Введите корректную сумму и причину", "error");
    payload = { ...payload, amount, reason };
  }
  const versionId = activeSheetVersionId();
  const result = await runSheetCellAction(view.id, address, { tab: tab.id, ...(versionId ? { version_id: versionId } : {}) }, payload);
  showToast(`Действие выполнено: ${result.action || actionId}`, "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("sheet_action").join(",");
  await openSheetCell(address);
  scheduleLiveRefresh("sheet_action", 30000);
}

async function openRequestSheetModal(requestId: string) {
  const detail = await api<any>(`/api/requests/${requestId}`);
  const version = detail.version || {};
  const sourceType = version.source_type || detail.lines?.[0]?.source_type;
  const viewId = sheetViewForSourceType(sourceType);
  const view = await getSheetView(viewId);
  const tab = view.tabs?.find((entry) => entry.id === view.default_tab) || view.tabs?.[0];
  if (!tab) return showToast("Для заявки нет Excel-вида", "error");
  const range = tab.default_range || tab.used_range;
  const data = await getSheetCells(view.id, { tab: tab.id, range, version_id: version.id });
  openSidePanel(html`
    <h2>Excel-вид заявки #${escapeHtml(requestId)}</h2>
    <p class="drawer-lead">${escapeHtml(view.title)} · ${escapeHtml(tab.label)} · версия ${escapeHtml(version.id || "")}</p>
    <div class="request-sheet-modal" data-request-sheet-view="${escapeHtml(view.id)}" data-request-sheet-tab="${escapeHtml(tab.id)}" data-request-sheet-version="${escapeHtml(version.id || "")}" data-request-id="${escapeHtml(requestId)}">
      <div id="requestSheetGridHost" class="sheet-grid-host" role="grid" aria-label="Excel-вид данных заявки"></div>
      <div id="requestSheetDetail" class="sheet-detail-panel">${item("Выберите ячейку", "Доступные действия появятся здесь")}</div>
    </div>
  `, { variant: "fullscreen" });
  renderRequestSheetGrid(data.cells || [], data.range || range, tab);
}

function renderRequestSheetGrid(cells: SheetCell[], range: string, tab: SheetTab) {
  const grid = rangeToGrid(range);
  const byAddress = new Map(cells.map((cell) => [cell.address, cell]));
  const host = document.querySelector<HTMLElement>("[data-request-sheet-view]");
  const viewId = host?.dataset.requestSheetView || "";
  const tabId = host?.dataset.requestSheetTab || tab.id;
  const versionId = host?.dataset.requestSheetVersion || "";
  const dimensions = readSheetDimensions(viewId, tabId, versionId);
  const columnHeaders = grid.columns.map((column) => `<div class="sheet-axis sheet-col-axis" style="grid-column:${column - grid.start.column + 2};grid-row:1;"><span>${escapeHtml(compactCellDisplay(columnLabel(column)))}</span>${sheetResizeHandle("column", column, viewId, tabId, versionId)}</div>`).join("");
  const rowHeaders = grid.rows.map((row) => `<div class="sheet-axis sheet-row-axis" style="grid-column:1;grid-row:${row - grid.start.row + 2};"><span>${row}</span>${sheetResizeHandle("row", row, viewId, tabId, versionId)}</div>`).join("");
  const body = grid.rows.map((row) => grid.columns.map((column) => {
    const address = columnLabel(column) + row;
    const cell = byAddress.get(address) || { address, row, column, display: "", kind: "empty", markers: [] } as SheetCell;
    const classes = ["sheet-cell", `cell-${cell.kind}`, ...(cell.style || "").split(/\s+/).filter(Boolean), ...frozenPaneClasses(cell, tab.freeze || {})];
    return `<button type="button" class="${classes.join(" ")}" style="grid-column:${column - grid.start.column + 2};grid-row:${row - grid.start.row + 2};" data-request-sheet-cell="${address}" title="${escapeHtml(address)} ${escapeHtml(cell.display || "")}" role="gridcell"><span>${escapeHtml(compactCellDisplay(cell.display ?? cell.value ?? "", 24))}</span>${sheetSourcePip(cell)}${cell.has_detail ? '<i class="sheet-cell-dot"></i>' : ""}</button>`;
  }).join("")).join("");
  render("#requestSheetGridHost", `<div class="sheet-grid" data-sheet-range="${escapeHtml(range)}" style="${sheetGridStyle(grid, dimensions)}">${columnHeaders}${rowHeaders}${body}</div>`);
}

async function openRequestSheetCell(address: string) {
  const host = document.querySelector<HTMLElement>("[data-request-sheet-view]");
  if (!host) return;
  const viewId = host.dataset.requestSheetView || "";
  const tab = host.dataset.requestSheetTab || "";
  const versionId = host.dataset.requestSheetVersion || "";
  const detail = await getSheetCellDetail(viewId, address, { tab, version_id: versionId });
  const actions = actionsForRole(detail.actions || [], state.me?.actor?.role || "observer").map((action: any) => renderSheetActionButton(action, detail, "request")).join("");
  render("#requestSheetDetail", `<div class="sheet-detail-head"><span class="eyebrow">${escapeHtml(detail.address)}</span><h3>${escapeHtml(detail.title)}</h3><p>${escapeHtml(detail.summary)}</p></div>${renderFormulaDetail((detail as any).formula_detail)}<div class="sheet-detail-meta">${renderSheetObjects(detail.objects || [])}</div><div class="sheet-actions">${actions}</div>`);
}

async function executeRequestSheetAction(actionId: string, address: string, button?: HTMLElement) {
  const kind = button?.dataset.sheetActionKind || "";
  if (kind === "clipboard" || actionId === "copy_address" || actionId === "copy_formula") {
    const text = button?.dataset.copyText || address;
    if (!text) return showToast("Нечего копировать", "error");
    await copyText(text, actionId === "copy_formula" ? "Формула скопирована" : "Адрес скопирован");
    return;
  }
  if (kind === "navigate" || ["open_drilldown", "open_request", "open_import"].includes(actionId)) {
    await dispatchSheetNavigation(actionId, button);
    return;
  }
  const host = document.querySelector<HTMLElement>("[data-request-sheet-view]");
  if (!host) return;
  const viewId = host.dataset.requestSheetView || "";
  const tab = host.dataset.requestSheetTab || "";
  const versionId = host.dataset.requestSheetVersion || "";
  let payload: Record<string, unknown> = { action: actionId };
  if (actionId === "add_comment") {
    const body = window.prompt("Комментарий к ячейке")?.trim() || "";
    if (!body) return showToast("Введите комментарий", "error");
    payload = { ...payload, body };
  }
  if (actionId === "resolve_validation") payload = { ...payload, resolution: window.prompt("Решение проверки") || "Закрыто из Excel-вида заявки" };
  if (actionId === "edit_amount") {
    const amountText = window.prompt("Новая сумма");
    const reason = window.prompt("Причина корректировки")?.trim() || "";
    const amount = Number(amountText);
    if (amountText === null || amountText.trim() === "" || !Number.isFinite(amount) || !reason) return showToast("Введите корректную сумму и причину", "error");
    payload = { ...payload, amount, reason };
  }
  const result = await runSheetCellAction(viewId, address, { tab, version_id: versionId }, payload);
  showToast(`Действие выполнено: ${result.action || actionId}`, "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("request_sheet_action").join(",");
  await openRequestSheetCell(address);
  scheduleLiveRefresh("request_sheet_action", 30000);
}

async function compareVersions(panel = activeWorkflowSection()) {
  if (!panel) return;
  const base = panel.querySelector<HTMLSelectElement>("#baseVersion")?.value || "";
  const compare = panel.querySelector<HTMLSelectElement>("#compareVersion")?.value || "";
  if (!base || !compare) return showToast("Выберите две версии", "error");
  const diff = await api<any>(`/api/versions/diff?base=${base}&compare=${compare}`);
  const host = panel.querySelector<HTMLElement>("#versionDiff");
  if (host) host.innerHTML = (diff.rows || []).map((row: any) => item(`${row.article_code} · ${periodLabel(row.month)}`, `${money(row.base_amount)} → ${money(row.compare_amount)} · дельта ${money(row.delta)}`)).join("") || item("Различий нет");
}

function activeWorkflowSection(): HTMLElement | null {
  return document.querySelector<HTMLElement>('[data-workplace="workflow"].is-active');
}

function activeControlRunPanel(): HTMLElement | null {
  return activeWorkflowSection()?.querySelector<HTMLElement>("[data-control-run-panel]") || null;
}

function controlVersionValue(panel = activeControlRunPanel()): string {
  return panel?.querySelector<HTMLSelectElement>("#controlVersion")?.value || "";
}

async function runControls(panel = activeControlRunPanel()) {
  const versionId = controlVersionValue(panel);
  if (!versionId) return showToast("Выберите версию", "error");
  await api("/api/controls/run", { method: "POST", body: JSON.stringify({ version_id: versionId }) });
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("control_run").join(",");
  await loadControlChecks(panel);
  scheduleLiveRefresh("controls_run", 30000);
}

async function loadControlChecks(panel = activeControlRunPanel()) {
  const versionId = controlVersionValue(panel);
  if (!versionId) return;
  const checks = await api<any[]>(`/api/controls?version_id=${versionId}`);
  const target = panel?.querySelector<HTMLElement>("#controlChecks");
  if (!target) return;
  target.innerHTML = checks.map((check) => `<div class="item">${severityBadge(check.severity)}<strong>${escapeHtml(check.message)}</strong><small>${statusLabel(check.status)} · ${check.code}</small><button data-control-resolve="${check.id}">Закрыть</button></div>`).join("") || item("Проверок нет");
}

async function resolveControlCheck(id: string) {
  const resolution = window.prompt("Комментарий к закрытию сверки", "Проверено") || "";
  if (!resolution.trim()) return;
  await api(`/api/controls/${id}/resolve`, { method: "POST", body: JSON.stringify({ resolution: resolution.trim() }) });
  showToast("Контрольная сверка закрыта", "success");
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("control_resolve").join(",");
  await loadControlChecks();
  scheduleLiveRefresh("control_resolve", 30000);
}

type UploadFormSummary = {
  mode?: unknown;
  template_type?: unknown;
  cost_center_code?: unknown;
  files: string[];
};

function uploadFormSummary(): UploadFormSummary {
  const form = document.querySelector<HTMLFormElement>("#uploadForm");
  if (!form) return { files: [] };
  const data = new FormData(form);
  return uploadSummaryFromEntries(data.entries()) as UploadFormSummary;
}

function setUploadStep(step: number) {
  document.querySelectorAll("[data-upload-step]").forEach((element) => element.classList.toggle("is-active", Number((element as HTMLElement).dataset.uploadStep) === step));
}

function renderUploadPreflight() {
  const summary = uploadFormSummary();
  render("#uploadPreflight", `<div class="preview-summary">${uploadPreflightItems(summary, requestTypeLabel).map((entry) => item(entry.title, entry.detail)).join("")}</div>`);
  text("#uploadWizardState", "Предварительная проверка готова");
  setUploadStep(2);
}

async function submitUpload(event: SubmitEvent) {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const body = new FormData(form);
  const contract = uploadSubmissionContract(body);
  const result = await api<any>("/api/imports", { method: "POST", body });
  root.dataset.lastUploadFiles = Array.isArray(contract.summary.files) ? contract.summary.files.join(",") : "";
  root.dataset.lastUploadInvalidationTargets = Array.isArray(contract.invalidates) ? contract.invalidates.join(",") : "";
  text("#uploadResult", uploadQueueResultText(result));
  text("#uploadWizardState", "Поставлено в очередь");
  setUploadStep(3);
  root.dataset.lastInvalidationTargets = mutationInvalidationTargets("upload_import").join(",");
  await Promise.allSettled([loadImports(), loadQueue()]);
  scheduleLiveRefresh("upload_import");
}

function buildManualLines() {
  return [...document.querySelectorAll<HTMLElement>("[data-manual-line]")].map((row) => ({
    article_code: (row.querySelector<HTMLInputElement>('[name="article_code"]')?.value || "C.2.1.1").trim(),
    month: Number(row.querySelector<HTMLInputElement>('[name="month"]')?.value || 1),
    month_start: row.querySelector<HTMLInputElement>('[name="month_start"]')?.value || "",
    month_end: row.querySelector<HTMLInputElement>('[name="month_end"]')?.value || "",
    months: row.querySelector<HTMLInputElement>('[name="months"]')?.value || "",
    month_cost_centers: parseMonthCostCenters(row.querySelector<HTMLElement>("[data-month-picker-field]")),
    month_cost_center_amounts: parseMonthCostCenterAmounts(row.querySelector<HTMLElement>("[data-month-picker-field]")),
    amount: Number(row.querySelector<HTMLInputElement>('[name="amount"]')?.value || 0),
    comment: row.querySelector<HTMLInputElement>('[name="comment"]')?.value || ""
  }));
}

function addManualLine() {
  const host = document.querySelector("#manualLineEditor");
  if (!host) return;
  const row = document.createElement("div");
  row.className = "manual-request-field-grid";
  row.dataset.manualLine = "1";
  row.innerHTML = `<label class="manual-line-field" data-manual-line-field="article"><span>Статья</span><select name="article_code">${articleOptions(state.dictionaries?.articles || [])}</select></label><label class="manual-line-field form-field" data-manual-line-field="month"><span>Месяц</span>${monthPickerControl("month", 1, true, "", false, true, true)}</label><label class="manual-line-field" data-manual-line-field="amount"><span>Сумма</span><input name="amount" type="number" value="-100000" /></label><label class="manual-line-field" data-manual-line-field="comment"><span>Комментарий</span><input name="comment" /></label><button type="button" data-remove-line>Удалить</button>`;
  host.append(row);
}

function manualRequestPayload(form: HTMLFormElement) {
  const data = new FormData(form);
  return { title: data.get("title"), request_type: data.get("request_type"), line_type: data.get("line_type"), cost_center_code: data.get("cost_center_code"), lines: buildManualLines() };
}

function revenueRequestPayload(form: HTMLFormElement) {
  const data = new FormData(form);
  const payload: Record<string, any> = Object.fromEntries(data.entries());
  payload.month_cost_centers = parseMonthCostCenters(form.querySelector<HTMLElement>("[data-month-picker-field]"));
  payload.month_cost_center_amounts = parseMonthCostCenterAmounts(form.querySelector<HTMLElement>("[data-month-picker-field]"));
  const monthlyBilling: Record<string, number> = {};
  form.querySelectorAll<HTMLInputElement>("[data-monthly-billing-input]").forEach((input) => {
    if (input.dataset.month) monthlyBilling[input.dataset.month] = Number(input.value || 0);
  });
  if (Object.keys(monthlyBilling).length) payload.monthly_billing = monthlyBilling;
  payload.driver_input_modes = {
    ak: form.dataset.akSource || "percent",
    rc: form.dataset.rcSource || "percent",
    sk: form.dataset.skSource || "percent",
  };
  payload.ak_percent = percentToFraction(payload.ak_percent, "АК");
  payload.rc_percent = percentToFraction(payload.rc_percent, "Разница цены (РЦ)");
  payload.sk_percent = percentToFraction(payload.sk_percent, "Суперкомиссия от поставщика (СК)");
  for (const derived of ["ak_amount", "rc_amount", "sk_amount", "profitability_amount", "profitability_percent", "profitability"]) delete payload[derived];
  return payload;
}

function explanationNotePayload(form: HTMLFormElement) {
  const data = new FormData(form);
  const allocationScope = data.get("allocation_scope") === "all" ? "all" : "selected";
  const line: Record<string, any> = Object.fromEntries(data.entries());
  line.allocation_scope = allocationScope;
  const monthlyAmounts: Record<string, number> = {};
  form.querySelectorAll<HTMLInputElement>("[data-explanation-monthly-input]").forEach((input) => {
    if (input.dataset.month) monthlyAmounts[input.dataset.month] = Number(input.value || 0);
  });
  if (line.amount_mode === "monthly") line.monthly_amounts = monthlyAmounts;
  return {
    title: data.get("title"),
    request_type: "explanation_note",
    allocation_mode: data.get("allocation_mode"),
    allocation_scope: allocationScope,
    line_type: data.get("line_type"),
    cost_center_code: data.get("cost_center_code"),
    lines: [line]
  };
}

function previewRequestPayload(form: HTMLFormElement) {
  if (form.id === "revenueRequestForm") return { request_type: "revenue", ...revenueRequestPayload(form) };
  if (form.id === "explanationNoteForm") return explanationNotePayload(form);
  return manualRequestPayload(form);
}

async function previewRequest(form: HTMLFormElement, target: string) {
  const payload = previewRequestPayload(form);
  const preview = await api<any>("/api/requests/preview", { method: "POST", body: JSON.stringify(payload) });
  renderRequestPreview(target, preview);
}

function renderRequestPreview(target: string, preview: any) {
  const lines = preview.calculated_lines || [];
  const allocationRows = lines.filter((line: any) => line.allocation_share !== undefined);
  const allocationTable = allocationRows.length
    ? `<div class="table-wrap"><table class="data-table"><thead><tr><th>КЦ</th><th>Месяц</th><th class="num">Общая база</th><th class="num">База КЦ</th><th class="num">Доля</th><th class="num">Сумма</th></tr></thead><tbody>${allocationRows.map((line: any) => `<tr><td>${escapeHtml(line.cost_center_code)}</td><td>${escapeHtml(monthLabel(line.month))}</td><td class="num">${escapeHtml(String(line.base_total))}</td><td class="num">${escapeHtml(String(line.cost_center_base))}</td><td class="num">${formatAllocationShare(line.allocation_share)}</td><td class="num">${formatAllocationAmount(line.amount)}</td></tr>`).join("")}</tbody></table></div>`
    : "";
  const revenueTable = lines.length && lines.some((line: any) => String(line.article_code || "").startsWith("A."))
    ? `<div class="table-wrap"><table class="data-table revenue-preview-table"><thead><tr><th>Статья</th><th>КЦ</th><th>Месяц</th><th class="num">Сумма</th></tr></thead><tbody>${lines.map((line: any) => `<tr><td>${escapeHtml(`${line.article_code || ""}${line.article_title ? ` · ${line.article_title}` : ""}`)}</td><td>${escapeHtml(line.cost_center_code || "")}</td><td>${escapeHtml(monthLabel(Number(line.month)))}</td><td class="num">${money(line.amount)}</td></tr>`).join("")}</tbody></table></div>`
    : "";
  const revenueTotals = revenueTable
    ? `<div class="metrics compact-metrics">${[
        ["Бюджет", preview.totals?.billing],
        ["АК", preview.totals?.ak_amount],
        ["РЦ", preview.totals?.rc_amount],
        ["СК", preview.totals?.sk_amount],
        ["Рентабельность", preview.totals?.profitability_amount ?? preview.totals?.income],
      ].map(([label, value]) => `<div class="metric"><strong>${money(value)}</strong><small>${label}</small></div>`).join("")}</div>`
    : item("Итог распределения", formatAllocationAmount(preview.totals?.amount || 0));
  render(target, `<div class="preview-summary">${item("Можно создавать заявку", preview.can_submit === false || preview.ok === false ? "есть замечания" : "предпросмотр готов")}${revenueTotals}${renderPreviewMessages(preview)}${revenueTable}${allocationTable}</div>`);
}

function renderPreviewMessages(preview: any) {
  return (preview.messages || preview.validation_errors || []).map((message: any) => item(message.field || "Проверка", message.message || message)).join("");
}

const ADMIN_UPDATE_ENDPOINTS: Record<string, (id: string) => string> = {
  mappingForm: (id) => `/api/mapping-rules/${encodeURIComponent(id)}`,
};

function selectAdminDictionaryItem(element: HTMLElement) {
  const form = document.querySelector<HTMLFormElement>(`#${element.dataset.adminForm || ""}`);
  if (!form) return;
  const payload = JSON.parse(decodeURIComponent(element.dataset.adminPayload || "{}"));
  document.querySelectorAll(".admin-editable-item.is-selected").forEach((item) => item.classList.remove("is-selected"));
  element.closest(".admin-editable-item")?.classList.add("is-selected");
  form.dataset.adminSelectedDictionary = element.dataset.adminEdit || "";
  form.dataset.adminSelectedKey = element.dataset.adminKey || "";
  form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[name]").forEach((field) => {
    const raw = payload[field.name];
    if (raw === undefined || raw === null) return;
    if (field.name === "aliases" || field.name === "aliases_json") {
      field.value = Array.isArray(raw) ? raw.join(", ") : aliasesDisplay(raw);
    } else {
      field.value = String(raw);
    }
  });
  const aliasesField = form.querySelector<HTMLInputElement>("[name='aliases']");
  if (aliasesField && payload.aliases_json !== undefined) aliasesField.value = aliasesDisplay(payload.aliases_json);
  const costCenterField = form.querySelector<HTMLSelectElement>("[name='cost_center_code']");
  if (costCenterField && payload.cost_center_code !== undefined) costCenterField.value = String(payload.cost_center_code || "");
  render("#adminResult", `Открыто для редактирования: ${escapeHtml(String(payload.code || payload.name || payload.id || ""))}`);
}

function aliasesDisplay(raw: unknown) {
  if (Array.isArray(raw)) return raw.join(", ");
  try {
    const parsed = JSON.parse(String(raw || "[]"));
    return Array.isArray(parsed) ? parsed.join(", ") : "";
  } catch {
    return String(raw || "");
  }
}

function clearAdminForm(formId: string) {
  const form = document.querySelector<HTMLFormElement>(`#${formId}`);
  if (!form) return;
  form.reset();
  delete form.dataset.adminSelectedDictionary;
  delete form.dataset.adminSelectedKey;
  const idField = form.querySelector<HTMLInputElement>("[name='id']");
  if (idField) idField.value = "";
  document.querySelectorAll(".admin-editable-item.is-selected").forEach((item) => item.classList.remove("is-selected"));
}

async function deactivateAdminItem(dictionary: string, key: string) {
  if (!window.confirm("Деактивировать запись справочника?")) return;
  await api(`/api/dictionaries/${encodeURIComponent(dictionary)}/${encodeURIComponent(key)}/deactivate`, { method: "POST", body: JSON.stringify({}) });
  render("#adminResult", "Запись деактивирована");
  await loadDictionaries();
}

async function deleteAdminItem(dictionary: string, key: string) {
  if (!window.confirm("Удалить запись справочника? Если запись используется, система деактивирует ее вместо удаления.")) return;
  const result = await api<any>(`/api/dictionaries/${encodeURIComponent(dictionary)}/${encodeURIComponent(key)}`, { method: "DELETE" });
  render("#adminResult", result.deactivated ? "Запись используется и была деактивирована" : "Запись удалена");
  await loadDictionaries();
}

async function submitManualRequest(event: SubmitEvent) {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const payload = manualRequestPayload(form);
  const result = await api<any>("/api/requests", { method: "POST", body: JSON.stringify(payload) });
  render("#manualRequestResult", `Создана заявка #${result.request?.id || ""}`);
  showToast("Заявка создана", "success");
  await Promise.allSettled([loadRequests(), loadApprovalQueue(), loadTasks(), loadDashboard()]);
  scheduleLiveRefresh("request_created");
}

async function submitRevenueRequest(event: SubmitEvent) {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const result = await api<any>("/api/requests/revenue", { method: "POST", body: JSON.stringify(revenueRequestPayload(form)) });
  render("#revenueRequestResult", `Создана заявка #${result.request?.id || ""}`);
  showToast("Доходная заявка создана", "success");
  await Promise.allSettled([loadRequests(), loadApprovalQueue(), loadTasks(), loadDashboard(), loadLines(), loadSummaries()]);
  scheduleLiveRefresh("request_created");
}

async function submitExplanationNote(event: SubmitEvent) {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const result = await api<any>("/api/requests", { method: "POST", body: JSON.stringify(explanationNotePayload(form)) });
  render("#explanationNoteResult", `Создана пояснительная #${result.request?.id || ""}`);
  showToast("Пояснительная создана", "success");
  await Promise.allSettled([loadRequests(), loadApprovalQueue(), loadTasks(), loadDashboard()]);
  scheduleLiveRefresh("request_created");
}

function bindAdminForm(id: string, endpoint: string) {
  const form = document.querySelector<HTMLFormElement>(`#${id}`);
  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const payload = serializeForm(form);
    const selectedKey = form.dataset.adminSelectedKey || "";
    const updateEndpoint = selectedKey && ADMIN_UPDATE_ENDPOINTS[id] ? ADMIN_UPDATE_ENDPOINTS[id](selectedKey) : endpoint;
    await api(updateEndpoint, { method: "POST", body: JSON.stringify(payload) });
    render("#adminResult", "Сохранено");
    delete form.dataset.adminSelectedDictionary;
    delete form.dataset.adminSelectedKey;
    await loadDictionaries();
  });
}

function bindAllocationReconciliationForm() {
  const form = document.querySelector<HTMLFormElement>("#allocationReconciliationForm");
  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const result = await api<any>("/api/allocations/calculate", { method: "POST", body: JSON.stringify(serializeForm(form)) });
    const targets = (result.targets || []).map((target: any) => `<tr><td>${escapeHtml(target.target_cost_center_code)}</td><td>${escapeHtml(target.target_article_code)}</td><td class="num">${formatAllocationAmount(target.allocated_amount)}</td><td class="num">${formatAllocationShare(target.share)}</td></tr>`).join("");
    render("#allocationReconciliationResult", `<strong>Сверка: ${escapeHtml(result.reconciliation_status)}</strong><div class="metric-grid"><div class="metric"><small>Источник</small><strong>${formatAllocationAmount(result.signed_source_amount)}</strong></div><div class="metric"><small>Целей</small><strong>${escapeHtml(result.target_count)}</strong></div><div class="metric"><small>Сумма целей</small><strong>${formatAllocationAmount(result.allocated_total)}</strong></div><div class="metric"><small>Остаток</small><strong>${formatAllocationAmount(result.residual)}</strong></div></div><div class="table-wrap"><table class="data-table"><thead><tr><th>Кост-центр</th><th>Статья</th><th class="num">Сумма</th><th class="num">Доля</th></tr></thead><tbody>${targets}</tbody></table></div>`);
  });
}

function formatAllocationAmount(value: unknown) {
  return Number(value || 0).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatAllocationShare(value: unknown) {
  return `${(Number(value || 0) * 100).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function formatAllocationInput(value: unknown, precision = 2) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";
  return number.toFixed(precision).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}

async function loadAllocationBasisList() {
  if (!hasCapability("allocation_basis")) return;
  const host = document.querySelector<HTMLElement>("#allocationBasisList");
  if (!host) return;
  const [versions, template] = await Promise.all([
    api<any[]>("/api/allocation-bases"),
    api<any>("/api/allocation-bases/hr-template-info")
  ]);
  renderAllocationTemplateAdmin(template);
  const version = versions.find((item) => item.status === "effective") || versions[0];
  // A successor becomes effective at its own interval start, which is not
  // necessarily January after a cutover. Do not erase drafts just because
  // that selected snapshot has no month-1 materialization.
  const displayMonth = Number(version?.effective_from_month || 1);
  const members = (version?.members || []).filter((item: any) => Number(item.month) === displayMonth);
  if (!version || !members.length) { render("#allocationBasisList", emptyState("Нет действующей базы", "Загрузите шаблон с листом ИНФО.")); return; }
  const staffTotal = members.reduce((total: number, member: any) => {
    const staff = Number(member.staff);
    return total + (Number.isFinite(staff) ? staff : 0);
  }, 0);
  const drafts = versions.filter((item) => item.status === "draft" || item.status === "owner_approved");
  const role = state.me?.actor?.role;
  const canOwnerApprove = role === "functional_owner" || role === "finance_admin";
  const canControllerApprove = role === "finance_controller" || role === "finance_admin";
  const totalBase = members.reduce((total: number, member: any) => total + Number(member.eligible_base || 0), 0);
  const draftCards = drafts.map((draft) => `<article class="allocation-draft-card"><strong>Черновик #${escapeHtml(draft.id)}</strong><span class="chip">${draft.status === "draft" ? "Черновик HR" : "Ожидает финансиста"}</span><span class="muted">${escapeHtml(draft.reason || "Без причины")}</span><div class="actions"><button type="button" data-allocation-basis-preview="${escapeHtml(draft.id)}">Сравнить</button>${draft.status === "draft" && canOwnerApprove ? `<button type="button" data-allocation-basis-owner="${escapeHtml(draft.id)}" data-allocation-basis-revision="${escapeHtml(draft.revision)}">Отправить финансисту</button>` : ""}${draft.status === "owner_approved" && canControllerApprove ? `<button type="button" data-allocation-basis-controller="${escapeHtml(draft.id)}" data-allocation-basis-revision="${escapeHtml(draft.revision)}">Подтвердить и применить</button>` : ""}${role === "finance_admin" ? `<button type="button" class="danger" data-allocation-basis-force="${escapeHtml(draft.id)}" data-allocation-basis-revision="${escapeHtml(draft.revision)}">Применить принудительно</button>` : ""}</div></article>`).join("");
  render("#allocationBasisList", `<small>Snapshot #${escapeHtml(version.id)} · действует месяцы ${escapeHtml(version.effective_from_month)}–${Number(version.effective_to_month) - 1} · ${escapeHtml(version.status)}</small><table class="data-table"><thead><tr><th>КЦ для офисных расходов</th><th class="num">Штат</th><th class="num">Вне офиса</th><th class="num">База</th><th class="num">Коэффициент</th></tr></thead><tbody>${members.map((member: any) => `<tr><td>${escapeHtml(member.display_name || member.cost_center_code)}</td><td class="num">${formatAllocationAmount(member.staff)}</td><td class="num">${formatAllocationAmount(member.remote)}</td><td class="num">${formatAllocationAmount(member.eligible_base)}</td><td class="num">${formatAllocationShare(totalBase ? Number(member.eligible_base) / totalBase : 0)}</td></tr>`).join("")}</tbody><tfoot><tr data-allocation-basis-total><th scope="row">Итого</th><td class="num">${formatAllocationAmount(staffTotal)}</td><td></td><td></td><td class="num">100,00%</td></tr></tfoot></table>${draftCards ? `<h3>Изменения на согласовании</h3><div class="allocation-draft-list">${draftCards}</div>` : ""}<div id="allocationBasisPreview" class="preview-box"></div>`);
  renderAllocationBasisEditor(version, displayMonth);
}

function renderAllocationTemplateAdmin(template: any) {
  const host = document.querySelector<HTMLElement>("#allocationBasisTemplateAdmin");
  if (!host) return;
  const labels = Object.values(template?.labels || {}).map((label) => escapeHtml(label)).join(" · ");
  if (state.me?.actor?.role !== "finance_admin") {
    host.innerHTML = `<p class="scope-note">Актуальный шаблон: ${escapeHtml(template?.file_name || "офисная база")}<br /><small>${labels}</small></p>`;
    return;
  }
  host.innerHTML = `<details class="template-admin-card"><summary>Управление шаблоном финансиста</summary><p class="muted">Загруженный файл станет новым шаблоном для HR, а его обозначения КЦ — подписями этого раздела.</p><p><strong>Сейчас:</strong> ${escapeHtml(template?.file_name || "офисная база")}</p><form id="allocationBasisTemplateForm" class="inline-upload"><input name="file" type="file" accept=".xlsx" required /><button type="submit">Установить новый шаблон</button></form><small>${labels}</small></details>`;
  host.querySelector<HTMLFormElement>("#allocationBasisTemplateForm")?.addEventListener("submit", (event) => submitAllocationBasisTemplate(event as SubmitEvent).catch((error) => showToast(error.message, "error")));
}

async function submitAllocationBasisTemplate(event: SubmitEvent) {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  await api("/api/allocation-bases/hr-template", { method: "POST", body: new FormData(form) });
  showToast("Новый шаблон HR установлен", "success");
  form.reset();
  await loadAllocationBasisList();
}

function renderAllocationBasisEditor(version: any, displayMonth: number) {
  const host = document.querySelector<HTMLElement>("#allocationBasisEditor");
  if (!host) return;
  const financeOverride = state.me?.actor?.role === "finance_admin";
  allocationBasisEditorMembers = version.members || [];
  const members = allocationBasisEditorMembers.filter((member: any) => Number(member.month) === displayMonth);
  const initialTotal = members.reduce((sum: number, member: any) => sum + Number(member.eligible_base || 0), 0);
  host.innerHTML = `<form id="allocationBasisDraftForm" class="manual-form allocation-basis-form"><h3>Внести данные вручную</h3><p class="muted">HR меняет штат, сотрудников вне офиса и пояснение. База и коэффициенты рассчитываются автоматически.${financeOverride ? " Финансовый администратор может изменить коэффициенты вручную." : ""}</p><input type="hidden" name="based_on_version_id" value="${escapeHtml(version.id)}" /><div class="form-grid allocation-period-fields"><label>С месяца<select name="effective_from_month">${Array.from({ length: 12 }, (_, index) => `<option value="${index + 1}" ${index + 1 === displayMonth ? "selected" : ""}>${monthLabel(index + 1)}</option>`).join("")}</select></label><label>По месяц<select name="effective_to_month">${Array.from({ length: 12 }, (_, index) => `<option value="${index + 2}" ${index + 2 === Number(version.effective_to_month) ? "selected" : ""}>${monthLabel(index + 1)}</option>`).join("")}</select></label><label class="wide">Причина<input name="reason" required placeholder="Опишите причину или напишите ТАК НАДО" /></label></div><div class="allocation-editor-grid"><div class="allocation-editor-head"><span>КЦ для офисных расходов</span><span>Штат</span><span>Вне офиса</span><span>База</span><span>Коэффициент</span><span>Пояснение</span></div>${members.map((member: any) => `<div class="allocation-editor-row" data-basis-editor-row="${escapeHtml(member.cost_center_code)}"><strong>${escapeHtml(member.display_name || member.cost_center_code)}</strong><label><span>Штат</span><input class="num" name="staff" type="number" min="0" step="0.01" value="${escapeHtml(formatAllocationInput(member.staff))}" /></label><label><span>Вне офиса</span><input class="num" name="remote" type="number" min="0" step="0.01" value="${escapeHtml(formatAllocationInput(member.remote))}" /></label><div class="allocation-derived"><span>База</span><strong data-basis-eligible>${formatAllocationAmount(Number(member.staff) - Number(member.remote))}</strong></div><label><span>Коэффициент</span>${financeOverride ? `<input class="num" name="share" type="number" min="0" max="100" step="0.0001" value="${escapeHtml(formatAllocationInput(initialTotal ? Number(member.eligible_base) / initialTotal * 100 : 0, 4))}" />` : `<strong data-basis-share>${formatAllocationShare(initialTotal ? Number(member.eligible_base) / initialTotal : 0)}</strong>`}</label><label><span>Пояснение</span><input name="remote_explanation" value="${escapeHtml(member.remote_explanation || "")}" placeholder="Кто работает вне офиса" /></label></div>`).join("")}<div class="allocation-editor-totals"><strong>Итого</strong><strong data-basis-staff-total></strong><span></span><strong data-basis-base-total></strong><strong data-basis-share-total></strong><span></span></div></div>${actionButton("Создать черновик базы", "primary", { type: "submit" })}</form>`;
  const form = host.querySelector<HTMLFormElement>("#allocationBasisDraftForm");
  form?.addEventListener("input", () => updateAllocationBasisEditorTotals(form));
  form?.addEventListener("submit", (event) => submitAllocationBasisDraft(event).catch((error) => showToast(error.message, "error")));
  if (form) updateAllocationBasisEditorTotals(form);
}

function updateAllocationBasisEditorTotals(form: HTMLFormElement) {
  let staffTotal = 0;
  let baseTotal = 0;
  form.querySelectorAll<HTMLElement>("[data-basis-editor-row]").forEach((row) => {
    const staff = Number(row.querySelector<HTMLInputElement>("[name='staff']")?.value || 0);
    const remote = Number(row.querySelector<HTMLInputElement>("[name='remote']")?.value || 0);
    const target = row.querySelector<HTMLElement>("[data-basis-eligible]");
    if (target) target.textContent = formatAllocationAmount(staff - remote);
    staffTotal += staff;
    baseTotal += staff - remote;
  });
  let shareTotal = 0;
  form.querySelectorAll<HTMLElement>("[data-basis-editor-row]").forEach((row) => {
    const staff = Number(row.querySelector<HTMLInputElement>("[name='staff']")?.value || 0);
    const remote = Number(row.querySelector<HTMLInputElement>("[name='remote']")?.value || 0);
    const base = staff - remote;
    const shareInput = row.querySelector<HTMLInputElement>("[name='share']");
    const share = shareInput ? Number(shareInput.value || 0) : (baseTotal ? base / baseTotal * 100 : 0);
    shareTotal += share;
    const shareLabel = row.querySelector<HTMLElement>("[data-basis-share]");
    if (shareLabel) shareLabel.textContent = `${share.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  });
  const staffTarget = form.querySelector<HTMLElement>("[data-basis-staff-total]");
  const baseTarget = form.querySelector<HTMLElement>("[data-basis-base-total]");
  const shareTarget = form.querySelector<HTMLElement>("[data-basis-share-total]");
  if (staffTarget) staffTarget.textContent = formatAllocationAmount(staffTotal);
  if (baseTarget) baseTarget.textContent = formatAllocationAmount(baseTotal);
  if (shareTarget) shareTarget.textContent = `${shareTotal.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

async function submitAllocationBasisDraft(event: SubmitEvent) {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const data = new FormData(form);
  const start = Number(data.get("effective_from_month"));
  const end = Number(data.get("effective_to_month"));
  const edits = new Map<string, { staff: number; remote: number; remoteExplanation: string }>();
  form.querySelectorAll<HTMLElement>("[data-basis-editor-row]").forEach((row) => {
    const code = row.dataset.basisEditorRow || "";
    edits.set(code, { staff: Number(row.querySelector<HTMLInputElement>("[name='staff']")?.value || 0), remote: Number(row.querySelector<HTMLInputElement>("[name='remote']")?.value || 0), remoteExplanation: row.querySelector<HTMLInputElement>("[name='remote_explanation']")?.value.trim() || "" });
  });
  const basedOnVersionId = Number(data.get("based_on_version_id"));
  const financeOverride = state.me?.actor?.role === "finance_admin";
  const shareTotal = Array.from(form.querySelectorAll<HTMLInputElement>("[name='share']")).reduce((sum, input) => sum + Number(input.value || 0), 0);
  if (financeOverride && Math.abs(shareTotal - 100) > 0.01) throw new Error("Сумма коэффициентов должна быть равна 100%.");
  const members = allocationBasisEditorMembers.filter((member: any) => Number(member.month) >= start && Number(member.month) < end).map((member: any) => {
    const edit = edits.get(member.cost_center_code);
    const staff = edit ? edit.staff : Number(member.staff);
    const remote = edit ? edit.remote : Number(member.remote);
    const row = form.querySelector<HTMLElement>(`[data-basis-editor-row="${member.cost_center_code}"]`);
    const share = financeOverride ? Number(row?.querySelector<HTMLInputElement>("[name='share']")?.value || 0) / 100 : null;
    return { month: Number(member.month), cost_center_code: member.cost_center_code, staff, remote, eligible_base: share === null ? staff - remote : share, provenance: { source: financeOverride ? "finance_override_ui" : "hr_manual_ui", based_on_version_id: basedOnVersionId, manual_share: share, display_name: member.display_name || member.cost_center_code, remote_explanation: edit?.remoteExplanation || "" } };
  });
  await api("/api/allocation-bases", { method: "POST", body: JSON.stringify({ basis_type: "workplaces", scope_key: "office_people", effective_from_month: start, effective_to_month: end, reason: data.get("reason"), source: "manual", members }) });
  showToast("Черновик базы создан: подтвердите владельцем и контролером", "success");
  await loadAllocationBasisList();
}

async function submitAllocationBasisImport(event: SubmitEvent) {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const draft = await api<any>("/api/allocation-bases/import", { method: "POST", body: new FormData(form) });
  render("#allocationBasisImportResult", `Excel проверен. Создан черновик #${escapeHtml(draft.id)} — отправьте его финансисту.`);
  showToast("Excel HR загружен в черновик", "success");
  form.reset();
  await loadAllocationBasisList();
}

async function previewAllocationBasis(versionId: string) {
  const versions = await api<any[]>("/api/allocation-bases");
  const version = versions.find((item) => String(item.id) === versionId);
  if (!version) return;
  const rows = version.comparison || [];
  render("#allocationBasisPreview", `<strong>Сравнение черновика #${escapeHtml(versionId)}</strong><div class="table-wrap"><table class="data-table"><thead><tr><th>КЦ</th><th class="num">Штат: было</th><th class="num">Штат: стало</th><th class="num">Коэф.: было</th><th class="num">Коэф.: стало</th></tr></thead><tbody>${rows.map((item: any) => `<tr class="${Number(item.before_staff) !== Number(item.after_staff) || Math.abs(Number(item.before_share) - Number(item.after_share)) > 0.000001 ? "is-changed" : ""}"><td>${escapeHtml(item.display_name || item.cost_center_code)}</td><td class="num">${formatAllocationAmount(item.before_staff)}</td><td class="num">${formatAllocationAmount(item.after_staff)}</td><td class="num">${formatAllocationShare(item.before_share)}</td><td class="num">${formatAllocationShare(item.after_share)}</td></tr>`).join("")}</tbody></table></div>`);
}

async function approveAllocationBasis(versionId: string, revision: string, stage: "owner-approve" | "controller-approve") {
  await api(`/api/allocation-bases/${versionId}/${stage}`, { method: "POST", body: JSON.stringify({ expected_revision: Number(revision) }) });
  await loadAllocationBasisList();
}

async function forceApproveAllocationBasis(versionId: string, revision: string) {
  const reason = window.prompt("Укажите причину принудительного изменения или напишите ТАК НАДО", "ТАК НАДО")?.trim();
  if (!reason) throw new Error("Причина принудительного изменения обязательна.");
  await api(`/api/allocation-bases/${versionId}/force-approve`, { method: "POST", body: JSON.stringify({ expected_revision: Number(revision), reason }) });
  showToast("Новая база применена принудительно", "success");
  await loadAllocationBasisList();
}

function bindCampaignForm() {
  const form = document.querySelector<HTMLFormElement>("#campaignForm");
  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const payload = serializeForm(form);
    await api("/api/campaigns", { method: "POST", body: JSON.stringify(payload) });
    render("#campaignResult", "Кампания сохранена");
    showToast("Бюджетная кампания создана", "success");
    await loadCampaigns();
    window.setTimeout(closeCampaignDialog, 250);
  });
}

function setAdminTool(tool: string) {
  document.querySelectorAll<HTMLElement>("[data-admin-tool]").forEach((button) => {
    const isActive = button.dataset.adminTool === tool;
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-selected", String(isActive));
  });
  document.querySelectorAll<HTMLElement>("[data-admin-tool-panel]").forEach((panel) => {
    panel.classList.toggle("is-active", panel.dataset.adminToolPanel === tool);
  });
  document.querySelectorAll<HTMLElement>("[data-admin-list-panel]").forEach((panel) => {
    panel.classList.toggle("is-active", panel.dataset.adminListPanel === tool);
  });
}

function openAdminTool(tool: string) {
  setWorkplace("admin");
  setAdminTool(tool);
}

function bindAdminToolTabs() {
  document.querySelectorAll<HTMLElement>("[data-admin-tool]").forEach((button) => {
    button.addEventListener("click", () => setAdminTool(button.dataset.adminTool || "articles"));
  });
  document.querySelectorAll<HTMLInputElement>("[data-admin-list-filter]").forEach((input) => {
    input.addEventListener("input", renderAdminLists);
  });
  setAdminTool("articles");
}

function setManualMode(mode: string) {
  document.querySelectorAll<HTMLElement>("[data-manual-mode]").forEach((button) => {
    const isActive = button.dataset.manualMode === mode;
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-selected", String(isActive));
  });
  document.querySelectorAll<HTMLElement>("[data-manual-mode-panel]").forEach((panel) => {
    const isActive = panel.dataset.manualModePanel === mode;
    panel.classList.toggle("is-active", isActive);
    panel.toggleAttribute("hidden", !isActive);
  });
}

function bindManualModeTabs() {
  document.querySelectorAll<HTMLElement>("[data-manual-mode]").forEach((button) => {
    button.addEventListener("click", () => setManualMode(button.dataset.manualMode || "manual"));
  });
  setManualMode("manual");
}

function serializeForm(form: HTMLFormElement) {
  const payload: Record<string, FormDataEntryValue | FormDataEntryValue[]> = {};
  const data = new FormData(form);
  for (const [key, rawValue] of data.entries()) {
    if (payload[key] !== undefined) continue;
    const values = data.getAll(key).filter((value) => String(value).trim() !== "");
    payload[key] = values.length > 1 ? values : rawValue;
  }
  for (const select of form.querySelectorAll<HTMLSelectElement>("select[multiple]")) {
    payload[select.name] = [...select.selectedOptions].map((option) => option.value);
  }
  return payload;
}

function value(selector: string) {
  return document.querySelector<HTMLInputElement | HTMLSelectElement>(selector)?.value || "";
}

function render(selector: string, content: string) {
  const element = document.querySelector(selector);
  if (element) element.innerHTML = content;
}

type SidePanelOptions = {
  variant?: "default" | "fullscreen";
  preserveFocus?: boolean;
};

function openSidePanel(content: string, options: SidePanelOptions = {}) {
  const panel = document.querySelector<HTMLElement>("#sidePanel");
  if (!panel) return;
  const wasOpen = panel.classList.contains("is-open");
  state.activeImportDetailId = null;
  state.activeRequestDetailId = null;
  panel.classList.remove("is-fullscreen");
  panel.innerHTML = `<div class="section-head"><strong id="sidePanelTitle">Панель деталей</strong><button type="button" data-close-panel aria-label="Закрыть панель деталей">Закрыть</button></div>${content}`;
  panel.setAttribute("aria-labelledby", "sidePanelTitle");
  if (options.variant === "fullscreen") panel.classList.add("is-fullscreen");
  panel.classList.add("is-open");
  if (!options.preserveFocus || !wasOpen) window.setTimeout(() => panel.querySelector<HTMLButtonElement>("[data-close-panel]")?.focus(), 0);
}

function closeSidePanel() {
  const panel = document.querySelector("#sidePanel");
  const detailRouteOpen = Boolean(state.activeImportDetailId || state.activeRequestDetailId);
  panel?.classList.remove("is-open", "is-fullscreen");
  state.activeImportDetailId = null;
  state.activeRequestDetailId = null;
  if (detailRouteOpen) setRoute(state.workplace);
}

async function refreshAll() {
  try {
    await Promise.allSettled([loadDashboard(), loadCampaigns(), loadRequests(), loadApprovalQueue(), loadValidationCenter(), loadVersions(), loadLines(), loadSummaries(), loadCostCenterBudget(), loadBudgetWorkspace({ preserveSelection: true }), loadSheetViews(), loadAudit(), loadAllocationBasisList()]);
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Ошибка загрузки", "error");
  }
}

function bindControlRunActions() {
  document.querySelectorAll<HTMLElement>('[data-action="controls-run"]').forEach((controlRun) => {
    controlRun.addEventListener("click", () => {
      const panel = controlRun.closest<HTMLElement>("[data-control-run-panel]");
      const section = panel?.closest<HTMLElement>('[data-workplace="workflow"]');
      controlRun.dataset.controlRunEvent = "received";
      if (!section?.classList.contains("is-active")) {
        controlRun.dataset.controlRunState = "inactive";
        return;
      }
      const versionId = controlVersionValue(panel);
      controlRun.dataset.controlRunVersion = versionId;
      controlRun.dataset.controlRunState = versionId ? "running" : "empty-version";
      if (versionId) runControls(panel).catch((error) => showToast(error.message, "error"));
    });
  });
}

function bindWorkflowPanelActions() {
  document.querySelectorAll<HTMLElement>('[data-workplace="workflow"]').forEach((panel) => {
    const whenActive = (action: () => void) => () => {
      if (!panel.classList.contains("is-active")) return;
      action();
    };
    panel.querySelector("#refreshApprovalQueue")?.addEventListener("click", whenActive(() => loadApprovalQueue()));
    panel.querySelector("#refreshValidationCenter")?.addEventListener("click", whenActive(() => loadValidationCenter(panel).catch((error) => showToast(error.message, "error"))));
    ["#validationStatus", "#validationSeverity", "#validationTemplate", "#validationImportId"].forEach((selector) => {
      panel.querySelector(selector)?.addEventListener("change", whenActive(() => loadValidationCenter(panel).catch((error) => showToast(error.message, "error"))));
    });
    panel.querySelector("#validationSearch")?.addEventListener("input", whenActive(() => loadValidationCenter(panel).catch((error) => showToast(error.message, "error"))));
    panel.querySelector<HTMLButtonElement>("#bulkResolveValidation")?.addEventListener("click", () => {
      const control = panel.querySelector<HTMLButtonElement>("#bulkResolveValidation");
      const ids = [...panel.querySelectorAll<HTMLInputElement>("[data-validation-select]:checked")].map((input) => input.dataset.validationSelect).filter(Boolean);
      const resolution = panel.querySelector<HTMLTextAreaElement>("#bulkValidationResolution")?.value.trim() || "";
      if (!control) return;
      control.dataset.bulkResolveEvent = "received";
      control.dataset.bulkResolveSelectedIds = ids.join(",");
      control.dataset.bulkResolveSelectedCount = String(ids.length);
      if (!panel.classList.contains("is-active")) {
        control.dataset.bulkResolveState = "inactive";
        return;
      }
      if (!ids.length) {
        control.dataset.bulkResolveState = "no-selection";
        return bulkResolveValidation(panel).catch((error) => showToast(error.message, "error"));
      }
      if (!resolution) {
        control.dataset.bulkResolveState = "blank";
        return bulkResolveValidation(panel).catch((error) => showToast(error.message, "error"));
      }
      control.dataset.bulkResolveState = "running";
      return bulkResolveValidation(panel).catch((error) => showToast(error.message, "error"));
    });
    panel.querySelector("#compareVersions")?.addEventListener("click", whenActive(() => compareVersions(panel).catch((error) => showToast(error.message, "error"))));
  });
}

function bindEvents() {
  document.querySelector("#themeToggle")?.addEventListener("click", toggleTheme);
  document.querySelector<HTMLFormElement>("#loginForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const data = new FormData(form);
    try {
      await login(String(data.get("code") || ""), String(data.get("password") || ""));
      form.reset();
      await loadMe();
      await refreshAll();
      showToast("Вход выполнен", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось войти", "error");
    }
  });
  document.querySelector("#logoutButton")?.addEventListener("click", async () => {
    await logout();
    await loadMe();
    showToast("Сессия завершена", "success");
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeSidePanel();
  });
  window.addEventListener("popstate", () => {
    const route = routeFromLocation();
    state.pendingImportId = route.importId || null;
    state.pendingSheetViewId = route.sheetViewId || null;
    state.pendingSheetTab = route.sheetTab || null;
    setWorkplace(route.workplace, { updateUrl: false });
    if (route.requestId && !state.deniedWorkplace) openRequestDetail(route.requestId, { updateUrl: false }).catch((error) => showToast(error.message, "error"));
    else if (route.importId && !state.deniedWorkplace) openImportDetail(route.importId, { updateUrl: false }).catch((error) => showToast(error.message, "error"));
    else if (route.workplace === "sheets" && route.sheetViewId && !state.deniedWorkplace) selectSheetView(route.sheetViewId).catch((error) => showToast(error.message, "error"));
    else closeSidePanel();
  });
  document.querySelector<HTMLSelectElement>("#role")?.addEventListener("change", async (event) => {
    setApiContext({ role: (event.target as HTMLSelectElement).value, user: "" });
    await bootstrapData();
  });
  document.querySelector<HTMLSelectElement>("#user")?.addEventListener("change", async (event) => {
    const selected = event.target as HTMLSelectElement;
    setApiContext({ user: selected.value, role: selected.selectedOptions[0]?.dataset.role || getApiContext().role });
    await bootstrapData();
  });
  document.querySelector("#pagePrimaryAction")?.addEventListener("click", () => {
    if (state.deniedWorkplace) setWorkplace("dashboard");
    else refreshAll();
  });
  document.querySelector("#openCampaignDialog")?.addEventListener("click", openCampaignDialog);
  document.querySelector("#previewUpload")?.addEventListener("click", renderUploadPreflight);
  document.querySelector("#uploadForm")?.addEventListener("submit", (event) => submitUpload(event as SubmitEvent).catch((error) => showToast(error.message, "error")));
  document.querySelector("#allocationBasisImportForm")?.addEventListener("submit", (event) => submitAllocationBasisImport(event as SubmitEvent).catch((error) => showToast(error.message, "error")));
  document.querySelector("#downloadAllocationBasisTemplate")?.addEventListener("click", () => downloadFile("/api/allocation-bases/hr-template.xlsx", "База распределения для ОФИСНЫХ РАСХОДОВ шаблон.xlsx").catch((error) => showToast(error.message, "error")));
  document.querySelector("#addManualLine")?.addEventListener("click", addManualLine);
  document.querySelector("#manualRequestForm")?.addEventListener("submit", (event) => submitManualRequest(event as SubmitEvent).catch((error) => showToast(error.message, "error")));
  document.querySelector("#revenueRequestForm")?.addEventListener("submit", (event) => submitRevenueRequest(event as SubmitEvent).catch((error) => showToast(error.message, "error")));
  document.querySelector("#revenueClientForm")?.addEventListener("submit", (event) => submitRevenueClient(event as SubmitEvent).catch((error) => {
    render("#revenueClientResult", errorState(error.message));
    showToast(error.message, "error");
  }));
  document.querySelector("#explanationNoteForm")?.addEventListener("submit", (event) => submitExplanationNote(event as SubmitEvent).catch((error) => showToast(error.message, "error")));
  document.querySelector<HTMLSelectElement>("#explanationArticle")?.addEventListener("change", (event) => {
    syncExplanationAllocationPolicy();
  });
  document.querySelector<HTMLInputElement>("#explanationAllCostCenters")?.addEventListener("change", () => {
    syncExplanationAllocationPolicy();
  });
  document.querySelectorAll<HTMLButtonElement>("[data-request-preview]").forEach((control) => {
    control.addEventListener("click", () => {
      const form = control.closest<HTMLFormElement>("form");
      const target = control.dataset.requestPreview === "revenue" ? "#revenueRequestResult" : "#explanationNoteResult";
      if (form) previewRequest(form, target).catch((error) => showToast(error.message, "error"));
    });
  });
  document.querySelector("#applyRequestFilters")?.addEventListener("click", () => loadRequests());
  bindWorkflowPanelActions();
  document.querySelector("#refreshLines")?.addEventListener("click", () => loadLines());
  document.querySelector("#refreshCostCenterBudget")?.addEventListener("click", () => loadCostCenterBudget());
  document.querySelector("#applyReportFilters")?.addEventListener("click", () => Promise.all([loadSummaries(), loadCostCenterBudget(), loadLines()]));
  document.querySelector<HTMLSelectElement>("#budgetCampaign")?.addEventListener("change", (event) => {
    state.budgetCampaignId = (event.target as HTMLSelectElement).value;
    state.budgetIncludedRequestIds = undefined;
    state.budgetSavedSelectionId = null;
    loadBudgetWorkspace().catch((error) => showToast(error.message, "error"));
  });
  document.querySelector<HTMLSelectElement>("#budgetScopeMode")?.addEventListener("change", (event) => {
    state.budgetScopeMode = (event.target as HTMLSelectElement).value === "effective" ? "effective" : "selection";
    syncBudgetScopeControls();
    loadBudgetWorkspace({ preserveSelection: true }).catch((error) => showToast(error.message, "error"));
  });
  document.querySelector<HTMLSelectElement>("#budgetTemplateProfile")?.addEventListener("change", (event) => {
    const selected = coerceBudgetTemplateState((event.target as HTMLSelectElement).value, "");
    state.budgetTemplateProfileId = selected.profileId;
    state.budgetTemplateTabId = selected.tabId;
    localStorage.setItem("budgetTemplateProfile", selected.profileId);
    localStorage.setItem("budgetTemplateTab", selected.tabId);
    renderBudgetTemplateProfileNote();
    if (state.budgetPreview) renderBudgetPreview(state.budgetPreview);
  });
  document.querySelector<HTMLSelectElement>("#budgetSavedSelection")?.addEventListener("change", (event) => {
    const selectionId = (event.target as HTMLSelectElement).value;
    state.budgetSavedSelectionId = selectionId || null;
    restoreBudgetSelection(selectionId).catch((error) => showToast(error.message, "error"));
  });
  document.querySelector("#refreshSheetView")?.addEventListener("click", () => loadSheetCells().catch((error) => showToast(error.message, "error")));
  document.querySelector<HTMLSelectElement>("#sheetViewSelect")?.addEventListener("change", (event) => selectSheetView((event.target as HTMLSelectElement).value).catch((error) => showToast(error.message, "error")));
  document.querySelector<HTMLSelectElement>("#sheetTabSelect")?.addEventListener("change", (event) => {
    state.activeSheetTab = (event.target as HTMLSelectElement).value;
    if (state.activeSheetView) setSheetRoute(state.activeSheetView.id, state.activeSheetTab);
    const tab = activeSheetTab();
    const rangeInput = document.querySelector<HTMLInputElement>("#sheetRangeInput");
    if (rangeInput && tab) rangeInput.value = tab.default_range || tab.used_range;
    loadSheetCells().catch((error) => showToast(error.message, "error"));
  });
  document.querySelector<HTMLSelectElement>("#sheetVersionSelect")?.addEventListener("change", (event) => {
    state.sheetVersionId = (event.target as HTMLSelectElement).value || null;
    state.selectedSheetCell = null;
    loadSheetCells().catch((error) => showToast(error.message, "error"));
  });
  document.querySelector("#sheetGridHost")?.addEventListener("keydown", (event) => {
    const keyboardEvent = event as KeyboardEvent;
    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Tab", "Enter"].includes(keyboardEvent.key) || !state.selectedSheetCell) return;
    keyboardEvent.preventDefault();
    const next = moveSelection(state.selectedSheetCell, keyboardEvent.key, state.sheetRange || activeSheetTab()?.used_range || "A1:A1");
    openSheetCell(next).catch((error) => showToast(error.message, "error"));
  });
  document.addEventListener("pointerdown", startSheetResize);
  document.querySelector("#exportXlsx")?.addEventListener("click", () => trackedDownload(`/api/exports/budget.xlsx${reportQuery()}`, "budget_export.xlsx").catch((error) => showToast(error.message, "error")));
  document.querySelector("#exportCsv")?.addEventListener("click", () => trackedDownload(`/api/exports/budget.csv${reportQuery()}`, "budget_export.csv").catch((error) => showToast(error.message, "error")));
  document.querySelector("#exportSummaryA")?.addEventListener("click", () => trackedDownload(`/api/exports/summary-a.xlsx${reportQuery()}`, "summary_a_export.xlsx").catch((error) => showToast(error.message, "error")));
  document.querySelector("#exportSummaryC")?.addEventListener("click", () => trackedDownload(`/api/exports/summary-c.xlsx${reportQuery()}`, "summary_c_export.xlsx").catch((error) => showToast(error.message, "error")));
  document.querySelector("#downloadTemplateCompiled")?.addEventListener("click", () => trackedDownload("/api/exports/templates/compiled_budget", "compiled_budget_template.xlsx").catch((error) => showToast(error.message, "error")));
  document.querySelector("#downloadTemplateBusinessV1")?.addEventListener("click", () => trackedDownload("/api/exports/templates/business_variant_1", "business_variant_1_template.xlsx").catch((error) => showToast(error.message, "error")));
  document.querySelector("#downloadTemplateBusinessV2")?.addEventListener("click", () => trackedDownload("/api/exports/templates/business_variant_2", "business_variant_2_template.xlsx").catch((error) => showToast(error.message, "error")));
  document.querySelector("#downloadTemplateExplanation")?.addEventListener("click", () => trackedDownload("/api/exports/templates/explanation_note", "explanation_note_template.xlsx").catch((error) => showToast(error.message, "error")));
  document.querySelector("#downloadTemplateSummaryC")?.addEventListener("click", () => trackedDownload("/api/exports/templates/summary_c", "summary_c_template.xlsx").catch((error) => showToast(error.message, "error")));
  bindAdminForm("articleForm", "/api/dictionaries/articles");
  bindAdminForm("costCenterForm", "/api/dictionaries/cost-centers");
  bindAdminForm("userForm", "/api/dictionaries/users");
  bindAdminForm("mappingForm", "/api/mapping-rules");
  bindAdminForm("allocationForm", "/api/allocation-rules");
  bindAllocationReconciliationForm();
  loadAllocationBasisList().catch((error) => render("#allocationBasisList", errorState(error.message)));
  bindAdminForm("clientForm", "/api/dictionaries/clients");
  bindAdminForm("businessDimensionForm", "/api/dictionaries/business-dimensions");
  bindCampaignForm();
  bindAdminToolTabs();
  bindManualModeTabs();
  renderRevenueMonthlyBilling();
  renderExplanationMonthlyAmounts();
  const revenueForm = document.querySelector<HTMLFormElement>("#revenueRequestForm");
  if (revenueForm) recalculateRevenueDrivers(revenueForm);
  bindControlRunActions();
  document.querySelector("#adminResetUserData")?.addEventListener("click", (event) => {
    event.preventDefault();
    resetUserData().catch((error) => showToast(error.message, "error"));
  });
  document.addEventListener("click", globalClick);
  document.addEventListener("input", globalInput);
  document.addEventListener("change", globalChange);
}

function globalInput(event: Event) {
  const input = (event.target as HTMLElement).closest<HTMLInputElement>("input");
  const monthlyBilling = input?.closest<HTMLInputElement>("[data-monthly-billing-input]");
  const form = monthlyBilling?.closest<HTMLFormElement>("#revenueRequestForm");
  if (form) syncRevenueBillingFromMonthlyInputs(form);
  const explanationMonthly = input?.closest<HTMLInputElement>("[data-explanation-monthly-input]");
  const explanationForm = explanationMonthly?.closest<HTMLFormElement>("#explanationNoteForm");
  if (explanationForm) syncExplanationTotalFromMonthlyInputs(explanationForm);
  const revenueForm = input?.closest<HTMLFormElement>("#revenueRequestForm");
  if (revenueForm && (input?.name === "billing" || input?.dataset.revenueDriver)) recalculateRevenueDrivers(revenueForm, input);
}

function globalClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (target.closest("[data-open-revenue-client-dialog]")) {
    event.preventDefault();
    openRevenueClientDialog();
    return;
  }
  const basisPreview = target.closest<HTMLElement>("[data-allocation-basis-preview]");
  if (basisPreview) previewAllocationBasis(basisPreview.dataset.allocationBasisPreview || "").catch((error) => showToast(error.message, "error"));
  const basisOwner = target.closest<HTMLElement>("[data-allocation-basis-owner]");
  if (basisOwner) approveAllocationBasis(basisOwner.dataset.allocationBasisOwner || "", basisOwner.dataset.allocationBasisRevision || "", "owner-approve").catch((error) => showToast(error.message, "error"));
  const basisController = target.closest<HTMLElement>("[data-allocation-basis-controller]");
  if (basisController) approveAllocationBasis(basisController.dataset.allocationBasisController || "", basisController.dataset.allocationBasisRevision || "", "controller-approve").catch((error) => showToast(error.message, "error"));
  const basisForce = target.closest<HTMLElement>("[data-allocation-basis-force]");
  if (basisForce) forceApproveAllocationBasis(basisForce.dataset.allocationBasisForce || "", basisForce.dataset.allocationBasisRevision || "").catch((error) => showToast(error.message, "error"));
  if (target.closest("#refreshBudgetPreview")) {
    event.preventDefault();
    loadBudgetWorkspace({ preserveSelection: true }).catch((error) => showToast(error.message, "error"));
    return;
  }
  if (target.closest("#openBudgetTemplateView")) {
    event.preventDefault();
    openBudgetTemplateView().catch((error) => showToast(error.message, "error"));
    return;
  }
  const budgetTemplateTab = target.closest<HTMLElement>("button[data-budget-template-tab]");
  if (budgetTemplateTab) {
    event.preventDefault();
    switchBudgetTemplateModalTab(budgetTemplateTab.dataset.budgetTemplateTab || "");
    return;
  }
  if (target.closest("#saveBudgetSelection")) {
    event.preventDefault();
    saveBudgetSelection().catch((error) => showToast(error.message, "error"));
    return;
  }
  if (target.closest("#exportBudgetPreview")) {
    event.preventDefault();
    exportBudgetPreview().catch((error) => showToast(error.message, "error"));
    return;
  }
  const adminClear = target.closest<HTMLElement>("[data-admin-clear-form]");
  if (adminClear) {
    event.preventDefault();
    clearAdminForm(adminClear.dataset.adminClearForm || "");
    return;
  }
  const adminDeactivate = target.closest<HTMLElement>("[data-admin-deactivate]");
  if (adminDeactivate) {
    event.preventDefault();
    deactivateAdminItem(adminDeactivate.dataset.adminDeactivate || "", adminDeactivate.dataset.adminKey || "").catch((error) => showToast(error.message, "error"));
    return;
  }
  const adminDelete = target.closest<HTMLElement>("[data-admin-delete]");
  if (adminDelete) {
    event.preventDefault();
    deleteAdminItem(adminDelete.dataset.adminDelete || "", adminDelete.dataset.adminKey || "").catch((error) => showToast(error.message, "error"));
    return;
  }
  const adminEdit = target.closest<HTMLElement>("[data-admin-edit]");
  if (adminEdit) {
    selectAdminDictionaryItem(adminEdit);
    return;
  }
  const workplace = target.closest<HTMLElement>("[data-open-workplace], [data-role-workplace-open]");
  if (workplace) setWorkplace((workplace.dataset.openWorkplace || workplace.dataset.roleWorkplaceOpen) as Workplace);
  const requestDetail = target.closest<HTMLElement>("[data-request-detail]");
  if (requestDetail) openRequestDetail(requestDetail.dataset.requestDetail || "").catch((error) => showToast(error.message, "error"));
  const requestSheet = target.closest<HTMLElement>("[data-request-sheet]");
  if (requestSheet) openRequestSheetModal(requestSheet.dataset.requestSheet || "").catch((error) => showToast(error.message, "error"));
  const requestDelete = target.closest<HTMLElement>("[data-request-delete]");
  if (requestDelete) {
    event.preventDefault();
    deleteRequest(requestDelete.dataset.requestDelete || "").catch((error) => showToast(error.message, "error"));
    return;
  }
  const budgetToggleButton = target.closest<HTMLElement>("[data-budget-request-toggle-button]");
  if (budgetToggleButton) {
    event.preventDefault();
    toggleBudgetRequest(budgetToggleButton.dataset.budgetRequestToggleButton || "").catch((error) => showToast(error.message, "error"));
    return;
  }
  const openMonth = target.closest<HTMLElement>("[data-open-month-picker]");
  if (openMonth) {
    const field = openMonth.closest<HTMLElement>("[data-month-picker-field]");
    if (field) openMonthPicker(field);
  }
  const monthOption = target.closest<HTMLElement>("[data-month-option]");
  if (monthOption) chooseMonth(Number(monthOption.dataset.monthOption || 0));
  if (target.closest("[data-month-clear]")) clearMonthPicker();
  if (target.closest("[data-month-apply]")) {
    persistMonthCostCenterPanelInputs();
    syncSingleRevenueBillingFromCostCenterAmounts(activeMonthPicker);
    renderRevenueMonthlyBilling();
    document.querySelector<HTMLDialogElement>("#monthDialog")?.close();
  }
  if (target.closest("[data-month-cost-center-apply-bulk]")) applyMonthCostCenterBulkAmount();
  const editLine = target.closest<HTMLElement>("[data-edit-line]");
  if (editLine) openBudgetLineEditor(editLine.dataset.editLine || "", editLine.dataset.request || "").catch((error) => showToast(error.message, "error"));
  const saveLine = target.closest<HTMLElement>("[data-save-line]");
  if (saveLine) saveBudgetLine(saveLine.dataset.saveLine || "", saveLine.dataset.request || "").catch((error) => showToast(error.message, "error"));
  const campaignDetail = target.closest<HTMLElement>("[data-campaign-detail]");
  if (campaignDetail) openCampaignDetail(campaignDetail.dataset.campaignDetail || "");
  const requestComment = target.closest<HTMLElement>("[data-request-comment]");
  if (requestComment) addRequestComment(requestComment.dataset.requestComment || "").catch((error) => showToast(error.message, "error"));
  const requestAttachment = target.closest<HTMLElement>("[data-request-attachment]");
  if (requestAttachment) uploadRequestAttachment(requestAttachment.dataset.requestAttachment || "").catch((error) => showToast(error.message, "error"));
  const attachmentDownload = target.closest<HTMLElement>("[data-attachment-download]");
  if (attachmentDownload) downloadFile(`/api/requests/${attachmentDownload.dataset.request}/attachments/${attachmentDownload.dataset.attachmentDownload}`, attachmentDownload.dataset.fileName || "attachment");
  const transition = target.closest<HTMLElement>("[data-transition]");
  if (transition) transitionRequest(transition.dataset.request || "", transition.dataset.transition || "").catch((error) => showToast(error.message, "error"));
  const queueCancel = target.closest<HTMLElement>("[data-queue-cancel]");
  if (queueCancel) queueAction(queueCancel.dataset.queueCancel || "", "cancel").catch((error) => showToast(error.message, "error"));
  const queueRetry = target.closest<HTMLElement>("[data-queue-retry]");
  if (queueRetry) queueAction(queueRetry.dataset.queueRetry || "", "retry").catch((error) => showToast(error.message, "error"));
  const validationResolve = target.closest<HTMLElement>("[data-resolve]");
  if (validationResolve) resolveValidationItem(validationResolve.dataset.resolve || "").catch((error) => showToast(error.message, "error"));
  const validationMap = target.closest<HTMLElement>("[data-validation-map]");
  if (validationMap) createMappingFromValidationItem(validationMap.dataset.validationMap || "").catch((error) => showToast(error.message, "error"));
  const validationIgnore = target.closest<HTMLElement>("[data-validation-ignore]");
  if (validationIgnore) ignoreValidationItem(validationIgnore.dataset.validationIgnore || "").catch((error) => showToast(error.message, "error"));
  const validationDetail = target.closest<HTMLElement>("[data-validation-detail]");
  if (validationDetail) openValidationDetail(validationDetail.dataset.validationDetail || "");
  const adminToolOpen = target.closest<HTMLElement>("[data-admin-tool-open]");
  if (adminToolOpen) openAdminTool(adminToolOpen.dataset.adminToolOpen || "articles");
  const controlResolve = target.closest<HTMLElement>("[data-control-resolve]");
  if (controlResolve) resolveControlCheck(controlResolve.dataset.controlResolve || "").catch((error) => showToast(error.message, "error"));
  const importDetail = target.closest<HTMLElement>("[data-import-detail]");
  if (importDetail) openImportDetail(importDetail.dataset.importDetail || "").catch((error) => showToast(error.message, "error"));
  const importDownload = target.closest<HTMLElement>("[data-import-download]");
  if (importDownload) downloadFile(`/api/imports/${importDownload.dataset.importDownload}/file`, importDownload.dataset.fileName || "import.xlsx");
  const importCommit = target.closest<HTMLElement>("[data-import-commit]");
  if (importCommit) commitImport(importCommit.dataset.importCommit || "").catch((error) => showToast(error.message, "error"));
  const importRollback = target.closest<HTMLElement>("[data-import-rollback]");
  if (importRollback) rollbackImport(importRollback.dataset.importRollback || "").catch((error) => showToast(error.message, "error"));
  const drill = target.closest<HTMLElement>("[data-drill]");
  if (drill) openDrilldown(drill.dataset.drill || "").catch((error) => showToast(error.message, "error"));
  const reportDrill = target.closest<HTMLElement>("[data-report-drill]");
  if (reportDrill) openReportDrilldown(reportDrill.dataset.article || "", reportDrill.dataset.block || "", reportDrill.dataset.costCenterCode || "").catch((error) => showToast(error.message, "error"));
  const reportDrillMore = target.closest<HTMLElement>("[data-report-drill-more]");
  if (reportDrillMore) appendReportDrilldownMore(reportDrillMore.dataset.article || "", reportDrillMore.dataset.block || "", reportDrillMore.dataset.costCenterCode || "", reportDrillMore.dataset.reportDrillMore || "0").catch((error) => showToast(error.message, "error"));
  const sheetCell = target.closest<HTMLElement>("[data-sheet-address]");
  if (sheetCell) openSheetCell(sheetCell.dataset.sheetAddress || "").catch((error) => showToast(error.message, "error"));
  const requestSheetCell = target.closest<HTMLElement>("[data-request-sheet-cell]");
  if (requestSheetCell) openRequestSheetCell(requestSheetCell.dataset.requestSheetCell || "").catch((error) => showToast(error.message, "error"));
  const sheetTab = target.closest<HTMLElement>("[data-sheet-tab]");
  if (sheetTab) {
    state.activeSheetTab = sheetTab.dataset.sheetTab || state.activeSheetTab;
    if (state.activeSheetView) setSheetRoute(state.activeSheetView.id, state.activeSheetTab);
    const rangeInput = document.querySelector<HTMLInputElement>("#sheetRangeInput");
    const tab = activeSheetTab();
    if (rangeInput && tab) rangeInput.value = tab.default_range || tab.used_range;
    loadSheetCells().catch((error) => showToast(error.message, "error"));
  }
  const sheetAction = target.closest<HTMLElement>("[data-sheet-action]");
  if (sheetAction) executeSheetAction(sheetAction.dataset.sheetAction || "", sheetAction).catch((error) => showToast(error.message, "error"));
  const requestSheetAction = target.closest<HTMLElement>("[data-request-sheet-action]");
  if (requestSheetAction) executeRequestSheetAction(requestSheetAction.dataset.requestSheetAction || "", requestSheetAction.dataset.requestSheetAddress || "", requestSheetAction).catch((error) => showToast(error.message, "error"));
  const notificationRead = target.closest<HTMLElement>("[data-notification-read]");
  if (notificationRead) markNotificationRead(notificationRead.dataset.notificationRead || "").catch((error) => showToast(error.message, "error"));
  if (target.closest("[data-close-panel]")) closeSidePanel();
  if (target.closest("[data-remove-line]")) target.closest("[data-manual-line]")?.remove();
}

function globalChange(event: Event) {
  const target = event.target as HTMLElement;
  if (target.closest("#explanationAmountMode")) {
    renderExplanationMonthlyAmounts();
    return;
  }
  const costCenterSelect = target.closest<HTMLSelectElement>("#manualCostCenter, #revenueCostCenter");
  if (costCenterSelect) {
    const form = costCenterSelect.closest("form");
    form?.querySelectorAll<HTMLElement>("[data-month-picker-field]").forEach((field) => {
      if (monthCostCentersEnabled(field)) ensureMonthCostCenterDefaults(field);
    });
    if (activeMonthPicker && form?.contains(activeMonthPicker)) renderMonthDialogGrid();
    return;
  }
  const monthCostCenter = target.closest<HTMLInputElement>("[data-month-cost-center-option]");
  if (monthCostCenter) {
    updateMonthCostCenterSelection(monthCostCenter);
    return;
  }
  const budgetToggle = target.closest<HTMLInputElement>("[data-budget-request-toggle]");
  if (budgetToggle) {
    toggleBudgetRequest(budgetToggle.dataset.budgetRequestToggle || "", budgetToggle.checked).catch((error) => showToast(error.message, "error"));
    return;
  }
  const monthCostCenterAmount = target.closest<HTMLInputElement>("[data-month-cost-center-amount]");
  if (monthCostCenterAmount) {
    updateMonthCostCenterAmount(monthCostCenterAmount);
    return;
  }
}

async function bootstrapData() {
  const generation = ++bootstrapGeneration;
  root.dataset.bootstrapState = "loading";
  root.dataset.bootstrapRole = getApiContext().role;
  const loaded = await loadMe(generation);
  if (!loaded || generation !== bootstrapGeneration) return;
  if (state.loginRequired) {
    syncVisibility();
    root.dataset.bootstrapState = "ready";
    return;
  }
  const route = routeFromLocation();
  state.pendingRequestId = route.requestId || null;
  state.pendingImportId = route.importId || null;
  state.pendingSheetViewId = route.sheetViewId || null;
  state.pendingSheetTab = route.sheetTab || null;
  setWorkplace(route.workplace, { updateUrl: false });
  syncVisibility();
  await refreshAll();
  if (generation !== bootstrapGeneration) return;
  if (state.pendingRequestId && !state.deniedWorkplace) await openRequestDetail(state.pendingRequestId, { updateUrl: false });
  if (state.pendingImportId && !state.deniedWorkplace) await openImportDetail(state.pendingImportId, { updateUrl: false });
  if (generation !== bootstrapGeneration) return;
  root.dataset.bootstrapState = "ready";
}

function bootstrap() {
  applyTheme();
  renderShell();
  bindEvents();
  addManualLine();
  bootstrapData().catch((error) => showToast(error.message, "error"));
}

bootstrap();
